// ════════════════════════════════════════════════════════════════════════════
//  ManDana — app shell
//  ----------------------------------------------------------------------------
//  Top-level component tree and the home of all app state. Renders, back-to-front:
//    • <Background>   tinted gradient + accent glows (theme-aware)
//    • scroll area    holds <TodayScreen> (the schedule timeline) — see md-today.jsx
//    • <TodayHeader>  pinned hero (countdown + sun arc) and week strip — md-today.jsx
//    • <NowButton>    floating "jump to now" pill (visibility logic lives here)
//    • <AddButton>    floating "+" action (panel only — the canvas uses
//                     <BoardFab>, which also makes records; the board's own
//                     "New desk" lives in the Desks screen)
//    • Settings       reached from the hero card's own corner — see md-today.jsx
//    • <TweaksPanel>  live design controls (theme, overlap label, scheduling
//                     defaults, and a "now time" test scrubber)
//
//  Shared primitives, data and theme helpers come from md-theme.jsx via `window`
//  (each <script type="text/babel"> has its own scope, so cross-file sharing is
//  done through window globals — see the Object.assign at the bottom of each file).
// ════════════════════════════════════════════════════════════════════════════
const { mdRgba: rgbaApp, mdSetLight, mdAccentFor, mdIsLight: isLightApp, mdLoadTheme, mdSaveTheme,
  mdThemeResolve, mdPrefersDark, mdOnSystemTheme,
  mdDayItems, mdPrayersFor, mdNextPrayer, mdSetGlass, mdSetVibrancy,
  mdLightGrad, mdDarkHSL, mdBackdropToken, mdBackdropCSS,
  mdNowSec, mdRefreshToday, mdClearPrayerCache, mdDayKey, MDI: MDIApp,
  mdCycleState, mdBuildTimeline, mdTimelineSource, mdBuildGridCycle, mdGridSource, mdCommitGridCycle,
  mdLocation, mdSetLocation, mdSetCalc, mdDetectLocation, mdSaveLocation,
  mdLoadCalc, mdSaveCalc, mdCalcOnMove,
  mdHijriAdjustIsUsers, mdSetHijriAdjust, mdSaveHijriAdjust, mdFetchHijriCorrection,
  TodayScreen, TodayHeader, PrayerCountdown, GridView, ComposeSheet, SettingsSheet, BoardCanvas, useBoards,
  mdCaptureRects, mdMorphOut, minToHHMM, ConfirmDialog, MDBoundary, useMdDismissLayer: useEscape } = window;
const { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } = React;

/* Which day-offset a date string is, or null when it is not a day this app can
   place. Written as a round-trip through mdDayKey rather than as date
   arithmetic alone: the arithmetic is the easy part and the part that is wrong
   twice a year, and asking mdDayKey to agree costs nothing and cannot drift
   from however it defines a day. */
function mdDayOffsetOf(day) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(typeof day === 'string' ? day : '');
  if (!m) return null;
  const target = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const offset = Math.round((target - today) / 86400000);
  return Number.isFinite(offset) && mdDayKey(offset) === day ? offset : null;
}

/* ── full-bleed backdrop: flat gradient + two soft accent glows ──
   Light theme uses a fixed near-white gradient; dark theme derives a deep
   charcoal-navy from `level` (base HSL lightness) so the frosted glass cards
   have something luminous to lift off of, rather than reading as grey smudges.

   The presets themselves live in md-theme.jsx (mdLightGrad / mdDarkHSL), where
   the rest of the palette is: this is not the only surface that has to know
   what the backdrop adds up to, and the pinned day header cannot read a
   constant that lives in the shell it is rendered by. */

/* How long a dropped video or picture takes in a day. The same half hour the
   "+" button gives a new record (md-compose.jsx:516) — a record arriving from
   the board is not a different kind of thing and must not be timed like one. */
const MD_MEDIA_DUR = 30;

/* A phone is a document, not a fixed app with a simulated document underneath
   it. This marker switches only the CSS geometry; the browser remains the sole
   owner of scrolling and therefore keeps native momentum and browser-chrome
   collapse. */
function usePhoneDocument(active) {
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (!active) return undefined;
    root.setAttribute('data-md-phone-document', 'true');
    return () => root.removeAttribute('data-md-phone-document');
  }, [active]);
}

/* A full-screen editor/window above the phone page is a real viewport layer.
   Lock the document once, preserving the exact page offset, and restore it when
   the layer leaves. This is navigation scroll restoration—not scroll mirroring:
   no scroll handler writes into another scroll owner and no animation frame is
   spent copying positions. */
function usePhoneScrollLock(active) {
  const saved = useRef(0);
  useLayoutEffect(() => {
    if (!active || !document.body) return undefined;
    const body = document.body;
    const root = document.documentElement;
    const y = Math.max(0, window.scrollY || 0);
    saved.current = y;
    const prior = {
      position: body.style.position, top: body.style.top, width: body.style.width,
      overflow: body.style.overflow,
    };
    root.setAttribute('data-md-phone-scroll-locked', 'true');
    body.style.position = 'fixed'; body.style.top = `${-y}px`;
    body.style.width = '100%'; body.style.overflow = 'hidden';
    return () => {
      body.style.position = prior.position; body.style.top = prior.top;
      body.style.width = prior.width; body.style.overflow = prior.overflow;
      root.removeAttribute('data-md-phone-scroll-locked');
      window.scrollTo(0, saved.current);
    };
  }, [active]);
}

/* Tabs children are pages, not modal editors. They take ownership of the same
   document scroller as Tabs so Safari gets real page movement (and can collapse
   its browser chrome), while the mounted day/Tabs pages wait behind them. One
   entry restore is enough: nothing mirrors scroll positions while a finger is
   moving and there is never a fixed body underneath an ordinary catalogue. */
function usePhonePanelPage(active) {
  const saved = useRef(0);
  useLayoutEffect(() => {
    if (!active) return undefined;
    const root = document.documentElement;
    saved.current = Math.max(0, window.scrollY || 0);
    root.setAttribute('data-md-phone-panel-page', 'true');
    let second = null;
    const first = requestAnimationFrame(() => {
      window.scrollTo(0, 0);
      second = requestAnimationFrame(() => window.scrollTo(0, 0));
    });
    return () => {
      cancelAnimationFrame(first);
      if (second != null) cancelAnimationFrame(second);
      root.removeAttribute('data-md-phone-panel-page');
      const y = saved.current;
      window.scrollTo(0, y);
      requestAnimationFrame(() => window.scrollTo(0, y));
    };
  }, [active]);
}

/* ONE utility owns the phone's scroll modes (N-11). The scene says which
   containment each standing surface asked for — `phonePage`, `locksScroll`,
   stated in its registry row — and the two mechanisms above are applied from
   that answer. The Tabs page is the one surface with its own choreography:
   its document ownership and scroll memory live in md-find.jsx, WebKit-tuned,
   keyed to the tabs surface. */
function usePhoneSurfaceScroll(scene, phone) {
  const modes = window.mdScenePhoneScroll(scene);
  usePhonePanelPage(phone && modes.page);
  usePhoneScrollLock(phone && modes.locked);
}

/* One portal boundary for every page that replaces Tabs on a phone. Keeping the
   decision here prevents four screen implementations from drifting into four
   subtly different Safari geometries. */
function mdPhonePanelPortal(node, phone) {
  return phone ? ReactDOM.createPortal(node, document.body) : node;
}

function Background({ light, accent, level = 11, gradient = 'cool', blackness = 0, fixed = false }) {
  // a black scrim painted over the backdrop — crank to 1 for a total void.
  // DARK THEME ONLY: the light theme always renders its original clean gradient,
  // so the void / card-depth tweaks never touch it.
  const blackLayer = !light && blackness > 0 ?
  <div style={{ position: 'absolute', inset: 0, background: '#000', opacity: blackness, pointerEvents: 'none' }} /> : null;
  if (light) {
    const g = mdLightGrad(gradient);
    return (
      <div style={{ position: fixed ? 'fixed' : 'absolute', inset: 0, background: `linear-gradient(180deg, ${g[0]} 0%, ${g[1]} 55%, ${g[2]} 100%)` }}>
        <div style={{ position: 'absolute', inset: 0,
          background: `linear-gradient(125deg, ${rgbaApp(accent, 0.035)} 0%, transparent 38%)` }} />
      </div>);

  }
  // lift the dark backdrop off near-black to a deep tinted charcoal so the
  // frosted glass has something to refract — cards read as a luminous lift, not a
  // grey smudge. `level` is the base lightness; hue/sat come from the gradient preset.
  const [H, S, L] = mdDarkHSL(gradient, level);
  const bgGrad = `linear-gradient(180deg, hsl(${H} ${S}% ${L}%) 0%, hsl(${H} ${S - 2}% ${L + 1.5}%) 58%, hsl(${H - 2} ${S - 3}% ${L + 3}%) 100%)`;
  return (
    <div style={{ position: fixed ? 'fixed' : 'absolute', inset: 0, background: bgGrad }}>
      <div style={{ position: 'absolute', top: '-12%', left: '-10%', width: '70%', height: '40%',
        background: `radial-gradient(circle, ${rgbaApp(accent, 0.16)}, transparent 70%)`, filter: 'blur(24px)' }} />
      <div style={{ position: 'absolute', bottom: '8%', right: '-15%', width: '65%', height: '35%',
        background: `radial-gradient(circle, rgba(255,255,255,0.05), transparent 70%)`, filter: 'blur(22px)' }} />
      {blackLayer}
    </div>);

}

/* ── floating "Now" pill — sits just above the "+" button ──
   Visibility and arrow direction are driven entirely by App's `nowBtn` state
   (see recomputeNowBtn). Hidden when the now-line is in view on today.
   Arrow points UP when "now" is below the current view (scroll up to reach it —
   also when viewing a future day) and DOWN when it's above (a past day, or
   scrolled below it). Tapping calls onClick → App.goToNow. */
function NowButton({ accent, show, dir, onClick, inset = 0, bottom = 100, above = false, viewportFixed = false }) {
  const light = isLightApp();
  return (
    // z 39 over the timeline; 65 over the hour grid (60) — still below compose (80),
    // the undo toast (85) and Settings (90), which must keep covering it
    <div style={{ position: viewportFixed ? 'fixed' : 'absolute', insetInlineEnd: 22 + inset, bottom, zIndex: above ? 65 : 39,
      transform: `translateY(${show ? 0 : 10}px) scale(${show ? 1 : 0.9})`, transformOrigin: 'bottom center',
      opacity: show ? 1 : 0, pointerEvents: show ? 'auto' : 'none',
      transition: 'opacity .2s ease, transform .26s cubic-bezier(.22,.61,.36,1)' }}>
      <button aria-label="Jump to now" onClick={onClick} className={show ? 'md-now-rim' : ''} style={{
        position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 38, height: 38, padding: 0, borderRadius: 999, cursor: 'pointer',
        border: '0.75px solid var(--md-accent, #8b5cf6)',
        /* md-float fill + frost (CR-9); the border stays a permanent purple
           in both themes. The className toggling with `show` restarts the 2s
           purple rim each time the pill appears. */
        background: light ? 'rgba(248,249,250,0.78)' : 'rgba(16,18,22,0.76)',
        backdropFilter: 'blur(30px) saturate(125%)', WebkitBackdropFilter: 'blur(30px) saturate(125%)',
        color: accent,
        boxShadow: light ? '0 6px 18px rgba(40,60,110,0.14), inset 0 1px 1px rgba(255,255,255,0.6)'
        : '0 6px 20px rgba(0,0,0,0.42), inset 0 1px 1px rgba(255,255,255,0.14)',
        transition: 'transform .14s ease' }}
      onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.94)'}
      onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
        <svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 24 24" fill="none"
        style={{ transform: dir === 'down' ? 'rotate(180deg)' : 'none', transition: 'transform .2s ease' }}>
          <path d="M12 4.5v15M12 4.5l-6.5 6.5M12 4.5l6.5 6.5" stroke={accent}
          strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>);

}

/* A bare disclosure icon, owned by the prayer status rather than floating as a
   separate control. Collapsed, its hit area and glyph match the board toolbar.

   THREE MEANINGS, NOT TWO. Show, hide — and, since the peek, PIN: the day is
   on screen because the pointer is leaning on it, and this is the press that
   makes it stay. It is a different act from hiding, not a different state of
   it, so the control says so in its name. `aria-expanded` is untouched by the
   distinction: a peeked panel is expanded, because it is there. */
function SidebarToggle({ collapsed, pin = false, onToggle, wrap = null, style = null, ...rest }) {
  const light = isLightApp();
  const size = collapsed ? 36 : 18;
  const glyph = collapsed ? 16 : 12;
  const name = collapsed ? 'Show sidebar' : pin ? 'Pin sidebar' : 'Hide sidebar';
  const mark = (
    <svg aria-hidden="true" focusable="false" width={glyph} height={glyph} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M9 4v16" />
    </svg>);
  /* ── THE COUNTDOWN WEARS THE BUTTON ────────────────────────────────────────
     Given a `wrap`, this control is not a square beside a group of text — it IS
     the group. The countdown owns the arrangement (a glyph beside a name with
     the clock beneath, or a glyph beside the clock in one line) and cannot
     render this component's semantics; this component owns the label, what it
     controls and whether that is open, and cannot know where the glyph goes. So
     it hands its glyph to the countdown's `wrap` and takes back the whole
     group's contents, and every prop the countdown passed — the group's marker,
     the group's own layout — lands on this button.

     One element, not two. A click handler on the surrounding div is a control
     no keyboard reaches (the a11y gate refuses it by name); a second button
     layered over this one is two tab stops and two announcements for one act. */
  if (wrap) {
    return (
      <button data-testid="sidebar-toggle" aria-label={name}
        aria-controls="md-day-sidebar" aria-expanded={!collapsed}
        title={name} onClick={onToggle} {...rest}
        style={{ border: 'none', background: 'transparent', padding: 0, margin: 0,
          cursor: 'pointer', color: 'inherit', font: 'inherit', textAlign: 'start',
          WebkitAppearance: 'none', ...(style || {}) }}>
        {wrap(
          <span aria-hidden="true" style={{ width: size, height: size, padding: collapsed ? 10 : 3,
            boxSizing: 'border-box', flexShrink: 0, display: 'grid', placeItems: 'center',
            color: ink(light ? 0.72 : 0.82) }}>{mark}</span>)}
      </button>);
  }
  return (
    <button data-testid="sidebar-toggle" aria-label={name}
      aria-controls="md-day-sidebar" aria-expanded={!collapsed}
      title={name} onClick={onToggle}
      style={{ width: size, height: size, padding: collapsed ? 10 : 3, border: 'none', cursor: 'pointer',
        display: 'grid', placeItems: 'center', color: ink(light ? 0.72 : 0.82),
        background: 'transparent', transition: 'color .18s ease, transform .14s ease' }}
      onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.92)'}
      onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
      {mark}
    </button>);
}

function OpenBoardButton({ onClick }) {
  const light = isLightApp();
  return (
    <div style={{ position: 'absolute', top: 0, bottom: 0, insetInlineStart: 434, insetInlineEnd: 0,
      zIndex: 94, display: 'grid', placeItems: 'center', pointerEvents: 'none' }}>
      <button data-testid="open-board" onClick={onClick} aria-label="Open desk"
        style={{ height: 36, padding: '0 2px', borderRadius: 0, pointerEvents: 'auto',
          display: 'inline-flex', alignItems: 'center', gap: 7, cursor: 'pointer',
          border: 'none', background: 'transparent', boxShadow: 'none',
          color: ink(light ? 0.7 : 0.78), fontSize: 12.5, fontWeight: 650,
          transition: 'transform .15s ease, color .18s ease' }}
        onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.94)'}
        onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
        {MDIApp.desk('currentColor', 15)}
        <span style={{ opacity: 0.5 }}>Open desk</span>
      </button>
    </div>);
}

/* testing control: scrub the virtual "now" time, with a readable HH:MM AM/PM readout */
function mdFmtClock(m) {
  const mm = (Math.round(m) % 1440 + 1440) % 1440;
  const h = Math.floor(mm / 60),min = mm % 60,ap = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(min).padStart(2, '0')} ${ap}`;
}
function NowTimeSlider({ value, onChange }) {
  return (
    <TweakRow label="Now time" value={mdFmtClock(value)}>
      <input type="range" className="twk-slider" min={0} max={1435} step={5}
      value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </TweakRow>);

}

/* ── floating primary "+" action button (bottom-right) — opens the compose sheet.
   `inset` is the column gutter: on a tablet the day sits in a centred column, and
   a "+" pinned to the window's edge would float in empty space beside it. ── */
function AddButton({ accent, onClick, inset = 0, viewportFixed = false }) {
  const light = isLightApp();
  /* Mobile Safari already keeps its browser toolbar outside visualViewport.
     A 28px fallback therefore became a second footer gutter. Installed mode
     still reports the home-indicator inset, which remains authoritative. */
  const bottom = viewportFixed
    ? 'max(var(--md-safe-bottom), 16px)'
    : 'max(var(--md-safe-bottom), 28px)';
  return (
    /* No guard, and none needed anywhere in the panel: a panel that has floated
       clear of the device's edges zeroes these tokens for everything inside it.
       The resting gutter is 16px for a phone viewport and 28px elsewhere; a
       nonzero home-indicator inset wins over either one. */
    <div style={{ position: viewportFixed ? 'fixed' : 'absolute', insetInlineEnd: 22 + inset,
      bottom, zIndex: 40 }}>
      <button aria-label="Add" onClick={onClick} style={{
        width: 60, height: 60, borderRadius: '50%', border: 'none', cursor: 'pointer',
        display: 'grid', placeItems: 'center', background: accent,
        boxShadow: light ?
        `0 8px 22px ${rgbaApp(accent, 0.45)}, inset 0 1px 1px rgba(255,255,255,0.6)` :
        `0 8px 24px ${rgbaApp(accent, 0.55)}, inset 0 1px 1px rgba(255,255,255,0.5)`,
        transition: 'transform .15s ease' }}
      onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.92)'}
      onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
        <svg aria-hidden="true" focusable="false" width="26" height="26" viewBox="0 0 24 24" fill="none">
          <path d="M12 5v14M5 12h14" stroke={onAcc()} strokeWidth="2.4" strokeLinecap="round" />
        </svg>
      </button>
    </div>);

}

/* ── the "+" on the board ─────────────────────────────────────────────────────
   AddButton's twin, in the board's bottom-right corner. A board is made from
   the Desks screen (hero-boards-menu) — the one place desk creation lives —
   so out here "+" makes records and nothing else. It asked the two-question
   for a while, and that question survived only one person: you meant "New
   desk" from a "+" pinned over a drawing and you meant "New record" every
   other time, since the panel's own AddButton still says so. One thing per
   corner.

   `md-board-fab` is styled in index.html for one reason: Excalidraw lays its own
   chrome out differently below ~730px of canvas, where it moves everything into
   a bar across the bottom — exactly where this sits. The stylesheet lifts the
   button above that bar when it appears, so the two can never overlap at any
   width. Doing that in CSS rather than by measuring here means it is right
   during a resize, not one frame after it. ── */
function BoardFab({ accent, onNewPage }) {
  const light = isLightApp();
  return (
    /* No `bottom` here — it is in the stylesheet, in both of its values. An
       inline one would win over the rule that lifts this above the compact
       toolbar, silently, and the button would sit on the bar. */
    <div className="md-board-fab" style={{ position: 'absolute', insetInlineEnd: 22, zIndex: 12 }}>
      {/* 46, not the panel's 60, and it is the record sheet's Done button it is
          matching rather than a number picked for the corner. Out here the "+"
          floats over somebody's drawing instead of over a list, and at 60 it was
          the largest object on the canvas — a permanent 60pt disc of accent
          sitting on a blank record. The sheet's ✓ is the app's other round accent
          button on this side of the window, they are seen one after the other,
          and two circles that nearly match are worse than either size. */}
      <button aria-label="Add" onClick={onNewPage} style={{
        width: 46, height: 46, borderRadius: '50%', border: 'none', cursor: 'pointer',
        display: 'grid', placeItems: 'center', background: accent,
        // The shadow comes down with it: 8/22 was drawn under a 60pt disc and
        // under a 46 it reads as the button hovering off the canvas.
        boxShadow: light ?
        `0 6px 16px ${rgbaApp(accent, 0.45)}, inset 0 1px 1px rgba(255,255,255,0.6)` :
        `0 6px 18px ${rgbaApp(accent, 0.55)}, inset 0 1px 1px rgba(255,255,255,0.5)` }}>
        <svg aria-hidden="true" focusable="false" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M12 5v14M5 12h14" stroke={onAcc()} strokeWidth="2.4" strokeLinecap="round" />
        </svg>
      </button>
    </div>);

}

/* Rescheduling uses the same adaptive overlay as compose. The FAB commits;
   the X, Escape and scrim discard the draft. */
function RescheduleSheet({ accent, light, item, dayOffset, nowMin, showAnchors, weekStart, onCommit, onClose }) {
  const ScheduleOverlayC = window.ScheduleOverlay;
  const [sched, setSched] = useState(() => ({
    offset: item.offset != null ? item.offset : dayOffset,
    // A legacy all-day record has no min. The picker has no untimed mode to
    // return it to, so re-homing one gives it the current time and promotes it
    // onto the timeline — which is the only way those records leave limbo.
    min: item.min != null ? item.min : Math.round((nowMin != null ? nowMin : mdNowSec() / 60) / 5) * 5,
    dur: item.dur || 30
  }));
  const confirm = () => {onCommit(sched);onClose();};
  return (
    ScheduleOverlayC ?
      <ScheduleOverlayC accent={accent} light={light} value={sched} showAnchors={showAnchors} weekStart={weekStart}
        onChange={setSched} onConfirm={confirm} onDiscard={onClose} /> : null);

}

/* Persisted Tweaks defaults. The EDITMODE markers let the host's Tweaks panel
   rewrite this block in place — keep it plain JSON, no comments inside, or the
   rewrite has nothing to parse. Exposed controls: theme, card depth (glow / halo /
   shadow / blur), background gradient + blackness, and the "now time" test
   scrubber. `nowMin` pins the clock to a minute past midnight for design work;
   null means follow the real clock, which is what ships.

   `cardHalo` is a DARK-ONLY value — Glass reads it in the dark branch alone, so
   nothing here can disturb the light theme. It was 0.14, which put a 22px white
   ring at 14% around every card; against a near-black backdrop that stopped
   reading as glass catching light and started reading as a lit sign, loudest on
   the hero because the hero is the biggest card. 0.05 keeps the edge separated
   from the background and lets the 0.5px stroke do the describing. The other two
   layers were measured and left alone: the black drop shadow is invisible on a
   black backdrop, and the accent wash behind the hero is indistinguishable at
   0.16 vs 0.10, so neither was what made it heavy. */
const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "theme": "dark",
  "accent": "#C4B5FF",
  "nowMin": null,
  "cardGlow": 0,
  "cardHalo": 0.05,
  "cardShadow": 1,
  "cardBlur": 0.4,
  "bgGradient": "cool",
  "bgBlackness": 1
} /*EDITMODE-END*/;

/* ── App: root component, owns all state (viewed day, virtual clock, now-button) ── */
function App({ onLight, onSignedOut }) {
  const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);
  /* The user's saved choice is authoritative; TWEAK_DEFAULTS only seeds the very
     first run. The ref lets the design-time Tweaks radio keep working — it fires
     only when t.theme actually CHANGES, so the initial default never overwrites a
     preference restored from storage. */
  const [theme, setThemeState] = useState(() => mdLoadTheme() || TWEAK_DEFAULTS.theme);
  const tweakTheme = useRef(t.theme);
  useEffect(() => {
    if (t.theme !== tweakTheme.current) { tweakTheme.current = t.theme; setThemeState(t.theme); }
  }, [t.theme]);
  /* Settings names the theme it wants rather than asking for "the other one":
     a preference the user is looking at is chosen, not flipped. */
  const chooseTheme = useCallback((next) => {
    if (next !== 'light' && next !== 'dark' && next !== 'system') return;
    mdSaveTheme(next);
    tweakTheme.current = next;
    setThemeState(next);
    setTweak('theme', next);            // keeps the Tweaks radio honest in design mode
  }, [setTweak]);
  /* What the device says, kept in state so that a phone going dark at sunset
     repaints an app that is already open. The listener runs whatever the choice
     is; mdThemeResolve is what decides whether the answer changes anything, so
     a user on Light sees nothing happen — which is the promise Light makes. */
  const [systemDark, setSystemDark] = useState(() => mdPrefersDark());
  useEffect(() => mdOnSystemTheme(setSystemDark), []);
  const light = mdThemeResolve(theme, systemDark) === 'light';
  mdSetLight(light);
  // glass card depth — now driven by Tweaks (glow / halo / shadow / blur)
  mdSetGlass({ fill: 0.03, stroke: 0.05, sheen: 0.19,
    glow: t.cardGlow, halo: t.cardHalo, shadow: t.cardShadow, blurMul: t.cardBlur });
  mdSetVibrancy(1.6);
  const accent = mdAccentFor(t.accent, light);
  useEffect(() => {onLight && onLight(light);}, [light]);
  useEffect(() => { try { document.documentElement.style.setProperty('--md-accent', accent); } catch (e) {} }, [accent]);
  /* WHAT COLOUR THE VOID IS, published once for whoever has to match it.
     The pinned day header paints a solid material (md-today.jsx TodayHeader) and
     that material has to BE the backdrop, or it is a rectangle sitting on it —
     which is exactly what shipped, because the header carried a constant
     measured against a backdrop the blackness slider then moved out from under
     it. Set here rather than in Background because a custom property is for the
     whole document and Background renders twice (panel and canvas gate). */
  useEffect(() => {
    try {
      document.documentElement.style.setProperty('--md-backdrop-rgb',
        mdBackdropToken(light, t.bgGradient, t.bgBlackness));
    } catch (e) {}
  }, [light, t.bgGradient, t.bgBlackness]);

  /* ⌘Z in the day, armed for as long as the app is open.

     The day is the surface with the least forgiveness in it: everywhere else
     had something — the whiteboard has Excalidraw's history, a database its own
     stack, your writing the editor's burst ring — and the day had a four-second
     toast on a deletion. Look away and the record was simply gone.

     The key list is DERIVED from md-store's own list of day kinds rather than
     written out here. md-store is the module that decides what a day is made
     of; a list copied into this file would mean a kind added there is silently
     outside undo, with nothing to say so. The guard is for headless runs, where
     the app renders without the history module.

     This scope sits at the bottom of the stack for the whole session. A window
     opened over it — Projects — arms above it and hands back on close. */
  useEffect(() => {
    if (typeof window.mdHistoryOpen !== 'function') return undefined;
    window.mdHistoryOpen('day', {
      keys: (window.MD_STORE_KINDS || []).map((kind) => `md:${kind}:`),
      text: (window.MD_STORE_RECORD_KINDS || []).map((kind) => `md:${kind}:`),
    });
    return () => window.mdHistoryClose('day');
  }, []);

  /* Viewport shape. `L.rail` is the one branch that changes the SCREEN rather
     than just its measurements: at desktop widths the month, the prayer times
     and the floating actions all move into a permanent left rail. Everything
     narrower keeps the phone's arrangement and only gets a wider column. */
  const L = useMdLayout();
  usePhoneDocument(L.phone);
  /* ── the two desk facts, and the one that is not a surface ────────────────
     `desksOpen` is the chooser; `deskShown` is "the day is folded and the desk
     is uncovered". They were one tri-state until the phone gained the desktop's
     model, and a tri-state is exactly what could not express the new middle
     case: a desk that is LOADED and standing under the day.

     `deskGround` is that case, and it is deliberately not a scene entry. On
     every wider layout the board is ground — it is not open, nothing dismisses
     it, and Escape has no business with it — so on a phone it is ground too,
     and ground has no place in a stack of open things. It is sticky because the
     drawing must be built once: fold, unfold, fold again, and the desk has not
     moved a pixel. Phone boards are still initialized only after the person
     explicitly asks for one, so useBoards cannot create or load a board merely
     because the app or More mounted. */
  const [desksOpen, setDesksOpen] = useState(false);
  const [deskShown, setDeskShown] = useState(false);
  const [deskGround, setDeskGround] = useState(false);
  /* AND IT CLOSES THE CHOOSER ITSELF. `desks` and `board` share the scene's
     `panel` group, so the two can never stand together; saying so here is
     Marwan's item 5 — choose a desk and the chooser is gone, the day is folded,
     the drawing is already loaded — rather than a swap inferred from a group
     and then argued with by the mirror below. */
  const showDesk = useCallback(() => { setDesksOpen(false); setDeskGround(true); setDeskShown(true); }, []);
  /* A device preference, like the open board: it changes this window, not the
     user's synced day. It is remembered so returning to a board does not rebuild
     furniture the user deliberately put away. Narrow layouts ignore it. */
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => mdRead('md:sidebarCollapsed', false) === true);
  useEffect(() => { mdWrite('md:sidebarCollapsed', sidebarCollapsed); }, [sidebarCollapsed]);
  /* THREE QUESTIONS THAT USED TO BE ONE, and separating them is what let the
     phone join without disturbing anything.

       deskUnder        is there a desk beneath the day on this layout. Every
                        layout that has one offers the toggle and stacks the
                        day above it — a phone has one now.
       canvasCollapsed  the DESK'S OWN SETTING: a persisted device preference,
                        which is what the peek is a pointer affordance for.
       panelCollapsed   "the day is put away", whichever layout is asking. On a
                        phone that is a PLACE rather than a preference — the
                        journal records the step and Back brings the day back —
                        so it is `deskShown` and it is not remembered.

     The peek must arm on the SETTING. A desktop window dragged under 700px is a
     phone with a mouse, and the phone panel does not float, so a peek there
     would pull a transparent day out over a live drawing. */
  const deskUnder = L.canvas || L.phone;
  const canvasCollapsed = L.canvas && sidebarCollapsed;
  const panelCollapsed = L.phone ? deskShown : canvasCollapsed;

  /* Boards belong to the canvas, so a screen without one never asks which board
     is open — and asking is what creates the first board, which is why a phone
     must not: it would mint a board it has no way to show and then sync it to
     every other device. `boardApiRef` is Excalidraw's own handle, handed back by
     BoardCanvas, and it exists here for one job: writing the current drawing out
     before switching away from it. */
  /* `createIfEmpty` — the second argument — used to be `L.canvas` alone, and the
     prohibition it enforced was exact for as long as it lasted: asking which
     board is open is what MINTS the first one, and a phone had no way to show
     what it had minted, so it would have synced a board nobody could see. That
     expires here, and only here — the phone has a desk now, and only after an
     explicit press for one. */
  const { boards, openId: boardId, refresh: refreshBoards } = useBoards(
    L.canvas || deskGround || desksOpen, L.canvas || deskGround);
  const boardApiRef = useRef(null);
  /* Whether the open board has anything on it, reported by the canvas itself —
     it is the only thing that sees the drawing change as it is drawn. One
     control depends on it: "Open board" (see OpenBoardButton). It starts TRUE,
     which is the answer that shows nothing: until the canvas has read the
     stored scene, a board is assumed to hold something, so the invitation never
     flashes over a drawing on the way in. */
  const [boardHasContent, setBoardHasContent] = useState(true);
  const manifestActionsRef = useRef({});

  const scrollRef = useRef(null);
  const scrollToNowRef = useRef(null); // kept in sync below; used by the visibility handler
  // Was seeded { fajr:true, dhuhr:true } so the design had two prayers already
  // marked. Nothing ever set it — the real per-item done state is `completed`
  // below — so it was pure demo dressing and now starts empty.
  const [prayed, setPrayed] = useState({});
  const [reminders, setReminders] = useState({ fajr: true, dhuhr: true, asr: true, maghrib: true, isha: true });
  // completed/"done" cards (prayers + events), keyed `${YYYY-MM-DD}|${uid}`. Persisted,
  // and seeded so today's earlier prayers (Fajr, Dhuhr) already read as done on first run.
  const [completed, setCompleted] = useState(() => mdStoreCompletedFlat());
  /* Skips the mount pass. These effects exist to persist CHANGES; on mount the
     state was just read from localStorage, so writing it straight back is at
     best pointless and at worst destructive — a sync pull landing between the
     read and the write would be overwritten by what the app already had. */
  const hydrated = useRef(false);
  useEffect(() => { hydrated.current = true; }, []);
  useEffect(() => { if (!hydrated.current) return; mdStoreWriteCompletedFlat(completed); }, [completed]);
  /* Timed events for a day, keyed "YYYY-MM-DD". These are the user's own now —
     the MD_WEEK_EVENTS demo fixtures are gone — so unlike before this is
     PERSISTED. It previously lived in memory keyed by day offset, which lost
     every edit on reload and, worse, re-pointed at a different date each
     midnight. */
  const [dayEvents, setDayEvents] = useState(() => mdStoreAll('events'));
  useEffect(() => { if (!hydrated.current) return; mdStoreWriteAll('events', dayEvents); }, [dayEvents]);
  // untimed "All Day" Records (notes / tasks / ideas), keyed "YYYY-MM-DD". Persisted.
  const [allDayRecords, setAllDayRecords] = useState(() => mdStoreAll('records'));
  useEffect(() => { if (!hydrated.current) return; mdStoreWriteAll('records', allDayRecords); }, [allDayRecords]);
  const [projectRecords, setProjectRecords] = useState(() => window.mdProjectRecordIndex ? window.mdProjectRecordIndex() : []);
  /* ── one render source (OD-1) ──────────────────────────────────────────────
     Every project-record write announces itself at the module's own chokepoint,
     and this is the one subscriber: re-read the index, and React carries the
     fact everywhere `projectRecords` flows — the Workshop's cards, the Project
     window's board, the timeline rail. A title changed in one window is simply
     true in the others, because nobody holds a second copy to go stale. */
  useEffect(() => {
    const reindex = () => setProjectRecords(window.mdProjectRecordIndex());
    window.addEventListener('md:project-records-written', reindex);
    return () => window.removeEventListener('md:project-records-written', reindex);
  }, []);
  useEffect(() => {
    if (!hydrated.current) return;
    window.dispatchEvent(new CustomEvent('md:records-changed', { detail: { at: Date.now() } }));
  }, [dayEvents, allDayRecords, projectRecords, completed]);
  /* The open record: null, or { seq, initial, fromGrid, gridUid, timeBinding }.
     `seq` is what makes opening a SECOND record while one is open work — it keys
     the sheet, so React remounts it on a fresh record instead of leaving the last
     one's text in the editor. `initial` is the record being opened, absent for a
     brand new one (the sheet mints its own uid and hands it back on the first
     live commit). */
  const [compose, setCompose] = useState(null);
  const composeSeq = useRef(0);
  /* What the open record currently is — updated on every keystroke by onLive.
     md-app needs it for the two things that happen AFTER the writing: dropping
     a record that was left blank, and putting a deleted one back. Reading it out
     of the day stores would work until the When picker moved the record to
     another day, so the sheet's own last word is what is kept. */
  const liveRef = useRef(null);
  /* And what was DRAWN before the open record existed. The grid paints a block
     the moment you finish the gesture, before the sheet is up; under P-5 the
     record behind it is not written until it is confirmed, so this is the one
     thing a discarded sheet can leave behind. `mdComposeGridDrop` decides. */
  const pendingGridRef = useRef(null);
  // destructive-confirm + reschedule overlays (timeline swipe actions)
  const [confirm, setConfirm] = useState(null); // { title, message, onConfirm }
  const [reschedule, setReschedule] = useState(null); // { item }
  const [settingsView, setSettingsView] = useState('root');
  /* A walk WITHIN a standing surface — a Settings sub-view, a craft's page,
     a database chosen inside the catalogue. Recorded for Recents and written
     onto the CURRENT entry so the URL stays shareable. recordVisit itself
     never pushes — a whole-screen sub-view gets its one entry through the
     frame ride (settings-frame, N-14), where a Back visibly steps it out;
     the old helper's pushes were extra steps NOTHING dismissed, which is
     the difference. */
  const recordVisit = useCallback((place) => {
    const target = window.mdNavigationTarget?.(place);
    if (!target) return;
    window.mdNavigationRecord?.(target);
    const href = window.mdNavigationHref?.(target);
    if (href && window.history?.replaceState && href !== window.location.href)
      window.history.replaceState({ ...(window.history.state || {}), mdTarget: target }, '', href);
  }, []);
  const visitSettingView = useCallback((view) => {
    /* Recents and the URL only — NOT setSettingsView. Echoing the sub-view
       back into initialView made every internal step look like a deep link,
       reseeding the frame walk flat so nothing could ever ride; deep links
       still reseed through the place dispatcher, which owns settingsView. */
    if (view !== 'root') recordVisit({ go: 'settings', view });
  }, [recordVisit]);
  /* A walk ONTO a new surface whose scene change follows in an effect — the
     D3 database window. The pushed entry carries the destination's URL and
     an unstamped mdTarget, and the history controller ADOPTS it when the
     scene change lands: one entry, stamped once, exactly like a navigation
     from Tabs. The entry beneath keeps the catalogue's own URL, which is
     what Back must reveal (the D3 defect was this address leaking). */
  const pushVisit = useCallback((place) => {
    const target = window.mdNavigationTarget?.(place);
    if (!target) return;
    window.mdNavigationRecord?.(target);
    const href = window.mdNavigationHref?.(target);
    if (href && window.history?.pushState)
      window.history.pushState({ mdTarget: target }, '', href);
  }, []);
  /* ── the window stack, remembered (CS-3) ─────────────────────────────────
     One boot read, validated by md-ui.ts against what still resolves — a
     refresh is not an eviction. The SHEET is deliberately not here: a record
     lives from its first keystroke, so nothing is lost, and a sheet that
     reopens uninvited after a reload is a surprise, not a memory. What the
     sheet DOES remember is how big it was, which is CS-4 and is a different
     kind of fact — a preference, not an open window.

     main has since given the app a ROUTE as well (md-navigation.ts), and the
     two answer different questions: the route says where this URL points, the
     stack says what you had open. Where a route names a destination it is the
     later word and wins, because it was typed; where it names none — the bare
     origin, a cold launch — the stack is the only memory there is. */
  const [bootUi] = useState(() => (window.mdUiLoad ? window.mdUiLoad() : null));
  /* ── the scene (N-4 panel screens, N-5 the workspace, N-8 Tabs) ───────────
     The ONE stack (md-scene.ts). Settings, Crafts, the Workshop, the Project
     window, the database catalogue/window and Tabs itself live here now —
     opened, excluded and dismissed by the registry's rules, never by
     hand-composed teardowns. Sheets and menus join in N-9.

     The boot restore: Tabs seats first when its return marker says it stood
     (it is the home every restored destination stands over), then the SAME
     five facts md-ui.ts always kept, in standing order: workshop, the
     project on it, or the catalogue — and a panel screen last. md-ui already
     refuses a project over a closed Workshop; when a stale blob claims both
     workspace families, the workshop family wins the slot, because the scene
     holds one occupant. */
  const [scene, setScene] = useState(() => {
    let s = window.mdSceneEmpty();
    if (window.mdTabsHasReturn?.()) s = window.mdSceneOpen(s, 'tabs');
    if (!bootUi) return s;
    if (bootUi.projectsOpen) {
      s = window.mdSceneOpen(s, 'workshop',
        bootUi.projectsView ? { view: bootUi.projectsView } : null);
      if (bootUi.projectWindowId)
        s = window.mdSceneOpen(s, 'project', { id: bootUi.projectWindowId });
    } else if (bootUi.databasesOpen) {
      s = window.mdSceneOpen(s, 'catalogue');
    }
    if (bootUi.craftsOpen) s = window.mdSceneOpen(s, 'crafts');
    return s;
  });
  const settingsOpen = window.mdSceneIsOpen(scene, 'settings');
  const craftsOpen = window.mdSceneIsOpen(scene, 'crafts');
  /* Live mirror for long-lived callbacks reached from popstate/events, whose
     closures can be stale by the time they run. */
  const sceneRef = useRef(scene); sceneRef.current = scene;
  /* ── Tabs rides the scene (N-8) ──────────────────────────────────────────
     The Finder's openness is the scene's `tabs` entry; it asks to stand or
     leave and the reducer answers. `covered` is a boot fact computed once:
     the marker re-seated Tabs, but something above it (or a routed target
     about to open) means it should come back hidden, exactly as the old
     private flag did. */
  const tabsStand = useCallback(() => setScene((s) => window.mdSceneOpen(s, 'tabs')), []);
  const tabsLeave = useCallback(() => setScene((s) => window.mdSceneDismiss(s, 'tabs')), []);
  const tabsBootCovered = useRef(null);
  if (tabsBootCovered.current === null) tabsBootCovered.current =
    window.mdSceneTop(scene) !== 'tabs' || !!window.mdNavigationTargetFromUrl?.();
  /* ── the history controller (N-7) ─────────────────────────────────────────
     The browser's stack mirrors the scene, kept by the JOURNAL (md-scene.ts):
     the scene that stood at every entry this session wrote, indexed by the
     `mdDepth` stamped on the entry. Opening pushes one entry per gesture —
     Tabs included, on its ordinary day URL (N-8); dismissing moves the
     journal FIRST and asks the browser to walk after (the D1 law — a
     swallowed back() self-heals because applying an entry is idempotent);
     popstate looks the depth up and applies exactly that scene — never a
     re-navigation forward, never the old close-everything unwind. */
  const historyRef = useRef(null);
  if (!historyRef.current) historyRef.current = window.mdSceneJournalStart(
    scene, `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`);
  useEffect(() => {
    /* Boot: this session's entry is depth zero of a fresh journal, whatever
       a dead session's stamp on it said — the era refuses the old numbers. */
    const st = window.history?.state;
    if (window.history?.replaceState) window.history.replaceState(
      { ...(st && typeof st === 'object' ? st : {}), mdDepth: 0, mdEra: historyRef.current.era },
      '', window.location.href);
  }, []);
  useEffect(() => {
    const journal = historyRef.current;
    const st = window.history?.state && typeof window.history.state === 'object'
      ? window.history.state : null;
    /* mdNavigationNavigate already pushed this entry for the destination that
       caused the change (a Tabs row, a wiki link, a pinned card). Whatever
       shape the scene change took — even a shrink, like a day row folding the
       stack — the ENTRY is the navigation: adopt it as the next depth rather
       than pushing a second one or walking back over it. */
    if (st && st.mdTarget && typeof st.mdDepth !== 'number') {
      const scenes = [...journal.scenes.slice(0, journal.pointer + 1), scene];
      historyRef.current = { ...journal, scenes, pointer: scenes.length - 1 };
      window.history.replaceState({ ...st, mdDepth: historyRef.current.pointer,
        mdEra: journal.era }, '', window.location.href);
      window.__mdSceneNow = scene;
      window.__mdJournalNow = historyRef.current;
      return;
    }
    const out = window.mdSceneJournalRecord(journal, scene);
    historyRef.current = out.journal;
    if (out.op === 'push' && window.history?.pushState)
      window.history.pushState({ mdDepth: out.journal.pointer, mdEra: out.journal.era },
        '', window.location.href);
    else if (out.op === 'back' && out.count > 0 && window.history?.go)
      window.history.go(-out.count);
    else if (out.op === 'back' && out.count === 0 || out.op === 'replace')
      /* No walk was owed — a depth-zero close (boot restore, cold deep
         link) or a middle dismissal rewriting the standing entry — so no
         popstate will run the URL reconcile. Square the hash here, or a
         routed `#md=…` naming the closed surface survives into the next
         reload (N-9). */
      reconcileRoutedRef.current?.(scene);
    /* Observability, not API: the live scene and journal, for DevTools and
       the walkthrough audits. Nothing in the app reads these. */
    window.__mdSceneNow = scene;
    window.__mdJournalNow = historyRef.current;
  }, [scene]);
  /* ── the ride: deep surfaces seat themselves (N-9) ───────────────────────
     A confirm, a datepicker, a column menu — owned far from the App —
     announce through the dismiss arbiter's registration (useMdDismissLayer's
     `surface`). The scene seats them by registry id, the journal gives each
     one entry, and a walk that retires the entry answers with the rider's
     own close: Back dismisses the picker, never the surface under it. */
  const ridersRef = useRef(new Map());
  const reconcileRoutedRef = useRef(null);
  useEffect(() => {
    const ride = (event) => {
      const { token, open } = event?.detail || {};
      if (!token || !token.surface) return;
      const list = ridersRef.current.get(token.surface) || [];
      if (open) {
        ridersRef.current.set(token.surface, [...list, token]);
        setScene((s) => {
          const next = window.mdSceneOpen(s, token.surface);
          /* Seated — or already standing (a confirm over a confirm shares
             the entry). Only seated riders may be closed by a walk below:
             a surface the registry refuses is Escape-only, never forced. */
          if (next !== s || window.mdSceneIsOpen(s, token.surface)) token.seated = true;
          return next;
        });
      } else {
        const remaining = list.filter((t) => t !== token);
        ridersRef.current.set(token.surface, remaining);
        if (!remaining.length) setScene((s) => window.mdSceneDismiss(s, token.surface));
      }
    };
    window.addEventListener('mdsceneride', ride);
    return () => window.removeEventListener('mdsceneride', ride);
  }, []);
  useEffect(() => {
    for (const [surface, list] of ridersRef.current) {
      const top = list[list.length - 1];
      if (!top || !top.seated || window.mdSceneIsOpen(scene, surface)) continue;
      top.close();
      /* Stacked same-id riders: the closed one unregisters above; whoever
         remains is re-seated, so the next Back addresses that one too. A
         rider whose close declines to leave (the recovery code, until it is
         acknowledged) is re-seated the same way — Back bounces rather than
         reaching beneath it. */
      const remaining = ridersRef.current.get(surface) || [];
      if (remaining.length) setScene((s) => window.mdSceneOpen(s, surface));
    }
  }, [scene]);
  /* ── the workspace, read from the scene (N-5) ────────────────────────────
     The Workshop, the Project window and the database family stopped being
     useState booleans: what stands is what the stack says, and the derived
     names below keep every consumer reading the same facts they always did.
     The workshop live-mirror ref died with them — there is no separate copy
     of this state left to go stale, which is M-1 made structural. */
  const workshopEntry = scene.stack.find((entry) => entry.id === 'workshop') || null;
  const projectEntry = scene.stack.find((entry) => entry.id === 'project') || null;
  const databaseEntry = scene.stack.find((entry) => entry.id === 'database') || null;
  const catalogueStanding = window.mdSceneIsOpen(scene, 'catalogue');
  const projectsOpen = !!workshopEntry;
  const projectsView = (workshopEntry && workshopEntry.params && workshopEntry.params.view) || null;
  /* ── the Project window, singular ────────────────────────────────────────
     One project, in a room of its own. ONE AT A TIME: opening a second
     project replaces the first (the registry's same-surface rule), because
     two of these stacked would be a pile nobody can find their way out of.
     It stands wherever it was OPENED: on the Workshop when it was chosen
     inside one, straight over search or the day when it arrived from there —
     the stack is the provenance, and the way out steps back to exactly it. */
  const projectWindowId = (projectEntry && projectEntry.params && projectEntry.params.id) || null;
  const [projectWindowFrame, setProjectWindowFrame] = useState(null);
  /* The window's own openness counts: a catalogue that closed BENEATH a
     standing D3 window leaves the scene, and the space must stay mounted
     for the window it still holds (N-9). */
  const [databaseWindowOpen, setDatabaseWindowOpen] = useState(false);
  const databasesOpen = !!databaseEntry || catalogueStanding || databaseWindowOpen;
  const databasesTarget = useMemo(() => (databaseEntry && databaseEntry.params
    ? { id: databaseEntry.params.id, row: databaseEntry.params.row || null } : null),
  [databaseEntry]);
  const openProjectWindow = useCallback((id, frame = null) => {
    if (!id) return;
    const target = window.mdNavigationTarget?.({ go: 'project', id });
    if (target) window.mdNavigationRecord?.(target);
    setProjectWindowFrame(frame);
    setScene((s) => window.mdSceneOpen(s, 'project', { id }));
  }, []);
  const closeProjectWindow = useCallback(() => {
    setProjectWindowFrame(null);
    setScene((s) => window.mdSceneDismiss(s, 'project'));
  }, []);
  /* Everything that used to mean "something else has the workspace now" is a
     scene dismissal: it removes the workshop family — project riding the
     cascade — and can never reach a neighbour. */
  const leaveProjects = useCallback(() => {
    setProjectWindowFrame(null);
    setScene((s) => window.mdSceneDismiss(window.mdSceneDismiss(s, 'workshop'), 'project'));
  }, []);
  /* Walking to a pursuit or a domain is a walk INTO the Workshop's own pages:
     the reducer replaces a standing project, because this is a walk somewhere,
     not an unwind back to what the project stands on. */
  const openWorkshopPage = useCallback((kind, id) => {
    if (!id) return;
    const target = window.mdNavigationTarget?.({ go: kind, id });
    if (target) window.mdNavigationRecord?.(target);
    setScene((s) => window.mdSceneOpen(s, 'workshop', { view: { kind, id } }));
  }, []);
  /* Manifest Space is called Crafts since main's rename, so the fact the stack
     remembers is renamed with it — md-ui.ts stores the surface, not an old
     word for it. Its openness is the scene's now (see the panel slice above);
     the target it opened onto stays a plain fact here. */
  const [craftsTarget, setCraftsTarget] = useState(null);
  /* The target lives exactly as long as Crafts stands in the scene — however
     Crafts left, the next opening starts from its list, not a stale row. */
  useEffect(() => { if (!craftsOpen) setCraftsTarget(null); }, [craftsOpen]);
  /* Once opened, Crafts stays one React instance for the rest of the session.
     Hiding a panel is navigation; it is not permission to throw away its list,
     query, expanded row, or device-availability answers. Most importantly, a
     workspace refresh can no longer produce an empty replacement mount. */
  const craftsMounted = useRef(false);
  if (craftsOpen) craftsMounted.current = true;
  /* A window opened INSIDE the catalogue — the scene's `dbwindow` (N-9).
     While the catalogue-hosted space reports one open it stands over
     `catalogue`, so its dismissal and the catalogue's are two different
     dismiss targets. When a walk or a rule retires the entry instead, the
     space is asked to close its own window: only it knows the openId. A
     direct-target window is already the scene's `database` entry and is
     never double-seated. */
  const seatDbWindow = useCallback((openId) => {
    const up = openId !== null && openId !== false;
    setDatabaseWindowOpen(up);
    setScene((s) => (up && !window.mdSceneIsOpen(s, 'database')
      ? window.mdSceneOpen(s, 'dbwindow', { id: openId })
      : window.mdSceneDismiss(s, 'dbwindow')));
  }, []);
  const dbWindowStood = useRef(false);
  useEffect(() => {
    const stands = window.mdSceneIsOpen(scene, 'dbwindow');
    if (!stands && dbWindowStood.current && databaseWindowOpen)
      window.dispatchEvent(new CustomEvent('mddbwindowretire'));
    dbWindowStood.current = stands;
  }, [scene]);
  /* Written on every stack change, cleared by the module when everything is
     closed. The five facts and nothing else — md-ui.ts drops anything more at
     the door, so the sheet can never ride along by accident. */
  useEffect(() => {
    if (window.mdUiSave) {
      window.mdUiSave({ projectsOpen, projectsView, projectWindowId,
        databasesOpen, craftsOpen });
    }
  }, [projectsOpen, projectsView, projectWindowId, databasesOpen, craftsOpen]);
  /* Every child panel of Tabs leaves through the same door. For Desks this is
     deliberately the dismiss button only: choosing or creating a Desk changes the
     canvas behind the chooser without dismissing the chooser itself.
     Whether Tabs comes back as a side-destination beside a docked workspace
     is asked OF THE SCENE BEING REVEALED (mdSceneDocksWorkspace) — a caller
     that just dismissed passes the scene it made. The render-time copy of
     that fact answers for the scene before the dismissal, and the stale
     answer left Tabs refusing its own Escape ladder over an empty ground. */
  const showTabsAfterPanel = useCallback((forScene) => {
    const docks = window.mdSceneDocksWorkspace(forScene || sceneRef.current);
    const sideDestination = !!(L.canvas && !L.overlay && docks);
    window.dispatchEvent(new CustomEvent('mdtabsshow', { detail: { sideDestination } }));
  }, [L.canvas, L.overlay]);
  /* The door out of a panel screen. What the dismissal reveals is READ FROM THE
     SCENE: Tabs comes back when it truly stands beneath (M-2), the day takes
     the panel when nothing does, and a docked workspace window keeps Tabs
     beside it as it always did. The old rule here forbade walking back to
     Tabs at all (the D4 prohibition) because the walk was a guess about
     where the close came from; the scene ended the guessing, so the
     prohibition retires with it. */
  const dismissPanelScreen = useCallback((id) => {
    const next = window.mdSceneDismiss(sceneRef.current, id);
    setScene(next);
    const docked = !!(L.canvas && !L.overlay && window.mdSceneDocksWorkspace(next));
    if (window.mdScenePanelReveal(next, docked) === 'tabs') showTabsAfterPanel(next);
    else if (window.mdSceneTop(next) === null) window.dispatchEvent(new CustomEvent('mdtabsclose'));
    /* else: a surface still owns the screen — it IS the reveal (the phone's
       shaded window standing back up), and Tabs keeps its place beneath it
       in the scene. Closing Tabs here desynced the surface from a scene
       that still held it, and the next reveal unhid nothing. */
  }, [L.canvas, L.overlay, showTabsAfterPanel]);
  /* The Desk chooser joined the scene at N-9, and the hand-rolled "back to
     day" door it used to need died with the move: the walk reveals. */
  /* The door out of a workspace surface. Dismiss reveals whatever truly
     stands: another workspace window beneath keeps the screen (nothing to
     announce), Tabs beneath comes back, and the empty ground closes the
     panel. The old fallbacks dispatched `mdtabsshow` unconditionally — Tabs
     summoned over a direct link's close was baked in. */
  const dismissWorkspaceSurface = useCallback((id) => {
    const next = window.mdSceneDismiss(sceneRef.current, id);
    if (next === sceneRef.current) return;
    if (id === 'workshop' || id === 'project') setProjectWindowFrame(null);
    setScene(next);
    const top = window.mdSceneTop(next);
    if (top === 'tabs') showTabsAfterPanel(next);
    else if (top === null) window.dispatchEvent(new CustomEvent('mdtabsclose'));
    /* else: a surface still owns the screen — it IS the reveal. */
  }, [showTabsAfterPanel]);
  /* One-column layouts cannot show a panel screen and a body-plane workspace
     window at once, and the ledger would paint the window over the screen
     that stands ABOVE it in the scene. The presentation shades the workspace
     — visibility, never teardown — so the state survives to be revealed when
     the screen above it goes (index.html carries the rule). */
  const workspaceShaded = (L.phone || L.overlay) && window.mdSceneWorkspaceShaded(scene);
  useEffect(() => {
    const root = document.documentElement;
    if (workspaceShaded) root.setAttribute('data-md-workspace-shaded', 'true');
    else root.removeAttribute('data-md-workspace-shaded');
    return () => root.removeAttribute('data-md-workspace-shaded');
  }, [workspaceShaded]);
  // transient toast (undo for deletions)
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const showToast = useCallback((msg, undo) => {clearTimeout(toastTimer.current);setToast({ msg, undo });toastTimer.current = setTimeout(() => setToast(null), 4200);}, []);

  /* ── boards ────────────────────────────────────────────────────────────────
     Switching saves the board you are leaving first. The canvas saves on a
     600ms debounce while you draw, so a stroke made in the last half-second is
     still only in the engine when the switch happens — and the engine is about
     to be remounted onto the other board's scene. The elements are captured
     here, synchronously, before the remount; the write itself lands
     asynchronously in IndexedDB, against the OLD board's key, so the two
     boards never meet. */
  const openBoard = useCallback((id) => {
    const api = boardApiRef.current;
    if (api && boardId) window.mdBoardSave(boardId, { elements: api.getSceneElements(), appState: api.getAppState() });
    window.mdBoardSetOpen(id);
    const target = window.mdNavigationTarget?.({ go: 'board', id });
    if (target) window.mdNavigationRecord?.(target);
    refreshBoards();
  }, [boardId, refreshBoards]);
  const newBoard = useCallback(() => { openBoard(window.mdBoardCreate('').id); }, [openBoard]);
  /* Choosing a desk on a phone LANDS you on it (Marwan, 2026-08-28): the desk
     loads and the day is already folded away, rather than the chooser handing
     over to a modal layer you then have to close. The chooser needs no explicit
     dismissal — `desks` and `board` share a group, so standing the desk retires
     it, which is the same swap this has always made. */
  const chooseBoard = useCallback((id) => {
    openBoard(id);
    if (L.phone) showDesk();
  }, [openBoard, showDesk, L.phone]);
  const createChosenBoard = useCallback(() => {
    const board = window.mdBoardCreate('');
    openBoard(board.id);
    if (L.phone) showDesk();
  }, [openBoard, showDesk, L.phone]);
  const newScopedBoard = useCallback((scopeRef) => {
    const ref = window.mdRecordRefParse && window.mdRecordRefParse(scopeRef);
    let name = '';
    if (ref && ref.type === 'project') {
      const project = window.mdProjectById && window.mdProjectById(ref.id);
      name = project ? `${project.name} desk` : '';
    } else if (ref && ref.type === 'pursuit') {
      const pursuit = window.mdPursuitById && window.mdPursuitById(ref.id);
      name = pursuit ? `${pursuit.name} desk` : '';
    }
    const board = window.mdBoardCreate(name, scopeRef);
    openBoard(board.id);
    leaveProjects();
  }, [openBoard, leaveProjects]);
  const openScopedBoard = useCallback((id) => {
    openBoard(id);
    leaveProjects();
    if (L.phone) showDesk();
  }, [openBoard, leaveProjects, showDesk, L.phone]);
  const renameBoard = useCallback((id, name) => { window.mdBoardRename(id, name); refreshBoards(); }, [refreshBoards]);
  // Asked through the app's own confirm sheet, not one of the board's: there is
  // one way this app asks "are you sure", and a drawing is not a special case.
  const deleteBoard = useCallback((b) => {
    setConfirm({
      title: `Delete “${b.name}”?`,
      /* It CAN be undone now — the desk and its drawing go to Trash and wait
         thirty days. The old sentence promised permanence, which is the one
         thing this delete no longer does. */
      message: `The drawing on this desk goes to Trash. You have ${window.MD_TRASH_DAYS || 30} days to change your mind.`,
      /* A refusal is SAID. Leaving the sheet open with no message is a button
         that does nothing, which is exactly the silence this app removed from
         its writes — a full device used to look identical to a successful
         save. mdTrashSayRefusal owns the words so all four doors agree. */
      onConfirm: async () => {
        const result = await window.mdTrashDelete(`board:${b.id}`);
        setConfirm(null);
        if (!result || !result.ok) {
          showToast(window.mdTrashSayRefusal
            ? window.mdTrashSayRefusal(result && result.reason) : 'It could not be moved to Trash.');
          return;
        }
        refreshBoards();
      },
    });
  }, [refreshBoards, showToast]);

  /* ── storage, out loud ─────────────────────────────────────────────────────
     Two things that used to be invisible.

     First: ask the browser to actually keep the data. Without a persistence
     grant, WebKit evicts origin storage after about a week without
     interaction, and every browser evicts under pressure — so a year of
     someone's journal is one eviction from gone. Asking costs one call and no
     prompt in most browsers; not asking costs everything.

     Second: if a write ever fails, SAY SO. Every write in this app used to be
     swallowed by `catch (e) {}`, which meant a full quota looked exactly like
     a successful save — the record stayed on screen and disappeared at reload.
     A toast is not a fix for a full disk, but it turns silent loss into
     something the user can see happening. */
  useEffect(() => {
    mdRequestPersistence();
    /* Move any boards still on the localStorage floor into IndexedDB — the
       ~5 MB they were squatting on is handed back to everything else. Fire
       and forget: reads heal one document at a time anyway (see readDoc in
       md-board.ts), so this is a broom, not a gate. */
    if (window.mdBoardMigrate) window.mdBoardMigrate();
  }, []);
  useEffect(() => mdOnStorageFault((f) => {
    showToast(f.quota
      ? 'Out of storage — that change was NOT saved'
      : 'That change could not be saved');
  }), [showToast]);

  // grid edit-view state: null = closed; else { phase, focalMin, focal:{x,y}, origin }
  const [edit, setEdit] = useState(null);
  const exitFnRef = useRef(null); // GridView hands back a getter for its committed draft
  const gridApiRef = useRef(null); // GridView hands back { upsertDraft, removeDraft } for live writes
  const gridLayerRef = useRef(null); // the overlay wrapper (contains the grid + its blocks)
  const gridBgRef = useRef(null); // opaque backdrop that covers the list during edit
  const openFindRecordRef = useRef(null); // the router arrives before the compose-sheet door is declared

  // which day is being viewed (offset in days from today; 0 = today)
  const [viewOffset, setViewOffset] = useState(() => { const v = mdRead('md:viewOffset', 0); return Number.isFinite(v) ? v : 0; });
  useEffect(() => { mdWrite('md:viewOffset', viewOffset); }, [viewOffset]);
  const isToday = viewOffset === 0;
  // jump the schedule back to the top whenever the viewed day changes
  useEffect(() => {
    if (L.phone) window.scrollTo({ top: 0, behavior: 'auto' });
    else if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [viewOffset, L.phone]);
  const selectDay = (off) => setViewOffset(off);
  /* goFind is a DISPATCHER now. What a destination opens and what it
     displaces is decided in one pure table — mdScenePlaceActions
     (md-scene.ts) — over the live scene; the branches left here are only the
     side effects a destination carries with it (a view activation, a day
     offset, an announcement). The old body composed teardowns by hand — a
     step-aside and two guarded closers reading a live-mirror ref; every
     wrong turn Marwan reported traced to one of them. */
  const goFind = useCallback((place, source = null) => {
    if (!place) return;
    const target = source && source.target ? source.target
      : window.mdNavigationTarget && window.mdNavigationTarget(place, source && source.key);
    if (target && window.mdNavigationRecord) {
      const fajr = mdPrayersFor(0).find((p) => p.id === 'fajr');
      const boundary = window.mdNavigationCycleStart(Date.now(), fajr && fajr.min);
      window.mdNavigationRecord(target, Date.now(), boundary);
    }
    const shedForLegacy = (s) => ['settings', 'crafts', 'catalogue', 'database']
      .reduce((acc, id) => window.mdSceneDismiss(acc, id), s);
    /* Surfaces the scene does not seat yet keep their doors: the record
       sheet (N-9) and the Desk layers (`desksOpen` and `deskShown`). Their
       displacement is spelled out here until then. */
    if (place.go === 'record') {
      setScene(shedForLegacy);
      openFindRecordRef.current?.(place.ref);
      return;
    }
    if (place.go === 'boards') {
      window.dispatchEvent(new CustomEvent('mdtabshide', { detail: { coverDay: true } }));
      setDesksOpen(true);
      return;
    }
    if (place.go === 'board') {
      setScene(shedForLegacy);
      openScopedBoard(place.id);
      return;
    }
    if (place.go === 'day') {
      const off = window.mdFindDayOffset(place.day, mdDayKey(0));
      if (off === null) return;
      setScene(window.mdSceneEmpty());
      setViewOffset(off);
      return;
    }
    const out = window.mdScenePlaceActions(place, sceneRef.current, L, source?.source);
    if (place.go === 'settings') setSettingsView(place.view || 'root');
    else if (place.go === 'crafts') setCraftsTarget(place.id || null);
    else if (place.go === 'databases') window.dispatchEvent(new CustomEvent('mddatabasescatalogueopen'));
    else if (place.go === 'project') setProjectWindowFrame(null);
    else if (place.go === 'view') {
      if (place.scope === 'account' && window.mdWorkshopSetActive) window.mdWorkshopSetActive(place.id);
      if (place.scope === 'project') {
        if (window.mdWorklistSetActive) window.mdWorklistSetActive(place.projectId, place.id);
        setProjectWindowFrame(null);
      }
      if (place.scope === 'database' && window.mdDatabaseViewActivate)
        window.mdDatabaseViewActivate(place.databaseId, place.id);
    }
    setScene(out.scene);
  }, [openScopedBoard, L]);
  const swipeWeek = (dir) => setViewOffset((o) => {const wi = Math.floor((o + 6) / 7) + dir;return dir > 0 ? wi * 7 - 6 : wi * 7;});
  // toggle a card's completed state on the currently-viewed day
  const toggleCompleted = useCallback((uid, sourceDay) => {
    // Date, not offset. R4 moved every store to YYYY-MM-DD keys; this one was
    // missed, so tapping Done wrote "0|uid" — a key that drifts to a different
    // day at midnight AND that the server rejects, which at the time cost the
    // whole push rather than just that document.
    const day = sourceDay || mdDayKey(viewOffset);
    const key = day + '|' + uid;
    /* LIFTING A TICK LIFTS THE WORD WITH IT (WR-3). A record wearing a word
       from its workflow's Completed category would otherwise stay in that
       column while the morning says it is not done — the exact disagreement
       this phase exists to make impossible. Ticking needs nothing written: the
       read derives the workflow's finish from the tick alone, which is what
       lets unticking put the card back where it came from.

       A uid that names no record is a prayer or a block, and the day's map is
       the whole of what it has; the door says so rather than refusing. */
    const held = window.mdStoreRead ? window.mdStoreRead('completed', day) : null;
    if (held && held[uid] && window.mdRecordTryTick) window.mdRecordTryTick(uid, false);
    setCompleted((prev) => {const n = { ...prev };if (n[key]) delete n[key];else n[key] = true;return n;});
  }, [viewOffset]);

  /* ── a record's status, wherever the record lives ─────────────────────────
     DONE ON THE BOARD AND TICKED IN THE DAY ARE THE SAME ACT, and WR-3 made
     that one fact rather than two that agree: the word is the truth, the tick
     is how the day says it, and md-project.ts's door writes whichever of the
     two a move actually needs.

     THE RULE IS NOT HERE, and neither is the writing any more. This asks the
     door and then RE-READS the state it holds, rather than patching the map
     itself — a second copy of the decision is how a board and a day start
     disagreeing, and a second writer racing the effect below is how a change
     lands, shows for a moment, and quietly un-happens. */
  const setRecordStatus = useCallback((row, want) => {
    if (!row || typeof row.uid !== 'string' || !row.uid) return { ok: false, reason: 'missing' };
    /* `scheduled`, and null when nobody chose one. The door falls back to the
       day the record was WRITTEN (WR-1), which is the day a captured thought's
       tick belongs under — but only the caller knows the chosen one, because a
       day record's day is the document it lives in and never a field on it. */
    const day = row.scheduled || null;
    /* ONE DOOR, BOTH WRITES (WR-3). The word goes to `md:recordStatus` and the
       tick to the day's own completions document, and md-project.ts decides
       which of the two a move needs. The state below is re-read from storage
       rather than patched here, because a second copy of that decision is how
       a board and a day start disagreeing. */
    if (!window.mdRecordTrySetStatus) return { ok: false, reason: 'missing' };
    const done = window.mdRecordTrySetStatus(row.record || row, want, day);
    if (!done.ok) return done;
    setCompleted(window.mdStoreCompletedFlat());
    setProjectRecords(window.mdProjectRecordIndex());
    return done;
  }, []);

  // ════════ Add / Edit Record (compose sheet) ════════
  // ── place / move a record on a specific day (honours the When picker's chosen offset) ──
  //  timed → an event in that day's stored event list (empty until the user adds one);
  //  all-day → that day's allDayRecords list. Both upsert by uid.
  const placeOnDay = useCallback((offset, rec, timed) => {
    const key = mdDayKey(offset);
    // `offset` is the When picker's answer — meaningful only while it is being
    // chosen. The store key is the record's day from here on, so the field is
    // dropped rather than persisted: a record re-opened after midnight would
    // otherwise carry a stale 0 and re-home itself onto the new today.
    rec = { ...rec }; delete rec.offset;
    if (timed) {
      setDayEvents((prev) => {
        // No fixture defaults to seed from any more — an untouched day is empty.
        const ex = prev[key] || [];
        return { ...prev, [key]: [...ex.filter((e) => e.uid !== rec.uid), rec] };
      });
    } else {
      setAllDayRecords((prev) => {
        const list = (prev[key] || []).slice();const i = list.findIndex((r) => r.uid === rec.uid);
        if (i >= 0) list[i] = rec;else list.push(rec);
        return { ...prev, [key]: list };
      });
    }
  }, []);
  // remove a record from wherever it currently lives (any day, either store) — so a
  // date change re-homes it cleanly instead of leaving a ghost on the old day.
  const removeEverywhere = useCallback((uid) => {
    setDayEvents((prev) => {const n = {};let touched = false;for (const k in prev) {const f = prev[k].filter((e) => e.uid !== uid);if (f.length !== prev[k].length) touched = true;n[k] = f;}return touched ? n : prev;});
    setAllDayRecords((prev) => {const n = {};let touched = false;for (const k in prev) {const f = prev[k].filter((r) => r.uid !== uid);if (f.length !== prev[k].length) touched = true;n[k] = f;}return touched ? n : prev;});
  }, []);

  /* ── the record, as it is written ────────────────────────────────────────────
     The compose sheet calls this on mount and then on every keystroke, so this
     is where a record comes into existence: opening one puts its card in the day
     immediately, and typing rewrites that card's title as the first line grows.
     There is no separate save.

     Two things it deliberately does NOT do on every call. It does not sweep the
     other days for the uid unless the record actually changed day (the When
     picker is the only thing that moves one), because that rebuilds every day
     the user has ever written. And it returns `completed` unchanged when the
     done-state already matches, so a keystroke does not re-render and re-persist
     a map it did not touch. */
  const commitLive = useCallback((rec, done) => {
    /* ── a backlog record commits to its own store (OD-3) ──────────────────
       The sheet is day-blind here: it writes what it holds and md-app decides
       where that lives. A backlog record lives in md:projectRecords, dateless,
       through the module's one announcing door — and it is NOT committed
       blank: the day needs its placeholder card, a board column does not, so
       the row first exists at the first keystroke (D2). The column's status
       is applied once, right after that first write, because a status is a
       fact about the record and the record has to exist to wear it. */
    if (compose && compose.backlog) {
      const blank = !rec.name && !rec.body;
      const existing = window.mdProjectRecordById && window.mdProjectRecordById(rec.uid);
      if (!existing && blank) {
        liveRef.current = { rec, backlog: true, blank: true };
        return;
      }
      window.mdProjectRecordCommit && window.mdProjectRecordCommit({
        uid: rec.uid, pid: rec.pid || compose.backlog.pid, name: rec.name, body: rec.body,
        links: rec.links, created: rec.created, origin: rec.origin });
      if (!existing && compose.backlog.status && window.mdProjectRecordSetStatus) {
        window.mdProjectRecordSetStatus(rec.uid, compose.backlog.status);
      }
      /* THE ROUTE LEARNS THE RECORD HERE, not at the "+" (ported from main).
         main recorded a navigation target the moment a backlog record was
         minted, which under P-5 is a moment that no longer exists — there is
         nothing to point a URL at until the beginning is confirmed. This is
         where that first write happens, so this is where the route hears
         about it, and it is asked once: `existing` is the same test the
         status seed uses. */
      if (!existing) {
        const refKey = window.mdRecordRefKey?.(window.mdRecordRefFor?.(rec, null));
        const routed = refKey && window.mdNavigationTarget?.({ go: 'record', ref: refKey });
        if (routed) window.mdNavigationRecord?.(routed);
      }
      liveRef.current = { rec, backlog: true, blank };
      return;
    }
    const target = rec.offset != null ? rec.offset
      : compose && Number.isFinite(compose.dayOffset) ? compose.dayOffset : viewOffset;
    const key = mdDayKey(target);
    const prev = liveRef.current;
    const moved = !prev || prev.rec.uid !== rec.uid || prev.dayKey !== key;
    const fromGrid = !!(compose && compose.fromGrid);
    const block = { ...rec, kind: 'event', record: true, id: rec.domain || undefined, t: minToHHMM(rec.min) };
    const gridApi = gridApiRef.current;
    if (fromGrid && gridApi && gridApi.acceptsOffset(target)) {
      // the grid owns one Fajr cycle, which can cross into the next civil date
      gridApi.upsertDraft(block, target);
    } else {
      if (fromGrid) gridApiRef.current && gridApiRef.current.removeDraft(rec.uid);
      if (moved) removeEverywhere(rec.uid);
      placeOnDay(target, block, true);
    }
    const blank = !rec.name && !rec.body;
    if (!blank && (!prev || prev.rec.uid !== rec.uid || prev.blank)) {
      const refKey = window.mdRecordRefKey?.(window.mdRecordRefFor?.(block, null));
      const navTarget = refKey && window.mdNavigationTarget?.({ go: 'record', ref: refKey });
      if (navTarget) window.mdNavigationRecord?.(navTarget);
    }
    liveRef.current = { rec: block, dayKey: key, offset: target, fromGrid, blank };
    setCompleted((p) => {
      const k = key + '|' + rec.uid;
      if (!!p[k] === !!done) return p;
      const n = { ...p };if (done) n[k] = true;else delete n[k];return n;
    });
  }, [viewOffset, compose, placeOnDay, removeEverywhere]);

  /* A record left with nothing written in it is not a record. This is what makes
     the "+"-then-think-better-of-it path leave no trace, and it runs whether
     the sheet was closed or another record was opened over the top of it. */
  const dropIfBlank = useCallback(() => {
    const live = liveRef.current;
    liveRef.current = null;
    /* First the block that was drawn rather than written (P-7). It is asked
       before the blank test because a NEVER-created record leaves no `live` at
       all now, so the test below would return and the block would stay. */
    const drawn = window.mdComposeGridDrop
      && window.mdComposeGridDrop(pendingGridRef.current, live);
    pendingGridRef.current = null;
    if (drawn) gridApiRef.current && gridApiRef.current.removeDraft(drawn);
    if (!live || !live.blank) return;
    if (live.backlog) {
      /* A blank backlog record was usually never written (commitLive skips the
         blank commit), so this remove is refused and the store never moves —
         byte-identical, as D2 promises. It only finds a row when the writing
         was erased back to nothing, and then removing it is the same promise. */
      window.mdProjectRecordRemove && window.mdProjectRecordRemove(live.rec.uid);
      return;
    }
    if (live.fromGrid) gridApiRef.current && gridApiRef.current.removeDraft(live.rec.uid);
    removeEverywhere(live.rec.uid);
  }, [removeEverywhere]);

  /* Open a fresh record. Everything written into it is already saved by the time
     it closes, so opening another record over this one is just this, again.

     `fresh` is what raises the keyboard, and it is set HERE rather than inferred
     from `initial` because a record drawn on the grid arrives with a record and is
     still a new record. A new record has nothing in it but a caret, so the keyboard
     is the next thing wanted; an existing record is something you came to READ as
     often as to edit, and a keyboard covering half of it uninvited is the app
     deciding that for you. Tap the text and it comes up, as it always did. */
  const openCompose = useCallback(() => {
    // synchronously, inside the tap: prime the iOS keyboard so it is already
    // rising while the record rises — the editor takes the focus over on mount
    if (window.mdPrimeKeyboard) window.mdPrimeKeyboard();
    dropIfBlank();
    setCompose({ seq: ++composeSeq.current, fresh: true });
  }, [dropIfBlank]);

  /* "New record" from inside a project. The sheet opens already joined, so the
     record belongs to the project from its first keystroke rather than needing to
     be filed afterwards — filing later is the step everybody skips. Projects
     closes first: it is a full takeover of the panel, and compose is another. */
  /* Opening a PROJECT opens its main record — the same sheet a day record opens
     in, because a project is a record. It is not `fresh`: the template is
     already in it, and autofocus would drop the caret above the first heading.
     Projects closes first, the way it does for a new record in a project. */
  const openProjectRecord = useCallback((id) => {
    if (window.mdPrimeKeyboard) window.mdPrimeKeyboard();
    leaveProjects();
    dropIfBlank();
    setCompose({ seq: ++composeSeq.current, project: id });
  }, [dropIfBlank, leaveProjects]);

  const composeForProject = useCallback((pid) => {
    if (window.mdPrimeKeyboard) window.mdPrimeKeyboard();
    leaveProjects();
    dropIfBlank();
    setCompose({ seq: ++composeSeq.current, fresh: true, pid });
  }, [dropIfBlank, leaveProjects]);
  /* A record born from a window is a BACKLOG record of that project (D2):
     dateless, joined from the first keystroke, the column's status carried.
     Nothing is minted here — the sheet opens fresh and commitLive writes the
     row when there is a first keystroke to write, so thinking better of it
     leaves the store byte-identical. */
  /* The seed the sheet is currently standing on, which the column opened and a
     pick in the band can replace (CS-10). It rides `compose` rather than a ref
     because the band DRAWS it: a ref would update the write and leave the chip
     showing the word you just replaced. */
  const setBacklogStatusSeed = useCallback((status) => {
    setCompose((now) => (now && now.backlog
      ? { ...now, backlog: { ...now.backlog, status: status || null } } : now));
  }, []);
  const composeProjectBacklog = useCallback((pid, status = null) => {
    if (window.mdPrimeKeyboard) window.mdPrimeKeyboard();
    dropIfBlank();
    setCompose({ seq: ++composeSeq.current, fresh: true,
      backlog: { pid, status: status || null } });
  }, [dropIfBlank]);
  /* An EXISTING backlog record opens in the same sheet — the inline page it
     used to open in retired with OD-3. Not `fresh`: this is a record you came
     to read as often as to edit. */
  const editProjectBacklogRecord = useCallback((uid) => {
    const record = window.mdProjectRecordById && window.mdProjectRecordById(uid);
    if (!record) return;
    dropIfBlank();
    setCompose({ seq: ++composeSeq.current, initial: record,
      backlog: { pid: record.pid } });
  }, [dropIfBlank]);
  /* `day` is optional and comes from ONE gesture: dragging across a record's
     empty row on the Project window's timeline. A drag names a day out loud, so
     obeying today instead would be the app overruling a gesture that could not
     have been clearer. Every other caller names no day and means today.

     A day this app cannot place — outside the range mdDayKey covers — falls
     back to today rather than refusing: the record is being scheduled either
     way, and the sheet opens on the day it landed on for the person to see. */
  const scheduleProjectBacklogRecord = useCallback((uid, day) => {
    const record = window.mdProjectRecordById && window.mdProjectRecordById(uid);
    if (!record) return;
    const asked = day ? mdDayOffsetOf(day) : null;
    const offset = asked == null ? 0 : asked;
    const min = Math.round((window.mdNowMin ? window.mdNowMin() : 0) / 5) * 5;
    const scheduled = { ...record, unscheduled: undefined, at: undefined, allDay: false,
      min, dur: 30, t: minToHHMM(min) };
    window.mdProjectRecordRemove(uid);   // announces; the OD-1 subscription re-reads
    /* THE TICK TRAVELS WITH IT (WR-3). Every record has a day — the one it was
       written on when nobody chose one — so a finished thought sitting in the
       backlog is ticked under THAT day. Re-keying it to the chosen day is one
       move, here, or the card would drop out of Completed the moment somebody
       scheduled it and leave a completion behind in a day it is no longer in. */
    const key = mdDayKey(offset);
    setCompleted((prev) => window.mdRecordStatusCarryTick(
      prev, window.mdRecordOriginDay(record), key, uid));
    leaveProjects();
    dropIfBlank();
    setCompose({ seq: ++composeSeq.current, initial: scheduled, dayOffset: offset,
      sourceDay: key });
  }, [dropIfBlank, leaveProjects]);

  // ✓ or the minus: leave the record. Nothing to commit — that already happened.
  const closeCompose = useCallback(() => {dropIfBlank();setCompose(null);}, [dropIfBlank]);

  /* ── the App-owned sheets ride the scene (N-9) ───────────────────────────
     Openness lives on the stack; the payload stays the App's own fact. The
     scene follows the payload when a door here moves it, and the payload
     follows the scene when a walk or a rule retires the entry — each
     direction converges on TRANSITIONS, so the pair cannot chase each other.
     The record sheet's entry carries the payload's seq, so a record-to-record
     walk inside the sheet is a params change the journal can see (a replace —
     and the entry a wiki link pushed is adopted, exactly like any other
     navigation). */
  const composeSeq2 = compose ? compose.seq || 0 : 0;
  useEffect(() => {
    setScene((s) => (compose ? window.mdSceneOpen(s, 'compose', { seq: composeSeq2 })
      : window.mdSceneDismiss(s, 'compose')));
  }, [!!compose && composeSeq2]);
  const composeStood = useRef(false);
  useEffect(() => {
    const stands = window.mdSceneIsOpen(scene, 'compose');
    if (!stands && composeStood.current && compose) { dropIfBlank(); setCompose(null); }
    composeStood.current = stands;
  }, [scene]);
  useEffect(() => {
    setScene((s) => (reschedule ? window.mdSceneOpen(s, 'reschedule')
      : window.mdSceneDismiss(s, 'reschedule')));
  }, [!!reschedule]);
  const rescheduleStood = useRef(false);
  useEffect(() => {
    const stands = window.mdSceneIsOpen(scene, 'reschedule');
    if (!stands && rescheduleStood.current && reschedule) setReschedule(null);
    rescheduleStood.current = stands;
  }, [scene]);
  /* The Desk layers: the chooser, and the fold that uncovers the desk. Each is
     a scene entry, and a chooser-to-desk step is the swap the journal already
     knows (a push the browser can walk back through) — which is where the
     phone's Back-returns-to-the-day comes from, free.

     WRITTEN ONLY WHILE `L.phone`. On a wider layout the fold is a persisted
     setting over ground that is always there, not a place you stepped onto, and
     seating it would leave the journal an entry for a surface nobody can see.
     A phone rotated into a tablet therefore dismisses the entry cleanly and
     hands the fold back to the setting; rotating back re-pushes it, because
     `deskShown` was never cleared. */
  const desksUp = desksOpen;
  /* EXCLUSIVE, and the scene is why: `desks` and `board` share `group:'panel'`,
     so whichever is written last retires the other. Two independent booleans
     mirrored in one pass fought over that — the chooser opened, the desk's own
     line put its entry straight back, and the watcher below then read a scene
     with no chooser in it and turned `desksOpen` off again. A chooser standing
     means the desk is covered; that is one sentence, and this is where it is
     said. */
  const boardUp = L.phone && deskShown && !desksOpen;
  useEffect(() => {
    setScene((s) => {
      const mid = desksUp ? window.mdSceneOpen(s, 'desks') : window.mdSceneDismiss(s, 'desks');
      return boardUp ? window.mdSceneOpen(mid, 'board') : window.mdSceneDismiss(mid, 'board');
    });
  }, [desksUp, boardUp]);
  const deskStood = useRef(false);
  useEffect(() => {
    const chooser = window.mdSceneIsOpen(scene, 'desks');
    const canvas = window.mdSceneIsOpen(scene, 'board');
    const stood = deskStood.current; deskStood.current = chooser || canvas;
    if (!stood) return;
    /* A walk that retired the fold puts the day back on screen; the desk stays
       loaded underneath it, because ground is not what was dismissed. */
    if (deskShown && !canvas) setDeskShown(false);
    if (desksOpen && !chooser) setDesksOpen(false);
  }, [scene]);
  /* THE EMPTY SIDE PANEL (Marwan, 2026-08-24). The desk layers close through
     the two facts above, the arbiter and the journal — every door lands in the
     scene above, and none of them spoke to Tabs. So Tabs stayed hidden
     beneath a walk that had finished, its opaque guard black over the day
     the layout was presenting. The shroud is a fact of the scene
     (mdSceneDeskShroud), restated to Tabs on every TRANSITION of it — on
     transition, because `mdtabshide` snapshots phone scroll and forgetting
     that would let a restatement write the destination's scroll over Tabs'
     own (the handler now also guards itself). goFind still announces the
     chooser eagerly so the guard is up before the chooser paints; this
     watcher makes that announcement impossible to strand. */
  const deskShroud = useRef('none');
  useEffect(() => {
    const shroud = window.mdSceneDeskShroud(scene, L);
    const was = deskShroud.current;
    if (shroud === was) return;
    deskShroud.current = shroud;
    if (shroud !== 'none') {
      window.dispatchEvent(new CustomEvent('mdtabshide',
        { detail: { coverDay: shroud === 'covered' } }));
    } else if (was !== 'none') {
      /* The desk walk finished: what the panel shows is ASKED OF THE SCENE
         (mdScenePanelReveal), not read off the top — Tabs comes back when
         it is the top AND when it is the docked side panel beneath a
         standing window, which was the second sighting of the empty panel:
         Desks closed over [tabs, workshop] left Tabs hidden beside the
         window because it was not the top of anything. */
      const docked = !!(L.canvas && !L.overlay && window.mdSceneDocksWorkspace(scene));
      if (window.mdScenePanelReveal(scene, docked) === 'tabs') showTabsAfterPanel(scene);
    }
  }, [scene, L, showTabsAfterPanel]);
  /* Escape joins the dismissal on both Desk layers. Plain layers, no surface id: the
     App seats `desks` and `board` in the scene itself, and a ride would seat
     them twice. Escape on an uncovered desk brings the DAY back rather than the
     chooser: the desk is ground now, and the only thing standing over it that
     Escape could take away is the fold. (Back differs for a desk reached
     through the chooser — the journal remembers that swap and steps into it.
     Both are true; they are just not the same walk.) */
  useEscape(() => setDesksOpen(false), desksUp && !boardUp);
  useEscape(() => setDeskShown(false), boardUp);

  // deliberate delete, from the sheet's More → Delete. Always undoable, because
  // by the time you can ask for this the record is real and already stored.
  const discardCompose = useCallback(() => {
    const live = liveRef.current;
    liveRef.current = null;
    pendingGridRef.current = null;   // this path removes the block itself, below
    setCompose(null);
    if (!live) return;
    const rec = live.rec;
    if (live.fromGrid) {
      gridApiRef.current && gridApiRef.current.removeDraft(rec.uid);
      if (!live.blank) showToast('Record removed', () => {gridApiRef.current && gridApiRef.current.upsertDraft(rec, live.offset);setToast(null);});
      return;
    }
    removeEverywhere(rec.uid);
    if (!live.blank) showToast('Record deleted', () => {placeOnDay(live.offset, rec, true);setToast(null);});
  }, [showToast, removeEverywhere, placeOnDay]);

  // Tapping a card in the day opens it — over whatever record is already open.
  // No keyboard: this record already has words in it, and the first thing asked of
  // it is usually to be read. See openCompose for the whole of that argument.
  const editRecord = useCallback((rec) => {
    const source = mdTimelineSource ? mdTimelineSource(rec) : { item: rec, offset: null, dayKey: null };
    const sourceDay = source.dayKey || mdDayKey(source.offset != null ? source.offset : viewOffset);
    const refKey = window.mdRecordRefKey?.(window.mdRecordRefFor?.(source.item, null));
    const target = refKey && window.mdNavigationTarget?.({ go: 'record', ref: refKey });
    if (target) window.mdNavigationRecord?.(target);
    dropIfBlank();
    setCompose({ seq: ++composeSeq.current, initial: source.item,
      dayOffset: source.offset != null ? source.offset : viewOffset,
      sourceDay });
  }, [dropIfBlank, viewOffset]);

  /* ── what the board can see, and what happens when a pin is tapped ────────
     The board resolves refs against the days this app already holds in memory
     rather than re-reading storage per element per frame — see md-resolve.ts.
     `todayKey` is what the picker offers as "Today".

     BOTH LISTS, not just the timed one. A day keeps its records in two places
     and the shelf was missing here, so an all-day record could not be resolved
     at all — the pin drew as "no longer here" while the record sat in the panel
     beside it. Nothing reached that path until media started landing on the
     shelf, which is what makes it a bug rather than a gap. */
  const boardCtx = useMemo(() => ({ events: dayEvents, allDay: allDayRecords, projectRecords,
    todayKey: mdDayKey(0) }), [dayEvents, allDayRecords, projectRecords]);

  /* ── serving a manifestation's reads ──────────────────────────────────────
     Every answer here comes from a function that already exists. The bridge
     adds no new way to reach anything — it is a door onto the ones the app
     already uses, which is what keeps "what a manifestation can see" a question
     with one answer rather than a second data layer to keep in step.

     It throws a CODE, not a message. md-bridge.ts owns the sentences; a string
     invented here would be one no interface has words for. */
  const manifestRead = useCallback((op, arg) => {
    const a = arg && typeof arg === 'object' ? arg : {};
    const day = typeof a.day === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(a.day) ? a.day : null;

    if (op === 'app.today') {
      /* Rounded. mdNowMin carries seconds as a fraction, which is right for a
         clock hand sweeping and wrong for an answer somebody prints — it read
         "minute 56.266666666666" in the first manifestation that asked. */
      const now = window.mdNowMin ? window.mdNowMin() : 0;
      return { day: mdDayKey(0), minute: Math.floor(now) };
    }

    if (op === 'records.day') {
      if (!day) throw 'bad_arg';
      const out = [];
      for (const list of [dayEvents[day], allDayRecords[day]]) {
        if (!Array.isArray(list)) continue;
        for (const rec of list) {
          const ref = window.mdRecordRefFor(rec, day);
          const found = ref && window.mdRecordResolve(ref, boardCtx);
          if (!found) continue;
          /* Two fields added HERE rather than inside mdBridgeRecordOut, and
             named one at a time rather than spread — the leak that function
             exists to prevent is a spread, not a longer list. Neither is new
             information: a manifestation allowed to read a record's title and
             text is not learning anything further from when it is and whether it
             is done. Both are what makes a table of records a table rather than
             a list of names.

             `min` comes off the raw record because a resolved one does not carry
             it: md-resolve answers what a record IS, not where it sits in a day. */
          out.push({
            ...window.mdBridgeRecordOut(found),
            min: Number.isFinite(rec.min) ? rec.min : null,
            done: !!completed[`${day}|${rec.uid}`],
          });
        }
      }
      return { day, records: out };
    }

    if (op === 'records.project') {
      const parsed = typeof a.project === 'string' && window.mdRecordRefParse
        ? window.mdRecordRefParse(a.project) : null;
      const projectId = parsed && parsed.type === 'project' ? parsed.id
        : (typeof a.project === 'string' && window.mdProjectById && window.mdProjectById(a.project) ? a.project : null);
      if (!projectId || !window.mdProjectById || !window.mdProjectById(projectId)) throw 'not_found';
      const out = [];
      for (const dayKey of new Set([...Object.keys(dayEvents), ...Object.keys(allDayRecords)])) {
        for (const list of [dayEvents[dayKey], allDayRecords[dayKey]]) {
          if (!Array.isArray(list)) continue;
          for (const rec of list) {
            if (!rec || rec.pid !== projectId) continue;
            const ref = window.mdRecordRefFor(rec, dayKey);
            const found = ref && window.mdRecordResolve(ref, boardCtx);
            if (!found) continue;
            out.push({ ...window.mdBridgeRecordOut(found),
              min: Number.isFinite(rec.min) ? rec.min : null,
              done: !!completed[`${dayKey}|${rec.uid}`] });
          }
        }
      }
      for (const rec of projectRecords) {
        if (!rec || rec.pid !== projectId) continue;
        const found = window.mdRecordResolve({ type: window.mdRecordType(rec), id: rec.uid }, boardCtx);
        if (found) out.push({ ...window.mdBridgeRecordOut(found), min: null, done: false });
      }
      out.sort((left, right) => (left.day || '').localeCompare(right.day || '') ||
        ((left.min == null ? -1 : left.min) - (right.min == null ? -1 : right.min)));
      return { project: `project:${projectId}`, records: out };
    }

    if (op === 'records.get') {
      const found = typeof a.ref === 'string' ? window.mdRecordResolve(a.ref, boardCtx) : null;
      if (!found) throw 'not_found';
      return window.mdBridgeRecordOut(found);
    }

    if (op === 'projects.list') {
      const list = window.mdProjectIndex ? window.mdProjectIndex() : [];
      /* Field by field, for md-bridge.ts's reason: a project grew a `target`
         once and would have been published by a spread the day it did. */
      return { projects: (Array.isArray(list) ? list : []).map((p) => ({
        ref: `project:${p.id}`, name: p.name, domain: p.domainId || null,
        pursuit: p.pursuitId ? `pursuit:${p.pursuitId}` : null })) };
    }

    if (op === 'pursuits.list') {
      const list = window.mdPursuitIndex ? window.mdPursuitIndex() : [];
      return { pursuits: (Array.isArray(list) ? list : []).map((pursuit) => ({
        ref: `pursuit:${pursuit.id}`, name: pursuit.name, domain: pursuit.domainId || null })) };
    }

    if (op === 'prayers.day') {
      /* Offset rather than date, because that is what the prayer engine takes;
         a day it cannot reach is `bad_arg` rather than a silently empty list. */
      const offset = Number.isFinite(a.offset) ? Math.trunc(a.offset) : 0;
      if (Math.abs(offset) > 366) throw 'bad_arg';
      const list = mdPrayersFor(offset) || [];
      return { offset, prayers: list.map((p) => ({ id: p.id, name: p.name, min: p.min })) };
    }

    if (op === 'records.open') {
      if (typeof a.ref !== 'string') throw 'bad_arg';
      const found = window.mdRecordResolve(a.ref, boardCtx);
      if (!found) throw 'not_found';
      const open = manifestActionsRef.current.openRecord;
      if (!open || !open(found)) throw 'not_found';
      window.dispatchEvent(new CustomEvent('md:craft-native-open'));
      return { opened: true };
    }

    if (op === 'day.open') {
      if (!day) throw 'bad_arg';
      const offset = mdDayOffsetOf(day);
      if (offset === null) throw 'bad_arg';
      setViewOffset(offset);
      window.dispatchEvent(new CustomEvent('md:craft-native-open'));
      return { opened: day };
    }

    if (op === 'projects.open') {
      setScene((s) => window.mdSceneWorkshopRoot(s));
      window.dispatchEvent(new CustomEvent('md:craft-native-open'));
      return { opened: true };
    }

    throw 'unknown_op';
  }, [dayEvents, allDayRecords, boardCtx, completed]);

  /* ── serving a manifestation's writes ─────────────────────────────────────
     EVERY ONE OF THESE GOES THROUGH placeOnDay, WHICH IS REACT STATE. Writing
     to md-storage.js directly would be overwritten by the next flush, and worse,
     would skip the stamping that decides which version of a record wins a merge
     on somebody's other device. There is one way to change a day and this uses
     it, exactly as the compose sheet does.

     What a manifestation may write is deliberately narrower than what it may
     read. It can add a record, change one it can already see, and tick one off.
     It cannot delete — the ⋯ menu's own delete offers an Undo that restores both
     the record and its card, a frame cannot offer that, and a delete that syncs
     is gone from every device at once. It cannot move a record between days
     either: rescheduling has rules that a JSON call would flatten. */
  const manifestWrite = useCallback((op, arg, actor) => {
    const a = arg && typeof arg === 'object' ? arg : {};
    const text = typeof a.text === 'string' ? a.text : '';
    const title = typeof a.title === 'string' ? a.title.trim() : '';

    if (op === 'records.create') {
      if (!title && !text) throw 'bad_arg';
      const day = typeof a.day === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(a.day) ? a.day : mdDayKey(0);
      const offset = mdDayOffsetOf(day);
      if (offset === null) throw 'bad_arg';
      const uid = window.mdRecordUid();
      const allDay = a.allDay !== false;
      /* All-day unless a time was asked for. A dashboard that adds "read this
         later" is not making an appointment, and putting it on the grid at
         whatever minute happened to be current would be the app inventing a
         commitment nobody made. */
      const min = Number.isFinite(a.min) ? Math.max(0, Math.min(1435, Math.trunc(a.min))) : null;
      const rec = { uid, kind: 'event', record: true, allDay: allDay || min === null,
        name: title || window.mdRecordParts(text).name, body: title ? text : window.mdRecordParts(text).body,
        created: Date.now(), origin: window.mdRecordOriginStamp() };
      if (min !== null && !allDay) { rec.allDay = false; rec.min = min; rec.t = minToHHMM(min); rec.dur = 30; }
      placeOnDay(offset, rec, !rec.allDay);
      const ref = `${window.MD_RECORD_DEFAULT_TYPE}:${day}:${uid}`;
      if (actor && window.mdCraftActivityAdd) window.mdCraftActivityAdd(actor.instance,
        { op, ref, summary: `Created “${rec.name || 'Untitled'}”` });
      showToast(`${actor && actor.name ? actor.name : 'Craft'} created a record`, () => {
        removeEverywhere(uid); setToast(null);
      });
      return { ref };
    }

    if (op === 'records.update' || op === 'records.complete') {
      const ref = typeof a.ref === 'string' ? window.mdRecordRefParse(a.ref) : null;
      const found = ref && window.mdRecordResolve(ref, boardCtx);
      /* A day record only. A project or a domain has its own home and its own
         writer, and a fourth one editing them is a merge question rather than an
         API question. */
      if (!found || !found.day) throw 'not_found';
      const offset = mdDayOffsetOf(found.day);
      if (offset === null) throw 'not_found';

      if (op === 'records.complete') {
        if (typeof a.done !== 'boolean') throw 'bad_arg';
        /* Present means done; the key is DELETED rather than set false. That is
           this map's existing convention (md-app's own toggle does the same) and
           writing `false` into it would be a value nothing else reads. */
        setCompleted((prev) => {
          const key = `${found.day}|${ref.id}`;
          const next = { ...prev };
          if (a.done) next[key] = true; else delete next[key];
          return next;
        });
        const previousDone = !!completed[`${found.day}|${ref.id}`];
        if (actor && window.mdCraftActivityAdd) window.mdCraftActivityAdd(actor.instance,
          { op, ref: a.ref, summary: a.done ? `Completed “${found.title}”` : `Reopened “${found.title}”` });
        showToast(`${actor && actor.name ? actor.name : 'Craft'} changed a record`, () => {
          setCompleted((prev) => { const key = `${found.day}|${ref.id}`; const next = { ...prev };
            if (previousDone) next[key] = true; else delete next[key]; return next; });
          setToast(null);
        });
        return { ref: a.ref, done: a.done };
      }

      const list = (dayEvents[found.day] || []).concat(allDayRecords[found.day] || []);
      const old = list.find((r) => r && r.uid === ref.id);
      if (!old) throw 'not_found';
      const next = { ...old };
      if (title) next.name = title;
      if (typeof a.text === 'string') next.body = text;
      placeOnDay(offset, next, !next.allDay);
      if (actor && window.mdCraftActivityAdd) window.mdCraftActivityAdd(actor.instance,
        { op, ref: a.ref, summary: `Updated “${next.name || found.title}”` });
      showToast(`${actor && actor.name ? actor.name : 'Craft'} updated a record`, () => {
        placeOnDay(offset, old, !old.allDay); setToast(null);
      });
      return { ref: a.ref };
    }

    if (op === 'app.compose') {
      openCompose();
      window.dispatchEvent(new CustomEvent('md:craft-native-open'));
      if (actor && window.mdCraftActivityAdd) window.mdCraftActivityAdd(actor.instance,
        { op, summary: 'Opened a new record' });
      return { opened: true };
    }

    throw 'unknown_op';
  }, [boardCtx, dayEvents, allDayRecords, completed, placeOnDay, openCompose,
    removeEverywhere, showToast]);

  /* One door for the frame, so ManifestFrame does not have to know which side of
     the read/write line an op falls on — md-bridge.ts's table already does. */
  const manifestCall = useCallback((op, arg, actor) => (
    window.MD_BRIDGE_OPS[op] === 'write' ? manifestWrite(op, arg, actor) : manifestRead(op, arg, actor)
  ), [manifestRead, manifestWrite]);

  /* Put a manifestation from the library onto the board that is open. Built
     here rather than reaching into BoardCanvas because the library is app
     chrome and the board may not even be mounted — if there is no board, the
     row simply cannot offer this. */
  const placeManifest = useCallback((row) => {
    const api = boardApiRef.current;
    if (!api || !row || !window.mdCraftRef) return;
    const ref = window.mdCraftRef(row.instance);
    const st = api.getAppState();
    const z = (st.zoom && st.zoom.value) || 1;
    const w = window.MD_BOARD_MANIFEST_W, h = window.MD_BOARD_MANIFEST_H;
    const x = st.width / 2 / z - st.scrollX - w / 2;
    const y = st.height / 2 / z - st.scrollY - h / 2;
    const el = window.mdBoardPinElement(ref, x, y, { width: w, height: h });
    if (!el) return;
    api.updateScene({ elements: [...api.getSceneElements(), el] });
  }, []);

  const wikiCatalog = useMemo(() => {
    /* The ROWS — what the [[wikilink]] autocomplete offers — come from the one
       candidate source the database member picker also consumes (md-resolve.ts),
       handed the maps already in memory so nothing re-reads storage. The
       SOURCES — the backlink index — still need each record's writing and its
       parsed links, which only these loops hold, so they are built here. */
    const rows = window.mdRecordCandidates
      ? window.mdRecordCandidates('', { events: dayEvents, allDay: allDayRecords, projectRecords })
      : [];
    const sources = [], seen = new Set();
    const addDayMap = (map) => {
      for (const day of Object.keys(map || {}).sort()) {
        const list = map[day];
        if (!Array.isArray(list)) continue;
        for (const rec of list) {
          if (!rec || typeof rec.uid !== 'string' || seen.has(rec.uid)) continue;
          seen.add(rec.uid);
          const ref = { type: window.mdRecordType(rec), id: rec.uid };
          const text = window.mdRecordSeedText(rec);
          const title = window.mdRecordTitle(rec);
          sources.push({ ref, title, day, subtitle: `${day} · Day record`,
            text, links: window.mdWikiBindings(rec.links, text) });
        }
      }
    };
    addDayMap(dayEvents);
    addDayMap(allDayRecords);
    for (const rec of projectRecords) {
      if (!rec || typeof rec.uid !== 'string' || seen.has(rec.uid)) continue;
      seen.add(rec.uid);
      const ref = { type: window.mdRecordType(rec), id: rec.uid };
      const text = window.mdRecordSeedText(rec);
      sources.push({ ref, title: window.mdRecordTitle(rec), domainId: rec.domain || null,
        subtitle: 'Project backlog', text, links: window.mdWikiBindings(rec.links, text) });
    }
    for (const pursuit of (window.mdPursuitIndex ? window.mdPursuitIndex() : [])) {
      const ref = { type: 'pursuit', id: pursuit.id };
      const text = window.mdPursuitTextRead(pursuit.id);
      sources.push({ ref, title: pursuit.name, domainId: pursuit.domainId, subtitle: 'Pursuit',
        text, links: window.mdPursuitLinksRead ? window.mdPursuitLinksRead(pursuit.id) : [] });
    }
    for (const project of (window.mdProjectIndex ? window.mdProjectIndex() : [])) {
      const ref = { type: 'project', id: project.id };
      const text = window.mdProjectTextRead(project.id);
      sources.push({ ref, title: project.name, domainId: project.domainId, subtitle: 'Project',
        text, links: window.mdProjectLinksRead ? window.mdProjectLinksRead(project.id) : [] });
    }
    for (const domain of (window.MD_DOMAINS || [])) {
      const ref = { type: 'domain', id: domain.id };
      const text = window.mdDomainTextRead(domain.id);
      sources.push({ ref, title: domain.label, domainId: domain.id, subtitle: 'Part of life',
        text, links: window.mdDomainLinksRead ? window.mdDomainLinksRead(domain.id) : [] });
    }
    return { rows, sources };
  }, [dayEvents, allDayRecords, projectRecords, compose, projectsOpen]);

  const wikiBacklinksFor = useCallback((target) => {
    const key = window.mdWikiTargetKey(target);
    if (!key) return [];
    return wikiCatalog.sources.filter((source) => source.links.some((link) =>
      window.mdWikiTargetKey(link.target) === key));
  }, [wikiCatalog]);

  /* Tags have no index document to sync. The records already in memory are the
     index, rebuilt here whenever their writing changes. A tag therefore cannot
     survive after its last occurrence was removed, and two devices can never
     disagree about metadata while agreeing about the record that contains it. */
  const tagCatalog = useMemo(() => {
    const byKey = new Map();
    for (const source of wikiCatalog.sources) {
      for (const tag of (window.mdTags ? window.mdTags(source.text) : [])) {
        let row = byKey.get(tag.key);
        if (!row) {
          row = { key: tag.key, name: tag.name, count: 0, records: [] };
          byKey.set(tag.key, row);
        }
        row.count++;
        row.records.push({ ref: source.ref, title: source.title, subtitle: source.subtitle });
      }
    }
    return [...byKey.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [wikiCatalog]);

  const createWikiRecord = useCallback((title) => {
    const clean = typeof title === 'string' ? title.trim().slice(0, 60) : '';
    if (!clean) return null;
    const uid = window.mdRecordUid();
    const clock = new Date();
    const rawNow = window.mdNowMin ? window.mdNowMin() : clock.getHours() * 60 + clock.getMinutes();
    const min = Math.max(0, Math.min(1435,
      Math.round(rawNow / 5) * 5));
    const record = { uid, kind: 'event', record: true, allDay: false,
      name: clean, body: '', min, dur: 30, t: minToHHMM(min),
      created: Date.now(), origin: window.mdRecordOriginStamp() };
    placeOnDay(viewOffset, record, true);
    const refKey = window.mdRecordRefKey?.(window.mdRecordRefFor?.(record, null));
    const navTarget = refKey && window.mdNavigationTarget?.({ go: 'record', ref: refKey });
    if (navTarget) window.mdNavigationRecord?.(navTarget);
    return { type: window.MD_RECORD_DEFAULT_TYPE, id: uid };
  }, [placeOnDay, viewOffset]);

  const openWikiTarget = useCallback((target) => {
    if (!target) return;
    if (target.type === 'project') {
      /* Its own window now. A wiki link's walk DELIBERATELY stands it on the
         Workshop — seated as a real, visible entry — so Escape lands where it
         always did rather than on the day. */
      dropIfBlank(); setCompose(null);
      setScene((s) => window.mdSceneWorkshopRoot(s));
      openProjectWindow(target.id, { kind: 'project-page' }); return;
    }
    if (target.type === 'pursuit') {
      const pursuit = window.mdPursuitById && window.mdPursuitById(target.id);
      if (!pursuit) return;
      dropIfBlank(); setCompose(null);
      setScene((s) => window.mdSceneOpen(s, 'workshop',
        { view: { kind: 'pursuit', id: target.id, domainId: pursuit.domainId } }));
      return;
    }
    if (target.type === 'domain') {
      dropIfBlank();
      setCompose(null);
      setScene((s) => window.mdSceneOpen(s, 'workshop',
        { view: { kind: 'domain', id: target.id } }));
      return;
    }
    const found = window.mdRecordResolve(target, boardCtx);
    if (!found) return;
    if (found.type === 'dbrow' && found.databaseId) {
      dropIfBlank(); setCompose(null);
      /* Opening the database retires the workshop family and the catalogue by
         the registry's group rule — no hand teardown left to mis-aim. */
      setScene((s) => window.mdSceneOpen(
        window.mdSceneDismiss(window.mdSceneDismiss(s, 'settings'), 'crafts'),
        'database', { id: found.databaseId, row: found.ref.id }));
      return;
    }
    if (!found.day) {
      /* A dateless record opens in the sheet, on top of wherever you are —
         no window is opened or closed to show it (OD-3). */
      const rec = projectRecords.find((row) => row && row.uid === target.id);
      if (rec) editProjectBacklogRecord(rec.uid);
      return;
    }
    const list = [...(dayEvents[found.day] || []), ...(allDayRecords[found.day] || [])];
    const rec = list.find((row) => row && row.uid === target.id);
    if (rec) editRecord(rec);
  }, [allDayRecords, boardCtx, dayEvents, projectRecords, dropIfBlank, editRecord,
    editProjectBacklogRecord]);

  /* One immutable destination id, one door. Search, Recents, Favorites,
     pasted links, and browser history all arrive through mdnavigationopen;
     none of those surfaces gets to invent its own approximation of where a
     record lives. The ref resolver follows day moves before this opens the
     native compose sheet or project backlog page. */
  openFindRecordRef.current = (refKey) => {
    const ref = window.mdRecordRefParse?.(refKey);
    if (!ref) return false;
    openWikiTarget(ref); return true;
  };
  /* A live mirror of the payload facts, because popstate reaches long-lived
     closures. */
  const routedRef = useRef({});
  routedRef.current = { compose: !!compose, reschedule: !!reschedule,
    board: desksOpen || deskShown, dbwindow: databaseWindowOpen };
  const sceneNowRef = useRef(scene);
  sceneNowRef.current = scene;
  /* After a journal entry is applied, square what stands with the payloads
     and the URL. The journal alone decides openness — a payload whose entry
     the walk retired is closed by its own transition effect, never from
     here. What is left for this reconcile is the two edges the scene cannot
     see: a Forward walk onto a sheet entry whose payload is gone (re-resolve
     it from the URL when it names a record, restore it directly when the
     surface is its own payload, and bounce the entry when a draft has
     nothing to rebuild from), and a `#md=…` naming a surface the applied
     scene does not hold — that hash is a resurrection: it survives into the
     next reload or share and opens a window nobody asked for, so it is
     cleared in place. */
  const reconcileRouted = useCallback((applied) => {
    const target = window.mdNavigationTargetFromUrl?.();
    const place = target ? window.mdNavigationPlace?.(target) : null;
    const holdsRecord = window.mdSceneIsOpen(applied, 'compose');
    const holdsDesk = window.mdSceneIsOpen(applied, 'desks') || window.mdSceneIsOpen(applied, 'board');
    if (holdsRecord && !routedRef.current.compose) {
      if (place?.go === 'record') openFindRecordRef.current?.(place.ref);
      else setScene((s) => window.mdSceneDismiss(s, 'compose'));
    }
    else if (!holdsRecord && place?.go === 'record') window.mdNavigationClearRoutedTarget?.();
    if (window.mdSceneIsOpen(applied, 'reschedule') && !routedRef.current.reschedule)
      setScene((s) => window.mdSceneDismiss(s, 'reschedule'));
    /* Adopt what the walk landed on. A `board` entry means the desk is
       uncovered — and the mirror writes one only on a phone — so ground it and
       show it; a `desks` entry alone means the chooser, so put the day back and
       stand the chooser up. This branch is the one the tri-state's last caller
       lived in: it runs only when a history walk lands on a desk layer the
       facts do not hold, which is why a call to a name that no longer existed
       went unnoticed until Back was pressed on a phone. */
    if (holdsDesk && !routedRef.current.board) {
      if (window.mdSceneIsOpen(applied, 'board')) showDesk();
      else { setDeskShown(false); setDesksOpen(true); }
    }
    else if (!holdsDesk && (place?.go === 'board' || place?.go === 'boards'))
      window.mdNavigationClearRoutedTarget?.();
    /* Forward onto the D3 window's entry: the scene holds `dbwindow`, the
       space's own window is closed, and the URL names the database — ask
       the space to stand it back up. */
    const d3Forward = window.mdSceneIsOpen(applied, 'dbwindow') && place?.go === 'database';
    if (d3Forward && !routedRef.current.dbwindow)
      window.dispatchEvent(new CustomEvent('mddbwindowrestore', { detail: { id: place.id } }));
    if (place && !d3Forward && !['record', 'board', 'boards', 'day'].includes(place.go)) {
      const opened = window.mdScenePlaceActions(place, applied, L).opened;
      if (opened && !window.mdSceneIsOpen(applied, opened)) window.mdNavigationClearRoutedTarget?.();
    }
  }, [L, showDesk]);
  reconcileRoutedRef.current = reconcileRouted;
  useEffect(() => {
    const open = (event) => {
      const detail = event?.detail;
      if (detail?.place) goFind(detail.place, { target: detail.target, source: detail.source });
    };
    const fromHistory = (event) => {
      /* An entry this session stamped: apply the scene that stood there.
         Idempotent — the reconcile after a dismissal arrives here and finds
         nothing to do. Tabs is an ordinary depth now (N-8): when the walk
         lands on its entry, ask the Finder forward — a scene where it never
         left keeps `open` true while the panel may still be hiding it. */
      const back = window.mdSceneJournalPop(historyRef.current, event?.state);
      if (back) {
        historyRef.current = back.journal;
        setScene((s) => (window.mdSceneHistoryDelta(s, back.scene).op === 'none' ? s : back.scene));
        if (window.mdSceneTop(back.scene) === 'tabs') showTabsAfterPanel(back.scene);
        reconcileRouted(back.scene);
        return;
      }
      /* An entry from before this journal. Forward into a destination
         re-opens it from its URL; a bare old entry gets nothing — the old
         handler tore every surface down here, which is the nuke this story
         retires. */
      const target = window.mdNavigationTargetFromUrl?.();
      if (target) window.mdNavigationNavigate?.(target, { history: false,
        source: event?.state?.mdFrom || 'history' });
    };
    const adoptQuiet = () => {
      /* A navigation whose destination already stood exactly as asked
         changes no scene, so the watcher never sees the entry Navigate
         pushed — and an unstamped entry is invisible to every walk. The
         entry exists, so it gets the standing scene as its own depth.
         Deferred a tick: a navigation that IS about to change the scene
         lets the watcher adopt it first, and finds it stamped here. */
      setTimeout(() => {
        const st = window.history?.state;
        if (!st || !st.mdTarget || typeof st.mdDepth === 'number') return;
        const journal = historyRef.current;
        const scenes = [...journal.scenes.slice(0, journal.pointer + 1), sceneNowRef.current];
        historyRef.current = { ...journal, scenes, pointer: scenes.length - 1 };
        window.history.replaceState({ ...st, mdDepth: historyRef.current.pointer,
          mdEra: journal.era }, '', window.location.href);
        window.__mdJournalNow = historyRef.current;
      }, 60);
    };
    window.addEventListener('mdnavigationopen', open);
    window.addEventListener('popstate', fromHistory);
    window.addEventListener('mdnavigationchange', adoptQuiet);
    return () => { window.removeEventListener('mdnavigationopen', open);
      window.removeEventListener('popstate', fromHistory);
      window.removeEventListener('mdnavigationchange', adoptQuiet); };
  }, [goFind, reconcileRouted, showTabsAfterPanel]);
  const initialDeepLinkHandled = useRef(false);
  useEffect(() => {
    if (initialDeepLinkHandled.current) return;
    initialDeepLinkHandled.current = true;
    const target = window.mdNavigationTargetFromUrl?.();
    if (target) setTimeout(() => window.mdNavigationNavigate?.(target, { history: false,
      source: window.history?.state?.mdFrom || null }), 0);
  }, []);

  /* Tapping a pinned record opens the thing itself, not a copy of it: a day
     record opens in the editor exactly as its card in the day would, and a
     project or a context opens where those are read. A pin is a VIEW, so there
     is nowhere else for it to lead. */
  const openPinned = useCallback((found) => {
    if (!found) return;
    if (found.ref.type === 'project') {
      setScene((s) => window.mdSceneWorkshopRoot(s));
      openProjectWindow(found.ref.id, { kind: 'project-page' }); return true;
    }
    if (found.ref.type === 'pursuit' || found.ref.type === 'domain') {
      setScene((s) => window.mdSceneOpen(s, 'workshop',
        { view: { kind: found.ref.type, id: found.ref.id } }));
      return true;
    }
    if (!found.day) {
      const backlog = projectRecords.find((row) => row && row.uid === found.ref.id);
      if (backlog) { editProjectBacklogRecord(backlog.uid); return true; }
      return false;
    }
    // Both lists, for the same reason boardCtx carries both: a record on the
    // all-day shelf is an ordinary record, and looking it up in the timed list
    // alone made tapping its pin do nothing at all.
    const find = (map) => {
      const list = map[found.day];
      return Array.isArray(list) ? list.find((r) => r && r.uid === found.ref.id) : null;
    };
    const rec = find(dayEvents) || find(allDayRecords);
    if (rec) { editRecord(rec); return true; }
    return false;
  }, [dayEvents, allDayRecords, projectRecords, editRecord, editProjectBacklogRecord]);

  manifestActionsRef.current.openRecord = openPinned;

  /* ── a link, dropped or pasted onto the board ─────────────────────────────
     It becomes a RECORD, on the all-day shelf of the day being looked at, and
     the board gets a pin pointing at it. Nothing about the media is stored on
     the board — md-media.ts reads it back out of the record's own text — so the
     rule the board already lives by holds: a pin is a view of a record, never a
     copy of one.

     THE DAY IS THE ONE BEING VIEWED, not literally today. That is what every
     other record here does (`commitLive` falls back to `viewOffset` for exactly
     this), and a record that appeared on a day the person is not looking at
     would be a record they have to go and find.

     THE TEXT IS THE URL, AND NOTHING ELSE. A title would have to be invented —
     "Video", or the host's name — and md-projects.jsx:227 already rejects
     writing the app's own words into somebody's record where their words go. So
     the link is the title until they type one over it, which is one line of
     editing in the sheet that already opens. Fetching the real title from the
     host is the honest fix and it is a network call with its own failure modes;
     it is not in this pass. */
  const addMediaRecord = useCallback((url) => {
    const text = typeof url === 'string' ? url.trim() : '';
    /* Any address that can stand as a line of writing, not only a playable one:
       md-embed.ts draws what it cannot play as a bookmark card, so an article
       dropped on a board becomes a record like a video does. */
    const embed = text && window.mdEmbedLine ? window.mdEmbedLine(text) : null;
    if (!embed) return null;
    const media = embed.media;
    const uid = window.mdRecordUid();
    const key = mdDayKey(viewOffset);
    /* THE ORDINARY NEW-RECORD CONVENTION, and nothing of its own. A dropped
       video is a record like any other: it starts now, snapped to five minutes,
       and runs half an hour — the same answer md-compose.jsx:516 gives the "+"
       button. It landed on the all-day shelf before this, which made it a
       different species of thing for no reason anybody could see.

       The snap is spelled out rather than borrowed: md-compose keeps `mdSnap5`
       private to its own module, and reaching into it would make a shared helper
       out of something neither file has asked to share. */
    const min = Math.max(0, Math.min(1435, Math.round((window.mdNowMin ? window.mdNowMin() : 0) / 5) * 5));
    placeOnDay(viewOffset, { uid, kind: 'event', record: true, allDay: false,
      /* A picture names itself and a bookmark is named by its own address, so
         both arrive titled. A video's name is only known to its host, so it
         starts blank and the fetch below fills it — writing a placeholder here
         would be worse than nothing, because that fetch will not overwrite a
         name that already exists. */
      name: (media && media.title) || (media ? '' : window.mdEmbedName(embed)) || '', body: text,
      min, dur: MD_MEDIA_DUR, t: minToHHMM(min),
      created: Date.now(), origin: window.mdRecordOriginStamp() }, true);

    /* THE TITLE ARRIVES LATE, OR NOT AT ALL. Only the host knows what a video is
       called, so the record is written first and named afterwards — a drop that
       waited on a network round trip would be a drop that hangs. An image needs
       no wait: its name is already in its own path, so `media.title` above is
       filled in and this only ever confirms it.

       IT WILL NOT OVERWRITE A TITLE SOMEBODY TYPED. The fetch can land seconds
       later, by which time the record may be open and named by hand; the whole
       point of a record is that what the person wrote wins. */
    if (media && window.mdMediaTitle) {
      window.mdMediaTitle(media).then((title) => {
        if (!title) return;
        setDayEvents((prev) => {
          const list = prev[key];
          if (!Array.isArray(list)) return prev;
          const i = list.findIndex((r) => r && r.uid === uid);
          if (i < 0 || list[i].name) return prev;      // gone, or already named
          const next = list.slice();
          next[i] = { ...next[i], name: title };
          return { ...prev, [key]: next };
        });
      });
    }
    return { type: window.MD_RECORD_DEFAULT_TYPE, id: uid, day: key };
  }, [viewOffset, placeOnDay]);

  /* Delete the WRITING behind a pinned card, from the card's own ⋯ menu. The
     board has already dropped the element by the time this runs — that is the
     half it owns — and this is the other half, which no board should be able to
     do by accident.

     It goes through the same undo toast every other deletion in this app gets.
     A record deleted from a board is not more deliberate than one deleted from
     the day, and offering the way back in one place but not the other is how a
     person learns not to trust either. */
  const deletePinnedRecord = useCallback((found, restoreCard) => {
    if (!found || !found.ref || !found.day) return;
    const uid = found.ref.id;
    const key = found.day;
    const timed = (dayEvents[key] || []).find((r) => r && r.uid === uid);
    const shelved = timed ? null : (allDayRecords[key] || []).find((r) => r && r.uid === uid);
    const rec = timed || shelved;
    if (!rec) return;
    /* The day the record actually lives on, not the one being looked at. Undo
       has to put it back where it was — a card pinned from last Tuesday and
       deleted today would otherwise reappear on today, which is a different
       record in every way that matters. */
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
    const offset = m && window.mdOffsetForDate
      ? window.mdOffsetForDate(+m[1], +m[2] - 1, +m[3]) : viewOffset;
    if (timed) {
      setDayEvents((prev) => ({ ...prev, [key]: (prev[key] || []).filter((e) => e.uid !== uid) }));
    } else {
      setAllDayRecords((prev) => ({ ...prev, [key]: (prev[key] || []).filter((r) => r.uid !== uid) }));
    }
    showToast('Record deleted', () => {
      placeOnDay(offset, rec, !!timed);
      // and the card it was deleted from, so one Undo undoes the whole act
      if (restoreCard) restoreCard();
      setToast(null);
    });
  }, [dayEvents, allDayRecords, viewOffset, showToast, placeOnDay]);

  /* ── a document, dropped on the board ─────────────────────────────────────
     The same record as any other, and the document is a LINE OF ITS WRITING —
     `![Al-Ghazali.pdf](mdfile:9fx2k3j1)`, which md-embed.ts explains at length.
     The name comes first because every device can read it; the id is the part
     only the device holding the bytes can use.

     `ref.name` is the document's own title where the PDF states one and its
     filename otherwise, so the record has a real first line from the moment it
     appears — no placeholder, and nothing to wait for. */
  const addFileRecord = useCallback((ref, given) => {
    if (!ref || !window.mdFileRefOk || !window.mdFileRefOk(ref)) return null;
    const line = window.mdEmbedFileLine ? window.mdEmbedFileLine(ref) : '';
    if (!line) return null;
    /* The caller's id where it has one, because the bytes have already been
       filed under it. Minting a second one here would leave the hold pointing
       at a record that never appears — the file would be nobody's, and the
       storage it takes would never come back. */
    const uid = (typeof given === 'string' && given) ? given : window.mdRecordUid();
    const key = mdDayKey(viewOffset);
    const min = Math.max(0, Math.min(1435, Math.round((window.mdNowMin ? window.mdNowMin() : 0) / 5) * 5));
    /* `{hash, blob:1}` ON THE RECORD, not only in the writing. The line is a
       sentence; this is a declaration, and it is what md-barrier.ts reads to
       hold this day back until the bytes have landed. Without it the day
       publishes first and the other device pulls a record naming a document
       the store has never heard of, with no way to tell "still arriving" from
       "gone".

       Only where there IS a hash. A Craft keeps its local name and is never
       offered to a store, and a record marked for bytes that are not coming
       would be held back for ever. */
    const bytes = ref.hash ? { hash: ref.hash, blob: 1 } : null;
    placeOnDay(viewOffset, { uid, kind: 'event', record: true, allDay: false,
      name: ref.name, body: line, ...(bytes || {}),
      min, dur: MD_MEDIA_DUR, t: minToHHMM(min),
      created: Date.now(), origin: window.mdRecordOriginStamp() }, true);
    return { type: window.MD_RECORD_DEFAULT_TYPE, id: uid, day: key };
  }, [viewOffset, placeOnDay]);

  // ════════ delete + reschedule (timeline swipe actions) ════════
  // Remove an item from whichever store owns it on the viewed day. Timed events live
  // in that day's stored event list; All-Day
  // records live in allDayRecords. Always offers an Undo toast that puts it back.
  const deleteItemNow = useCallback((item, sourceOffset = viewOffset, sourceDay = mdDayKey(sourceOffset)) => {
    const timed = !(item.kind === 'record' || item.allDay === true);
    if (timed) {
      setDayEvents((prev) => {
        const key = sourceDay;
        return { ...prev, [key]: (prev[key] || []).filter((e) => e.uid !== item.uid) };
      });
    } else {
      setAllDayRecords((prev) => {const key = sourceDay;return { ...prev, [key]: (prev[key] || []).filter((r) => r.uid !== item.uid) };});
    }
    setCompleted((prev) => {const k = sourceDay + '|' + item.uid;if (!prev[k]) return prev;const n = { ...prev };delete n[k];return n;});
    showToast('Deleted', () => {placeOnDay(sourceOffset, item, timed);setToast(null);});
  }, [viewOffset, showToast, placeOnDay]);

  // ask first — a calm confirm sheet, then delete
  const requestDeleteItem = useCallback((item) => {
    const source = mdTimelineSource ? mdTimelineSource(item) : { item, offset: null, dayKey: null };
    const sourceOffset = source.offset != null ? source.offset : viewOffset;
    const sourceDay = source.dayKey || mdDayKey(sourceOffset);
    setConfirm({
      title: 'Delete this record?',
      message: source.item && source.item.name ? `“${source.item.name}” will be removed from your day.` : 'This will be removed from your day.',
      onConfirm: () => {deleteItemNow(source.item, sourceOffset, sourceDay);setConfirm(null);}
    });
  }, [deleteItemNow, viewOffset]);

  const rescheduleItem = useCallback((item) => {
    const source = mdTimelineSource ? mdTimelineSource(item) : { item, offset: null, dayKey: null };
    const sourceOffset = source.offset != null ? source.offset : viewOffset;
    setReschedule({ item: source.item, sourceOffset, sourceDay: source.dayKey || mdDayKey(sourceOffset) });
  }, [viewOffset]);

  // commit a reschedule: pull the item off its source (viewed) day, then place it on the
  // chosen target day/window. The target is always timed now, so this is one-way:
  // a legacy all-day record that gets moved is promoted onto the timeline. Carries
  // the done-state and identity (icon colour) across the move.
  const moveItem = useCallback((item, sched, sourceOffset = viewOffset, sourceDay = mdDayKey(sourceOffset)) => {
    const srcTimed = !(item.kind === 'record' || item.allDay === true);
    const target = sched.offset != null ? sched.offset : viewOffset;
    if (srcTimed) {
      setDayEvents((prev) => {
        const key = sourceDay;
        return { ...prev, [key]: (prev[key] || []).filter((e) => e.uid !== item.uid) };
      });
    } else {
      setAllDayRecords((prev) => {const key = sourceDay;return { ...prev, [key]: (prev[key] || []).filter((r) => r.uid !== item.uid) };});
    }
    setCompleted((prev) => {const sk = sourceDay + '|' + item.uid;if (!prev[sk]) return prev;const n = { ...prev };delete n[sk];n[mdDayKey(target) + '|' + item.uid] = true;return n;});
    placeOnDay(target, { ...item, kind: 'event', allDay: false, id: item.id || item.domain || undefined,
      min: sched.min, dur: sched.dur, t: minToHHMM(sched.min), offset: target }, true);
  }, [viewOffset, placeOnDay]);

  // ── grid → compose bridges ──
  // GridView publishes its draft-write API; App opens the compose sheet bound to a
  // drawn time, edits an existing grid Record on tap, or removes a tray Record once scheduled.
  const registerGridApi = useCallback((api) => {gridApiRef.current = api;}, []);
  // Drawing a block on the grid is a NEW record that happens to arrive already
  // timed, so it gets the keyboard; tapping an existing block does not.
  const composeForGrid = useCallback((rec) => {
    if (window.mdPrimeKeyboard) window.mdPrimeKeyboard();
    dropIfBlank();
    const anchor = edit && Number.isFinite(edit.dayOffset) ? edit.dayOffset : viewOffset;
    const source = mdGridSource(rec, anchor, mdDayKey);
    // md-grid drew this block already; remember it, so closing without creating
    // anything takes it back off the grid (P-7).
    pendingGridRef.current = rec.uid;
    setCompose({ seq: ++composeSeq.current, initial: source.item, fresh: true, fromGrid: true,
      gridUid: rec.uid, dayOffset: source.offset, timeBinding: { min: source.item.min, dur: source.item.dur } });
  }, [dropIfBlank, edit, viewOffset]);
  const editGridRecord = useCallback((rec) => {
    dropIfBlank();
    const anchor = edit && Number.isFinite(edit.dayOffset) ? edit.dayOffset : viewOffset;
    const source = mdGridSource(rec, anchor, mdDayKey);
    setCompose({ seq: ++composeSeq.current, initial: source.item, fromGrid: true,
      gridUid: rec.uid, dayOffset: source.offset, timeBinding: { min: source.item.min, dur: source.item.dur } });
  }, [dropIfBlank, edit, viewOffset]);
  const scheduleFromTray = useCallback((uid) => {
    const offset = edit && Number.isFinite(edit.dayOffset) ? edit.dayOffset : viewOffset;
    setAllDayRecords((prev) => {const key = mdDayKey(offset);return { ...prev, [key]: (prev[key] || []).filter((r) => r.uid !== uid) };});
  }, [edit, viewOffset]);

  /* ── place ───────────────────────────────────────────────────────────────
     md-theme seeded a location synchronously (saved, else timezone guess) so the
     first paint has real times. This refines it with the edge lookup, which
     knows the city rather than just the zone.

     It never overrides a location the user picked themselves — `source` of
     'manual' or 'precise' means they answered the question and the edge's guess
     does not get to disagree. `locStamp` exists to force the re-render, since
     the prayer times live outside React state. */
  const [locStamp, setLocStamp] = useState(0);
  useEffect(() => {
    let cancelled = false;
    /* THE WORLD ARRIVES BEFORE ANYBODY ASKS FOR IT (Marwan, 2026-08-31). The
       city table is a megabyte that stays out of the first morning's
       download; this quiet fetch, well clear of first paint, is what makes
       the picker whole OFFLINE for somebody who never opened it online — the
       service worker keeps every same-origin response it sees. It stands
       ABOVE the manual-place door below: the device that already chose a city
       is the device most likely to open the picker again, and it was exactly
       the one the door was turning away (found live, 2026-08-31). */
    if (window.mdWarmWorldCities) window.mdWarmWorldCities(8000);
    const current = mdLocation();
    if (current && (current.source === 'manual' || current.source === 'precise')) return;
    mdDetectLocation().then((found) => {
      if (cancelled || !found) return;
      mdSetLocation(found);
      mdSaveLocation(found);
      const moved = mdCalcOnMove(mdLoadCalc(), found.country);
      if (moved) { mdSetCalc(moved); mdSaveCalc(moved); }
      setLocStamp((n) => n + 1);
    });
    return () => { cancelled = true; };
  }, []);

  /* The served Hijri correction. Applied only when the user has not set their
     own — see /api/hijri in web/src/worker.js for why this exists. */
  useEffect(() => {
    if (mdHijriAdjustIsUsers()) return;
    let cancelled = false;
    mdFetchHijriCorrection().then((c) => {
      if (cancelled || !c || !c.adjust) return;
      mdSetHijriAdjust(c.adjust, false);
      setLocStamp((n) => n + 1);
    });
    return () => { cancelled = true; };
  }, []);

  /* Local-first: every store write is already done by the time this runs, and
     nothing waits on it. A failed or absent sync is invisible to the app. */
  useEffect(() => { window.mdSyncSoon && window.mdSyncSoon(); }, [dayEvents, allDayRecords, projectRecords, completed]);

  /* A second device only learns about the first when it asks. Local edits are
     not the only reason to ask: coming back to the app, or coming back online,
     are the two moments something is most likely to have changed elsewhere.

     THEY ARE NOT THE SAME MOMENT, though, and they shared a debounce until
     2.3. A tab regaining focus has lost nothing — a few hundred milliseconds
     of coalescing is exactly right for it. A device regaining the NETWORK may
     have lost a month: it is the one machine in the account still holding
     records everybody else agreed to delete, and every edit it made while it
     was away has already scheduled a push of its own. Waiting is the window in
     which that push goes first.

     A debounce is also a promise the tab has to survive to keep, and this is
     the event least likely to be survived: a phone that reconnects as it is
     being put down never syncs at all, and the reconciliation waits for the
     next launch. `document.hidden` is deliberately not consulted for the same
     reason — nobody has to be looking for the account to need settling.

     So it asks immediately, and asks for everything. `force` offers every
     document rather than only those whose hash has moved, which is what makes
     a device that has been away re-state its whole position instead of
     trusting bookkeeping written before the gap. mdSync is pull-then-push, so
     what it re-states is what it holds AFTER it has caught up. */
  useEffect(() => {
    const soon = () => { if (!document.hidden) window.mdSyncSoon && window.mdSyncSoon(400); };
    const back = () => { if (window.mdSync) window.mdSync({ force: true }); };
    document.addEventListener('visibilitychange', soon);
    window.addEventListener('online', back);
    window.addEventListener('focus', soon);
    return () => {
      document.removeEventListener('visibilitychange', soon);
      window.removeEventListener('online', back);
      window.removeEventListener('focus', soon);
    };
  }, []);

  /* A pull can rewrite the stores from under React. Re-read them when it does,
     rather than leaving the screen showing what this device knew before. */
  useEffect(() => {
    const reread = () => {
      setDayEvents(mdStoreAll('events'));
      setAllDayRecords(mdStoreAll('records'));
      setCompleted(mdStoreCompletedFlat());
      /* The backlog moves for the same two reasons the day does. It reached
         this list late, when the Project window gave a captured thought a
         status you can take back: `md:projectRecords` is in that window's undo
         scope, so ⌘Z rewrites the document — and without this line the store
         said Backlog while the board went on drawing Done. */
      setProjectRecords(window.mdProjectRecordIndex());
    };
    /* An undo rewrites the stores from under React in exactly the way a pull
       does, so it gets exactly the same answer. An undo that moved storage and
       left the screen showing the old day would look like a press that did
       nothing — and the hand would press again, losing a second act to account
       for the first.

       Only for an undo or a redo. A push is this screen's own edit; re-reading
       on it would fight the state that just caused it. */
    const onHistory = (event) => {
      const kind = event && event.detail && event.detail.kind;
      if (kind === 'undo' || kind === 'redo') reread();
    };
    window.addEventListener('mdsynced', reread);
    window.addEventListener('mdhistorychange', onHistory);
    return () => {
      window.removeEventListener('mdsynced', reread);
      window.removeEventListener('mdhistorychange', onHistory);
    };
  }, []);

  /* #book is derived from committed Record snapshots, never from editor DOM or
     a parallel tag store. The store kind/day is the source CAS owner. Running
     after the persistence effects above means mdBook can distinguish retained
     bytes from a local edit that has not reached sync yet. */
  useEffect(() => {
    if (!window.mdBookReconcileAll) return;
    const snapshots = [];
    const addDay = (kind, recordsByDay) => {
      for (const [day, records] of Object.entries(recordsByDay || {})) {
        if (!Array.isArray(records)) continue;
        for (const record of records) {
          const ref = window.mdRecordRefFor && window.mdRecordRefFor(record, day);
          const refKey = ref && window.mdRecordRefKey(ref);
          const text = window.mdRecordSeedText && window.mdRecordSeedText(record);
          if (refKey && text) snapshots.push({
            refKey, sourceKey: `${kind}:${day}`, text,
            locale: document.documentElement.lang || undefined,
          });
        }
      }
    };
    addDay('events', dayEvents);
    addDay('records', allDayRecords);
    for (const record of projectRecords || []) {
      const ref = window.mdRecordRefFor && window.mdRecordRefFor(record);
      const refKey = ref && window.mdRecordRefKey(ref);
      const text = window.mdRecordSeedText && window.mdRecordSeedText(record);
      if (refKey && text) snapshots.push({
        refKey, sourceKey: 'project-records', text,
        locale: document.documentElement.lang || undefined,
      });
    }
    void window.mdBookReconcileAll(snapshots);
  }, [dayEvents, allDayRecords, projectRecords]);

  const applyHijriAdjust = useCallback((days) => {
    mdSetHijriAdjust(days, true); mdSaveHijriAdjust(days); setLocStamp((n) => n + 1);
  }, []);

  /* Applied by the Settings sheet. Persists, recomputes, repaints — AND MOVES
     THE CALCULATION METHOD WITH YOU. Choosing a city in another country used to
     leave the method exactly where it was, so a person who set ManDana up in
     Cairo and then chose New York computed Fajr at the Egyptian authority's
     angle in America. The decision is mdCalcOnMove's, not this callback's: a
     country is a default and a chosen method is a decision, and the second one
     wins. Saved as un-chosen (mdSetCalc/mdSaveCalc, never applyCalc), because a
     default the app applied is not the person having answered. */
  const applyLocation = useCallback((loc) => {
    mdSetLocation(loc); mdSaveLocation(loc);
    const moved = mdCalcOnMove(mdLoadCalc(), loc && loc.country);
    if (moved) { mdSetCalc(moved); mdSaveCalc(moved); }
    setLocStamp((n) => n + 1);
  }, []);
  const applyCalc = useCallback((calc) => {
    // `chosen` goes into memory as well as storage. Without it the in-memory copy
    // still reads as un-chosen, and any later code that checks before applying a
    // country default would quietly overwrite what the user just picked.
    const next = { ...calc, chosen: true };
    mdSetCalc(next); mdSaveCalc(next); setLocStamp((n) => n + 1);
  }, []);

  // live clock — ticks every second, but only while the page is visible
  // (backgrounded tabs stop ticking entirely, then snap to the correct time on return)
  const [elapsed, setElapsed] = useState(0);
  const start = useRef(Date.now());
  useEffect(() => {
    let iv = null;
    let firstRun = true;
    const tick = () => {
      setElapsed(Math.floor((Date.now() - start.current) / 1000));
      // Midnight, or a return from background days later. The anchor moves, and
      // the viewed day moves with it so the user keeps looking at the same
      // calendar date instead of being slid forward a day underneath them.
      const moved = mdRefreshToday();
      if (moved !== 0) {
        // Every cached day is keyed by offset, and the anchor just shifted under
        // all of them — recompute rather than show yesterday's sun.
        mdClearPrayerCache();
        setViewOffset((o) => (o === 0 ? 0 : o - moved));
      }
    };
    const run = () => {
      if (document.hidden) {if (iv) {clearInterval(iv);iv = null;}return;}
      tick(); // resync immediately on resume
      if (!iv) iv = setInterval(tick, 1000);
      // scroll back to "now" whenever the app returns from background
      if (!firstRun) requestAnimationFrame(() => requestAnimationFrame(() => scrollToNowRef.current?.()));
      firstRun = false;
    };
    run();
    document.addEventListener('visibilitychange', run);
    return () => {if (iv) clearInterval(iv);document.removeEventListener('visibilitychange', run);};
  }, []);
  // `elapsed` is not added to the real clock — mdNowSec() already reads it fresh.
  // It stays in the dependency chain because its per-second change is what
  // re-renders this component, and it still drives the pinned design-time clock.
  const nowSec = t.nowMin != null ? t.nowMin * 60 + elapsed : mdNowSec();
  const nowMin = nowSec / 60;
  const next = mdNextPrayer(nowMin);

  /* The prayer engine remains the sole authority for every boundary. This layer
     only decides whether the live scroll holds one Fajr cycle or, between
     midnight and Fajr, yesterday plus the newly reached day. */
  const todayFajr = mdPrayersFor(0).find((p) => p.id === 'fajr');
  const cycle = mdCycleState(viewOffset, nowMin, todayFajr && todayFajr.min);
  const timeline = mdBuildTimeline({
    anchorOffset: cycle.anchorOffset, cycles: cycle.cycles,
    dayItemsFor: mdDayItems, eventsByDay: dayEvents, dayKeyFor: mdDayKey,
  });
  const items = timeline.items;
  const timelineNowMin = isToday ? cycle.nowMin : nowMin;
  const todayPrayers = mdPrayersFor(0);
  const nextOffset = next && todayPrayers.indexOf(next) !== -1 ? 0 : 1;
  const nextTimelineKey = next ? `prayer:${mdDayKey(nextOffset)}:${next.id}` : null;

  // The grid edits one Fajr cycle. Its tail belongs to the next civil date, so
  // composition and persistence both go through the civil/Fajr bridge.
  const gridCycleFor = (anchorOffset, eventsByDay = dayEvents) => mdBuildGridCycle({
    anchorOffset, cycles: 1, dayItemsFor: mdDayItems, eventsByDay, dayKeyFor: mdDayKey,
  });
  const commitGridDraft = (anchorOffset, draft) => {
    setDayEvents((prev) => {
      const bounds = gridCycleFor(anchorOffset, prev);
      if (!Number.isFinite(bounds.startMin) || !Number.isFinite(bounds.endMin)) {
        return { ...prev, [mdDayKey(anchorOffset)]: draft };
      }
      return mdCommitGridCycle({ anchorOffset, cycles: 1, dayItemsFor: mdDayItems,
        eventsByDay: prev, dayKeyFor: mdDayKey, draft,
        startMin: bounds.startMin, endMin: bounds.endMin });
    });
    setCompleted((prev) => {
      let next = prev;
      for (const item of draft) {
        if (!item || typeof item._mdSourceDay !== 'string') continue;
        const target = mdGridSource(item, anchorOffset, mdDayKey).dayKey;
        const oldKey = item._mdSourceDay + '|' + item.uid;
        const newKey = target + '|' + item.uid;
        if (target && target !== item._mdSourceDay && prev[oldKey]) {
          if (next === prev) next = { ...prev };
          delete next[oldKey]; next[newKey] = true;
        }
      }
      return next;
    });
  };

  // ════════ grid edit view: open / close + liquid morph ════════
  const openEdit = useCallback(({ focalMin, x, y, dayOffset }) => {
    const sc = scrollRef.current;if (!sc) return;
    // clear any transient press-transforms so origin rects are measured clean
    sc.querySelectorAll('[data-morph]').forEach((el) => {el.style.transform = '';});
    const origin = mdCaptureRects ? mdCaptureRects(sc) : {};
    setEdit({ phase: 'enter', focalMin, dayOffset: Number.isFinite(dayOffset) ? dayOffset : viewOffset,
      focal: { x, y }, origin });
  }, [viewOffset]);

  // fade the grid's opaque backdrop in once it mounts (covers the list beneath)
  useEffect(() => {
    if (!edit || edit.phase !== 'enter') return;
    const bg = gridBgRef.current;if (!bg) return;
    bg.style.opacity = '0';
    requestAnimationFrame(() => requestAnimationFrame(() => {
      bg.style.transition = 'opacity .5s ease';
      bg.style.opacity = '1';
    }));
  }, [edit && edit.phase]);

  const closeEdit = useCallback(() => {
    const getDraft = exitFnRef.current;
    const draft = getDraft ? getDraft() : null;
    const editedOffset = edit && Number.isFinite(edit.dayOffset) ? edit.dayOffset : viewOffset;
    if (draft) commitGridDraft(editedOffset, draft);
    // begin exit: scaffold recedes (GridView), blocks fly out, backdrop fades
    setEdit((e) => e ? { ...e, phase: 'exit' } : e);
    const layer = gridLayerRef.current;
    const focal = edit ? edit.focal : null;
    if (gridBgRef.current) {gridBgRef.current.style.transition = 'opacity .42s ease .08s';gridBgRef.current.style.opacity = '0';}
    if (layer && mdMorphOut) mdMorphOut(layer, null, { focal, duration: 460, stagger: 80 });
    setTimeout(() => {setEdit(null);exitFnRef.current = null;}, 520);
  }, [edit, viewOffset]);

  // switch the edited day from the in-grid week bar: commit the current draft,
  // hop to that day, and remount the grid (no FLIP — just a content swap).
  const switchEditDay = useCallback((off) => {
    const editedOffset = edit && Number.isFinite(edit.dayOffset) ? edit.dayOffset : viewOffset;
    if (off === editedOffset) return;
    const getDraft = exitFnRef.current;
    const draft = getDraft ? getDraft() : null;
    if (draft) commitGridDraft(editedOffset, draft);
    setViewOffset(off);
    setEdit((e) => e ? { ...e, dayOffset: off, phase: 'switch', focalMin: null, origin: null, key: (e.key || 0) + 1 } : e);
  }, [edit, viewOffset]);

  // measure the pinned Today header so the day list starts just below it
  const hdrRef = useRef(null);
  const [hdrH, setHdrH] = useState(252);
  useLayoutEffect(() => {
    const el = hdrRef.current;
    if (!el) return;
    const update = () => setHdrH(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // scroll area already pads 60px for the status bar; clear the rest of the header
  const todayTopPad = Math.max(0, hdrH - 60);

  // ════════ "Now" button visibility + jump-to-now ════════
  // The timeline marks two DOM anchors (set in md-today.jsx):
  //   [data-now-anchor] — the live now-line element (also the empty marker row)
  //   [data-now-card]   — every schedule card the current moment falls inside
  //                       (overlapping cards are all "now"; scroll to the first)
  // `nowBtn` = { show, dir }. recomputeNowBtn decides both; the pill renders it.
  const [nowBtn, setNowBtn] = useState({ show: false, dir: 'up' });
  // set when a Now-tap happens on another day: defer the scroll until today re-renders
  const pendingNow = useRef(false);

  // Decide whether the pill shows and which way its arrow points.
  //   • viewing another day → always show; arrow follows the day's direction
  //     (past = down, future = up) so it mirrors the scroll case below
  //   • today → show only when the now-line is outside the visible window:
  //       above the header fold → arrow up; below the bottom edge → arrow down
  const recomputeNowBtn = useCallback(() => {
    const sc = scrollRef.current;if (!sc) return;
    if (!isToday) {const d = viewOffset < 0 ? 'down' : 'up';setNowBtn((p) => p.show && p.dir === d ? p : { show: true, dir: d });return;}
    const anchor = sc.querySelector('[data-now-anchor]');
    if (!anchor) {setNowBtn((p) => p.show ? { show: false, dir: p.dir } : p);return;}
    const vv = window.visualViewport;
    const scR = L.phone
      ? { top: vv?.offsetTop || 0, bottom: (vv?.offsetTop || 0) + (vv?.height || window.innerHeight) }
      : sc.getBoundingClientRect();
    const headerH = hdrRef.current ? hdrRef.current.offsetHeight : 0;
    const aY = anchor.getBoundingClientRect().top;
    let show = false,dir = 'up';
    if (aY < scR.top + headerH + 6) {show = true;dir = 'up';} // line is above the fold
    else if (aY > scR.bottom - 10) {show = true;dir = 'down';} // line is below the fold
    setNowBtn((p) => p.show === show && p.dir === dir ? p : { show, dir });
  }, [isToday, viewOffset, L.phone]);

  // recompute on scroll (rAF-throttled)
  useEffect(() => {
    const sc = scrollRef.current;if (!sc) return;
    let raf = null;
    const onScroll = () => {if (raf != null) return;raf = requestAnimationFrame(() => {raf = null;recomputeNowBtn();});};
    const owner = L.phone ? window : sc;
    owner.addEventListener('scroll', onScroll, { passive: true });
    return () => {owner.removeEventListener('scroll', onScroll);if (raf != null) cancelAnimationFrame(raf);};
  }, [recomputeNowBtn, L.phone]);

  // recompute when the day, clock, header size, or timeline scaling changes
  useEffect(() => {recomputeNowBtn();}, [recomputeNowBtn, viewOffset, nowSec, hdrH]);

  // Smooth-scroll the active "now" card to sit just under the pinned header, so
  // the current moment becomes the first thing in the list. Falls back to the
  // now-line marker if no card is "current" (e.g. now lands in a free-time gap).
  const scrollToNowCard = useCallback(() => {
    const sc = scrollRef.current;if (!sc) return;
    const target = sc.querySelector('[data-now-card]') || sc.querySelector('[data-now-anchor]');
    if (!target) return;
    const headerH = hdrRef.current ? hdrRef.current.offsetHeight : 0;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (L.phone) {
      const top = Math.max(0, (window.scrollY || 0) + target.getBoundingClientRect().top - headerH - 10);
      window.scrollTo({ top, behavior: reduce ? 'auto' : 'smooth' });
      return;
    }
    const scR = sc.getBoundingClientRect();
    const delta = target.getBoundingClientRect().top - scR.top - headerH - 10;
    sc.scrollTo({ top: Math.max(0, sc.scrollTop + delta), behavior: reduce ? 'auto' : 'smooth' });
  }, [L.phone]);

  // keep the visibility handler's ref in sync, and scroll to now on first mount
  useEffect(() => {scrollToNowRef.current = scrollToNowCard;}, [scrollToNowCard]);
  useEffect(() => {
    // double rAF: let React paint + ResizeObserver settle before scrolling
    requestAnimationFrame(() => requestAnimationFrame(scrollToNowCard));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const cycleExpandedRef = useRef(cycle.expanded);
  useEffect(() => {
    if (cycleExpandedRef.current === cycle.expanded) return;
    cycleExpandedRef.current = cycle.expanded;
    // Midnight appends a cycle; Fajr removes the completed one. In both cases the
    // current moment is the stable visual anchor after the list changes shape.
    requestAnimationFrame(() => requestAnimationFrame(scrollToNowCard));
  }, [cycle.expanded, scrollToNowCard]);

  // Tap handler for the pill: on today, just scroll; otherwise hop to today
  // first and let the effect below finish the scroll once the day re-renders.
  const goToNow = () => {
    // inside the hour grid the pill drives the grid's own day; switchEditDay also
    // lands the timeline underneath on today for when the grid closes
    if (edit) {pendingNow.current = true;switchEditDay(0);return;}
    if (isToday) {scrollToNowCard();} else
    {pendingNow.current = true;setViewOffset(0);} // switch to today first, then scroll
  };

  // once today is rendered after a Now-tap from another day, scroll to the card
  // (double rAF: wait for the new day's items to lay out before measuring)
  useEffect(() => {
    if (isToday && pendingNow.current) {
      pendingNow.current = false;
      requestAnimationFrame(() => requestAnimationFrame(scrollToNowCard));
    }
  }, [isToday, items, scrollToNowCard]);

  /* Built once and mounted in whichever container this screen size calls for —
     the panel on a phone, the canvas on a desktop. Same element either way, so
     the two paths cannot drift apart.

     `key` is the whole of "open another record while one is open": without it
     React reuses the mounted sheet, and a sheet's text is state seeded once at
     mount — so the second record would open showing the first record's writing. */
  /* ── WHAT A SECOND MOUNT REOPENS ON ──────────────────────────────────────
     `compose.initial` is the record as it stood when it was opened — a snapshot
     — and the sheet seeds its text from it once, at mount. The sheet mounts
     TWICE, though: it is drawn inside the panel without a canvas and out on the
     canvas with one, two different places in the tree, so crossing that width
     unmounts and remounts it. Seeded from the snapshot again, its first commit
     wrote the opening text back over everything written since — measured in the
     browser on 2026-08-11: a resize with a record open reverted an embed removal
     and a paragraph, in storage. A record opened with "+" fared worse: with no
     snapshot at all the sheet minted a second uid and the day ended up with two.

     `liveRef` is what the sheet last committed, which is the record as it
     actually stands. It is null until that first commit and it is cleared by
     dropIfBlank on every path that opens a record, so it can only ever belong to
     the sheet on screen — and md-record.ts checks the uid anyway, because the
     one thing worse than stale writing is another record's writing.

     The day follows the record for the same reason: a record moved to Thursday
     by the When picker must not reopen on the day it was opened from. */
  const composeCommitted = compose ? liveRef.current : null;
  const composeInitial = window.mdRecordLatest
    ? window.mdRecordLatest(compose && compose.initial, composeCommitted && composeCommitted.rec)
    : (compose && compose.initial);
  const composeOnLive = !!composeCommitted && composeInitial === composeCommitted.rec;
  const composeDayOffset = composeOnLive && Number.isFinite(composeCommitted.offset) ? composeCommitted.offset
    : compose && Number.isFinite(compose.dayOffset) ? compose.dayOffset : viewOffset;
  const composeSourceDay = composeOnLive ? composeCommitted.dayKey
    : compose && compose.sourceDay ? compose.sourceDay : mdDayKey(composeDayOffset);
  const activeGridCycle = edit ? gridCycleFor(edit.dayOffset) : null;
  const composeSheet = compose && ComposeSheet ?
    <ComposeSheet key={compose.seq} accent={accent} light={light}
      initial={composeInitial} fresh={!!compose.fresh}
      initialPid={compose.pid || (compose.backlog && compose.backlog.pid) || null}
      backlog={!!compose.backlog} onBacklogSchedule={scheduleProjectBacklogRecord}
      initialStatus={(compose.backlog && compose.backlog.status) || null}
      onStatusSeed={setBacklogStatusSeed}
      project={compose.project || null} onProjectGone={closeCompose}
      timeBinding={compose.timeBinding} dayOffset={composeDayOffset} nowMin={nowMin}
      showAnchors={true} weekStart="Sat"
      wikiRecords={wikiCatalog.rows} wikiBacklinksFor={wikiBacklinksFor}
      onWikiOpen={openWikiTarget} onWikiCreate={createWikiRecord}
      tagOptions={tagCatalog}
      bookRefKey={composeInitial && window.mdRecordRefKey(window.mdRecordRefFor(composeInitial, composeSourceDay))}
      initialDone={composeInitial ? !!completed[composeSourceDay + '|' + composeInitial.uid] : false}
      /* The properties band's status row writes through the SAME door the board
         does — the word, the tick, and the state re-read from storage rather
         than patched. A second writer racing the flush below is how a change
         lands, shows for a moment, and quietly un-happens (CS-7). */
      onRecordStatus={setRecordStatus}
      onLive={commitLive}
      /* Just the payload close (N-9): the sheet is a scene entry now, so its
         dismissal walks the journal, the walk's popstate reveals what truly
         stands (Tabs included — fromHistory shows it), and a cold deep
         link's dismissal clears the hash through the zero-walk reconcile. The old
         hand-retire and its unconditional Tabs summon are gone. */
      onClose={closeCompose}
      onDiscard={discardCompose} /> : null;

  /* ── THE PEEK — app/md-peek.ts ───────────────────────────────────────────
     Folding the day away used to be a commitment. Leaning the pointer into the
     edge it went behind brings it back for as long as you are looking at it,
     and moving away takes it back. The rules live in the module; what is
     decided HERE is what the module cannot know:

       enabled     a pointer that hovers (`fine`, md-layout's one answer) and a
                   day that is actually folded away. There is no setting.
       suppressed  the record sheet stands. The sentence you are in the
                   middle of outranks the glance you might have wanted.
       sheetOpen   everything the day opens FULL-SCREEN INSIDE ITSELF. This
                   used to be a named list of seven booleans; it is read off
                   the standing scene now (N-10) — a surface states
                   `holdsPeek` or `suppressesPeek` in its registry row, and
                   the riders N-9 seated (the confirm, the hour grid) answer
                   through the same door as the windows.
       covered     a WINDOW stands in the workspace, over the day rather than
                   inside it. It cancels one clause of the retract's hold —
                   the caret's — because a caret left in a covered day is
                   where you were, not where you are. */
  const panelRef = useRef(null);
  const pillRef = useRef(null);
  const scenePeek = window.mdScenePeek(scene);
  const { peeking, close: closePeek } = useMdPeek({
    enabled: L.fine && canvasCollapsed,
    panelRef, pillRef,
    sheetOpen: scenePeek.held,
    suppressed: scenePeek.suppressed,
    covered: scenePeek.covered,
  });
  /* The two questions the panel is actually drawn from, and they are not the
     same question. FOLDED is "the day is not on screen" — a peeked day is on
     screen, so it is not folded, whatever the setting says. FLOATS is "this
     panel draws its own edges". Everything below spends one of these; the
     setting itself is spent only where the SETTING is the subject.

     FLOATS USED TO NAME ITS CASES — `L.overlay || peeking` — and naming them
     was the defect. A docked desktop panel was in neither list, so it stayed an
     in-flow flex item: `width: folded ? 0 : L.panel` beside a board taking
     `flex: 1`. Folding the day did not uncover the board, it RESIZED it, and
     since the engine keeps its scrollX across a resize the whole drawing slid
     420px left with the container's edge. A drawing surface that moves when you
     open a panel is the one thing it must never do.

     So the question is asked of the board instead of of the device: if there is
     a board, the day floats on top of it. That subsumes both old cases —
     `L.overlay` implies `L.canvas`, and a peek implies `panelCollapsed` which is
     itself `L.canvas && sidebarCollapsed` — and adds the one that was missing. */
  const folded = panelCollapsed && !peeking;
  /* Tabs children keep the document. Only actual editors/workspace windows
     lock it. Which is which is no longer decided here: each surface states
     `phonePage` or `locksScroll` in its registry row, and the one utility
     reads them off the standing scene (N-11). A catalogue and its selected
     window still coexist — the catalogue remains the document page beneath
     while only the window locks — because the answer is a set, not a mode. */
  usePhoneSurfaceScroll(scene, L.phone);
  const floats = L.canvas;
  /* The desk is mounted and waiting, but the day is over it. Painted it would
     read straight THROUGH the phone day, whose material is 0.46 in light and
     transparent in dark — the day is legible because the ambient Background is
     behind it, not because it is opaque.

     ALL THREE, and the engine is the reason for the first. `visibility`
     inherits, so a descendant may re-assert it, and Excalidraw ships exactly
     that (`.ToolIcon_type_button--show{visibility:visible}`) on the edit,
     duplicate and delete buttons of the very row this app moves to the corner:
     a visibility-only park leaves three icons floating over the day with no
     pane around them. `display:none` is not the answer either — a zero-size
     container makes the engine re-measure on reveal, and the drawing travels,
     which is the whole of tests/board-still.test.js. */
  const deskParked = L.phone && !folded;
  /* Which way is OUT. The panel leaves by the edge it is docked at, and in
     Arabic that is the other one — a hard-coded -100% would send the day across
     the screen in front of the desk instead of off the edge behind it. */
  const rtl = typeof document !== 'undefined' && document.documentElement.dir === 'rtl';
  /* Putting the day away means two different things, and they must not share a
     flag. On a desk it is a PREFERENCE: it persists, and the day stays away
     until you say otherwise. On a phone it is a PLACE: the journal records the
     step onto the desk, and Back walks off it. */
  const foldToDesk = useCallback(() => {
    /* Retracting first matters: the panel must stop being a peek before it
       becomes a docked panel, or the two states argue for a frame. */
    if (peeking) { closePeek(); setSidebarCollapsed(false); return; }
    if (L.phone) { showDesk(); return; }
    setSidebarCollapsed(true);
  }, [peeking, closePeek, L.phone]);
  const unfoldToDay = useCallback(() => {
    if (L.phone) { setDeskShown(false); return; }
    setSidebarCollapsed(false);
  }, [L.phone]);
  /* Tabs asks for the fold when it opens a desk on an upright tablet — the one
     layout where the destination is already drawn underneath the panel, so the
     only thing a tap on a Desk row can mean is "get the day out of its way".
     It is an event rather than a prop because Tabs is portalled and does not
     hold the day's state; the fold has one owner and this is it. */
  useEffect(() => {
    const fold = () => foldToDesk();
    window.addEventListener('mddayfold', fold);
    return () => window.removeEventListener('mddayfold', fold);
  }, [foldToDesk]);

  return (
    /* The window: one ambient backdrop, then the PANEL, then the CANVAS.
       On an upright tablet the canvas is full-bleed and the panel becomes an
       inset sheet over it — and so it does for a peek, where a folded day is
       drawn back out over the board by the pointer. Nothing inside the panel
       changes composition in either case. */
    <div style={{ position: 'relative', height: L.phone ? 'auto' : '100%',
      minHeight: L.phone ? '100lvh' : undefined, overflow: L.phone ? 'visible' : 'hidden', isolation: 'isolate',
      fontFamily: 'var(--md-body)', display: 'flex',
      /* Centred only when the panel IS the window and nothing is behind it. A
         phone with a desk under it has to start at the leading edge like every
         other layout with one: the fold is `width: 0`, and a centred flex item
         does not clip on the way to zero — it slides half a screen sideways. */
      justifyContent: deskUnder ? 'flex-start' : 'center' }}>
      {/* the backdrop spans the whole window, so the panel and the canvas sit on
          one continuous surface rather than looking like two apps side by side */}
      <Background light={light} accent={accent} level={5} gradient={t.bgGradient} blackness={t.bgBlackness}
        fixed={L.phone} />

      {/* ════ THE PANEL — the app, exactly as it is on a phone ════
          Only this outer clip changes width. The app inside remains a 420px
          surface throughout the motion, so cards and text never compress on the
          way out; the board simply takes over the space the clip releases. */}
      <div id="md-day-sidebar" ref={panelRef}
        aria-hidden={folded} inert={folded ? '' : undefined}
        /* THE SHEET SAYS IT HAS ALREADY PAID, and index.html hands the zeroed
           vocabulary to its children — the day and Tabs both, which is the
           whole reason it is a mark here and not an inline style down there.
           See the rule; it carries the reasons. */
        data-md-panel-floats={floats ? 'true' : undefined}
        style={{ width: folded ? 0 : L.panel, flexShrink: 0,
        /* Absolute the moment it floats, and that is what keeps the board
           still: the space a peek occupies was already zero in this flex row,
           so the canvas beside it is never re-measured for a glance. Nothing
           new animates either — the reveal is the same clip the fold already
           runs, which is why "reduce motion" already covers it. */
        position: floats ? 'absolute' : 'relative',
        top: floats ? 'var(--md-top-inset)' : 'auto',
        bottom: floats ? 'var(--md-bottom-inset)' : 'auto',
        insetInlineStart: floats ? 'max(var(--md-safe-inline), 14px)' : 'auto',
        /* Over the board and everything the board pins to itself (20 at the
           most), under the corner the folded status owns (94). Every layout
           with a board needs this now, not just the tablet's: a docked panel
           used to be in flow, where `auto` was enough because nothing was
           underneath it. Something is underneath it now. */
        zIndex: peeking ? 93 : deskUnder ? 32 : 'auto',
        height: L.phone ? 'auto' : floats ? 'auto' : '100%',
        minHeight: L.phone ? '100lvh' : undefined,
        overflow: L.phone ? 'visible' : 'hidden', opacity: folded ? 0 : 1,
        /* AND VISIBILITY, because the day's own header is `position:fixed` and
           WebKit lifts a fixed box into a composited layer the ancestor's
           opacity never reaches. That cost nothing while the desk was a modal
           portalled above the day; the desk is ground beneath it now, and a
           folded panel drew its full prayer countdown across a live drawing.
           `visibility` inherits and is resolved at style time, so no descendant
           escapes it. */
        visibility: folded ? 'hidden' : 'visible',
        pointerEvents: folded ? 'none' : 'auto',
        borderRadius: floats ? 'var(--md-radius-sheet)' : 0,
        /* NO FILL HERE, and that is the whole of a bug Marwan found on
           2026-08-28. This sheet is SHARED: the day is one occupant of it and
           Tabs is another, and the material used to live here, on the landlord.
           Tabs is `absolute; inset:0` at 0.76, so with the day painting
           underneath the two composited and Tabs read greyer than the identical
           Projects window; the cure was to blank this fill while Tabs was
           MOUNTED. But a window hides Tabs without unmounting it, and the sheet
           then shows the day — with no material at all. Reload with a window
           open, lean on the edge, and the desk was visible straight through the
           day until you opened Search and came back.
           The material belongs to whoever is being looked at, so it moved down
           to the day's own surface, and Tabs REPLACES the day rather than
           painting over it. This box keeps what is genuinely the sheet's: its
           radius, its edge and its shadow. */
        /* The hairline down the docked column's trailing edge is gone with the
           column: `!floats && L.canvas` is a condition that can no longer be
           true, because a canvas is now exactly when the panel floats. A sheet
           draws all four of its edges, which is the `border` above. */
        border: floats && !folded ? `0.75px solid ${ink(light ? 0.1 : 0.14)}` : 'none',
        boxShadow: floats && !folded
          ? (light ? '0 24px 70px rgba(32,48,82,0.18), 0 2px 14px rgba(32,48,82,0.10)'
            : '0 28px 80px rgba(0,0,0,0.46), 0 2px 18px rgba(0,0,0,0.34)')
          : 'none',
        /* HIDING WAITS OUT THE MOTION; showing takes effect on the first frame.
           Both numbers here used to be 180ms, because a fade was all there was.
           The surface below now SLIDES for 340ms, and the two things that make
           the panel stop existing have to land after it rather than halfway
           through: the fade is delayed so that it ends with the motion, and
           `visibility` — the only thing that reaches the day's fixed, blurred
           header — flips when the motion is over. */
        transition: `width .34s cubic-bezier(.22,.61,.36,1), opacity .2s ease ${folded ? '.14s' : '0s'}, visibility 0s linear ${folded ? '.34s' : '0s'}` }}>
      {/* SPENDING THE SAFE AREA MEANS ZEROING IT FOR EVERYTHING INSIDE — and
          that is now the sheet's `data-md-panel-floats` mark above, answered by
          one rule in index.html against the sheet's CHILDREN. It used to be an
          inline style on the surface below, under a comment claiming it reached
          everything inside the panel; it reached the day's descendants only,
          and Tabs is the sheet's other occupant, portalled in beside the day.
          Ruling 4 of the safe-edge round, made structural: whoever draws the
          edge pays, once. */}
      <div data-md-day-surface="true"
        style={{ position: 'relative', width: L.panel, height: L.phone ? 'auto' : '100%',
        minHeight: L.phone ? '100lvh' : undefined, overflow: L.phone ? 'visible' : 'hidden',
        /* ONE MATERIAL, and no flag to pick it with. The day stands on the app's
           own ground — the same token the shell publishes from the gradient and
           blackness Background draws with, so the day and the void cannot
           disagree at any position of the slider — and it does NOT blur what it
           covers. The upright tablet's sheet and the peek used to be glass, and
           being glass is what made the hero card's 0.72 composite over the
           whiteboard instead of over the void. The expanded desktop was the one
           that looked right, so the expanded desktop is the style.
           A PHONE has no board to stand on and keeps main's values exactly: it
           is in flow over the Background, which is the ground, already.
           DECLARED ON THE DAY, not on the sheet above: the sheet is shared with
           Tabs, and a material on a shared host is a material the wrong
           occupant can be left holding. See the note up there. */
        background: floats
          ? (light ? `linear-gradient(rgba(235,238,242,0.46), rgba(235,238,242,0.46)), ${mdBackdropCSS()}` : mdBackdropCSS())
          : (light ? 'rgba(235,238,242,0.46)' : 'transparent'),
        /* ── AND THIS IS THE PART THAT MOVES ─────────────────────────────────
           The clip above releases the SPACE; this releases the view. Without
           it the fold is a wipe on a desktop — the column eaten from its
           trailing edge while its contents stand still — and on a phone it is
           nothing at all, because the panel is `overflow: visible` there and a
           width of zero hides nothing: the day simply blinked out.
           A PERCENTAGE OF THIS BOX, never of the clip. This one is fixed at the
           panel's width — one full column, one full screen on a phone — while
           the clip's is mid-animation and would give a distance that shrinks as
           it is being used. */
        transform: folded ? `translateX(${rtl ? '' : '-'}100%)` : 'none',
        transition: 'transform .34s cubic-bezier(.22,.61,.36,1)',
        /* The side edges, for the one shape that has any: a phone turned
           sideways, where the camera housing eats into the leading edge. The
           day's own 16px gutters are narrower than that, so the column narrows
           rather than being read from underneath a piece of hardware. Zero on
           every other device, which is every other device. Paid here, not on
           the panel itself, so the panel's own inset arithmetic is untouched —
           and only when the panel is flush, because a floating one has already
           been zeroed by the sheet's mark. Spending it here is safe for the
           same reason: the two branches are exclusive, so this box never both
           spends a token and is handed a zero for it. */
        ...(floats ? null : { paddingInline: 'var(--md-safe-inline)' }) }}>
      {!light && <div style={{ position: L.phone ? 'fixed' : 'absolute', top: 0, left: 0, right: 0, height: 120, pointerEvents: 'none',
        background: 'linear-gradient(180deg, rgba(6,6,8,0.45), rgba(6,6,8,0))' }} />}

      {/* scroll area */}
      <main ref={scrollRef} className="md-scroll" aria-label="Your day" style={{
        position: L.phone ? 'relative' : 'absolute', inset: L.phone ? undefined : 0,
        minHeight: L.phone ? '100lvh' : undefined,
        overflowY: L.phone ? 'visible' : 'auto', overflowX: 'hidden',
        paddingTop: 60, WebkitOverflowScrolling: 'touch' }}>
        <MDBoundary resetKey={viewOffset} fallback={
          <div style={{ padding: '48px 24px', paddingTop: todayTopPad + 32, textAlign: 'center' }}>
            <div style={{ color: ink(0.8), fontSize: 16, fontWeight: 650, marginBottom: 8 }}>
              This day could not be displayed
            </div>
            <p style={{ color: ink(0.5), fontSize: 13.5, lineHeight: 1.6, margin: '0 0 18px' }}>
              Something in the records for this day is unreadable. The rest of the app still works —
              your other days are unaffected, and Settings has Export and Sign out.
            </p>
            <button onClick={() => setViewOffset(0)}
              style={{ padding: '10px 18px', borderRadius: 12, border: `1px solid ${ink(0.14)}`,
                background: 'transparent', color: ink(0.75), fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              Go to today
            </button>
          </div>}>
        <TodayScreen accent={accent} items={items} nowSec={nowSec} nowMin={timelineNowMin}
        next={next} nextTimelineKey={nextTimelineKey} closingFajr={timeline.closingFajr}
        prayed={isToday ? prayed : {}} reminders={reminders} fine={L.fine}
        sizeByDur={true} pxPerMin={1.6} topPad={todayTopPad}
        isToday={isToday} dayOffset={viewOffset} timelineAnchorOffset={cycle.anchorOffset} overlapLabel="window"
        done={completed} onToggleDone={toggleCompleted}
        allDayRecords={allDayRecords[mdDayKey(viewOffset)] || []} onEditRecord={editRecord}
        onDeleteItem={requestDeleteItem} onRescheduleItem={rescheduleItem}
        onOpenEdit={openEdit} />
        </MDBoundary>
      </main>

      {/* pinned Today header (date + countdown card).

          The board switcher used to be a CARD under the hero. It is a row in the
          hero's own ⋯ menu now — the day column is the day, and the one row in
          it that was about the other half of the screen has gone to live with
          the app's other doors. Still canvas-only: switching a board you cannot
          see does nothing you can watch happen. */}
      <TodayHeader innerRef={hdrRef} accent={accent} next={next} nowSec={nowSec}
      selectedOffset={viewOffset} onSelectDay={selectDay}
      onSwipeWeek={swipeWeek} onSettings={() => goFind({ go: 'settings' })}
      onFind={() => window.dispatchEvent(new CustomEvent('mdfindopen'))}
      onProjects={() => goFind({ go: 'workshop' })}
      onDatabases={() => goFind({ go: 'databases' })}
      onCrafts={() => goFind({ go: 'crafts' })}
      /* Geometry follows the float: the sheet pays the top inset now, so the
         header must not pay it twice. Colour is not passed at all any more —
         there is one frost, because there is one sheet. */
      insetSafeArea={!floats}
      /* Offered on every layout with a desk beneath the day, which is every
         layout now: a phone's first press mounts the desk and steps onto it,
         and every press after that is the fold it already was. */
      sidebarToggle={deskUnder && !folded ?
        <SidebarToggle collapsed={false} pin={peeking} onToggle={foldToDesk} /> : null}
      boards={L.canvas ? boards : null} openBoardId={boardId}
      onOpenBoard={L.canvas ? openBoard : undefined}
      onRenameBoard={renameBoard} onDeleteBoard={deleteBoard}
      viewportFixed={L.phone} />

      {/* With a canvas beside it the "+" lives out on the board, so the Now pill
          drops into the corner it used to sit above. While the hour grid is open the
          pill is re-aimed at the grid's day and lifted above the grid layer, so
          back-to-today works without closing the grid first. The extra 11px inset
          centres the 38px circle over the FAB: the 60px disc sits at end 22, so its
          centre axis is 52 from the edge; half of 38 is 19, and 52 − 19 = 33. */}
      <NowButton accent={accent} onClick={goToNow}
        show={edit ? edit.dayOffset !== 0 : nowBtn.show}
        dir={edit ? (edit.dayOffset < 0 ? 'down' : 'up') : nowBtn.dir}
        above={!!edit}
        inset={L.canvas && !edit ? 0 : 11}
        viewportFixed={L.phone}
        /* Keep the 12px relationship above the phone FAB as its resting gutter
           tightens from 28 to 16. The device inset is still the authority in
           installed mode; calc() merely adds the two controls' fixed spacing. */
        bottom={L.canvas && !edit ? 'max(var(--md-safe-bottom), 28px)'
          : L.phone ? 'calc(max(var(--md-safe-bottom), 16px) + 72px)' : 100} />
      {!L.canvas && <AddButton accent={accent} onClick={openCompose} viewportFixed={L.phone} />}

      {/* ── Projects — the Workshop. See app/md-projects.jsx.

             It is written here but it does not live here: it portals to the
             body and opens over the BOARD, the way the Databases window does,
             because what you are building is the thing you consult WHILE
             working. Mounting it inside the panel would clip it. The panel is
             still where it is CONDITIONED, so closing the panel closes it.

             The counting state is handed IN rather than re-read: md-app already
             holds every day, every completion and the board list, and the row
             pipeline is pure so the arithmetic stays provable. ── */}
      {projectsOpen && window.ProjectsSpace &&
      <window.ProjectsSpace accent={accent}
        onClose={() => dismissWorkspaceSurface('workshop')}
        initialView={projectsView}
        events={dayEvents} records={allDayRecords} projectRecords={projectRecords}
        boards={boards} completed={completed}
        todayKey={mdDayKey(0)} nowMin={Math.floor(nowSec / 60)}
        onNewPage={composeForProject}
        onOpenDayRecord={editRecord}
        onOpenBoard={openScopedBoard} onNewBoard={newScopedBoard}
        onOpenProject={openProjectWindow}
        wikiRecords={wikiCatalog.rows} wikiBacklinksFor={wikiBacklinksFor}
        onWikiOpen={openWikiTarget} onWikiCreate={createWikiRecord}
        tagOptions={tagCatalog} />}

      {/* ── The Project window, singular. See app/md-project-space.jsx.
             IT STANDS ON PROJECTS RATHER THAN REPLACING IT: rendered after, at
             z 145 — above the Workshop's 140, below DbFullScreen's 150 — and
             conditioned only on having a project, so the window underneath keeps
             its scroll, its view and its stack while this one is up. Escape
             closes this one first (the escape layers are LIFO), which is what
             "step back out of the project" has to mean.

             It is handed the SAME counting state the Workshop gets, from the
             same source, so a record cannot say one thing in one window and
             something else in the other. ── */}
      {/* The window is conditioned on the project alone: the stack is the
           provenance, so a project that arrived from search stands with no
           Workshop beneath — and its dismissal walks back to exactly where it was
           opened. The sticky `fromTabs` flag this close used to fork on died
           with N-5. */}
      {projectWindowId && window.ProjectSpace &&
      <window.ProjectSpace accent={accent} projectId={projectWindowId}
        onClose={() => dismissWorkspaceSurface('project')}
        initialFrame={projectWindowFrame}
        events={dayEvents} records={allDayRecords} projectRecords={projectRecords}
        boards={boards} completed={completed}
        todayKey={mdDayKey(0)} nowMin={Math.floor(nowSec / 60)}
        onOpenDayRecord={editRecord} onRecordStatus={setRecordStatus}
        onOpenBacklog={editProjectBacklogRecord} onComposeBacklog={composeProjectBacklog}
        onScheduleBacklog={scheduleProjectBacklogRecord}
        onOpenBoard={openScopedBoard} onNewBoard={newScopedBoard}
        onOpenPursuit={(id) => openWorkshopPage('pursuit', id)}
        onOpenDomain={(id) => openWorkshopPage('domain', id)}
        wikiRecords={wikiCatalog.rows} wikiBacklinksFor={wikiBacklinksFor}
        onWikiOpen={openWikiTarget} onWikiCreate={createWikiRecord}
        tagOptions={tagCatalog} />}

      {craftsMounted.current && window.CraftSpace && mdPhonePanelPortal(
      <window.CraftSpace accent={accent} initial={craftsTarget} active={craftsOpen}
        phoneDocument={L.phone}
        onClose={() => { setCraftsTarget(null); dismissPanelScreen('crafts'); }}
        onView={(row) => recordVisit({ go: 'crafts', id: row.instance })}
        onPlace={boardId ? placeManifest : null} />, L.phone)}

      {/* ── Databases — the Main DB and every database beside it. See
             app/md-databases.jsx. Nothing is handed in: unlike Projects, this
             reads through the engine's own list, which is the same read the AI
             Organizer will make, so a second copy here could disagree with it. ── */}
      {databasesOpen && window.DatabaseSpace &&
      <window.DatabaseSpace accent={accent} initial={databasesTarget}
        onOpenDatabase={(id) => { leaveProjects(); pushVisit({ go: 'database', id }); }}
        onWindowOpenChange={seatDbWindow}
        catalogueStanding={databasesTarget ? null : catalogueStanding}
        onCatalogueClose={databasesTarget ? null
          : () => setScene((s) => window.mdSceneDismiss(s, 'catalogue'))}
        closeInitialToParent={!!databasesTarget}
        key={databasesTarget ? `find-${databasesTarget.id}-${databasesTarget.row || ''}` : 'space'}
        onClose={() => { setDatabaseWindowOpen(false);
          /* The catalogue is a pane-screen since it stopped occupying the
             workspace slot — it leaves through the pane-screen door, which
             asks the reveal with the docked fact. Through the window door
             its × over a standing window left Tabs hidden beside it. */
          if (window.mdSceneIsOpen(sceneRef.current, 'database')) dismissWorkspaceSurface('database');
          else dismissPanelScreen('catalogue'); }} />}

      {/* ── a scanned pairing code, if this boot arrived with one. Always
             mounted and almost always nothing: it reads the address once and
             draws only when a code is in it. No state here, because the answer
             to "is another screen asking to be signed in" is in the URL and
             nowhere else. ── */}
      {window.MdPairApproval && <window.MdPairApproval accent={accent} />}

      {/* ── the Finder — always mounted, because the ⌘K listener lives in it.
             Boards are canvas-only here for the same reason they are in the ⋯
             menu: opening a board you cannot see does nothing you can watch
             happen. ── */}
      {window.MdFinder &&
      <window.MdFinder accent={accent} canBoards={true} onGo={goFind}
        standing={window.mdSceneIsOpen(scene, 'tabs')}
        onStand={tabsStand} onDismiss={tabsLeave}
        covered={tabsBootCovered.current}
        recordCtx={boardCtx}
        /* The same question the day's header asks, because it is the same
           countdown. Tabs was gated on `L.canvas` for a written reason — a
           phone's Tabs is its own full-screen page, so folding from here would
           put the desk under a Tabs that was still standing, with
           mdSceneDeskShroud's opaque guard raised over a day it was no longer
           covering. Every clause of that was fixed on 2026-08-28: the shroud
           answers 'bare' on a phone, the handoff backstop stands only while
           Tabs is looked at, and a hidden phone Tabs leaves layout entirely.
           The gate outlived its reason and read as a missing button. */
        sidebarToggle={deskUnder && !folded
          ? <SidebarToggle collapsed={false} pin={peeking} onToggle={foldToDesk} /> : null}
        onBoards={() => setDesksOpen(true)} next={next} nowSec={nowSec} />}

      {/* Explicit phone/tablet board stack. More remains mounted underneath, so
          closing a board returns to the preserved chooser and search state. */}
      {desksOpen && window.BoardList && mdPhonePanelPortal(
      <div role="dialog" aria-modal="true" aria-label="Desks"
        data-md-panel-screen="atomic"
        data-md-phone-document-screen={L.phone ? 'true' : undefined}
        style={{ position: 'absolute', inset: 0,
        zIndex: 280, padding: 'var(--md-top-inset) 14px var(--md-bottom-inset)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        background: light ? 'rgba(248,249,250,.78)' : 'rgba(8,9,12,.99)', color: ink(.9),
        backdropFilter: 'blur(30px) saturate(125%)', WebkitBackdropFilter: 'blur(30px) saturate(125%)' }}>
        <div style={{ display: 'flex', alignItems: 'center', minHeight: 48, marginBottom: 8 }}>
          <span data-md-title-icon="desks" style={{ display: 'grid', placeItems: 'center',
            marginInlineStart: 8, flex: 'none' }}>{MDIApp.desk(rgbaApp(accent, 1), 22)}</span>
          <strong style={{ flex: 1, fontSize: 20, marginInlineStart: 8 }}>Desks</strong>
          {/* One door (N-9): the chooser is a scene entry, so closing the
              layer walks the journal and the walk reveals what truly stands —
              Tabs hidden beneath comes back through fromHistory, the day
              stays the day, and the routed hash clears with the entry. */}
          <window.MdDismiss label="Close Desks" onClick={() => setDesksOpen(false)}
            style={{ marginInlineEnd: -6 }} />
        </div>
        <window.BoardList accent={accent} boards={boards} openId={boardId}
          onOpen={chooseBoard} onRename={renameBoard} onDelete={deleteBoard}
          onCreate={createChosenBoard} documentScroll={L.phone} />
      </div>, L.phone)}

      {/* ── Settings — invented surface, see app/md-settings.jsx ── */}
      {settingsOpen && mdPhonePanelPortal(
      <SettingsSheet accent={accent} phoneDocument={L.phone} onClose={() => {
        /* The dismissal reveals what the scene says stands beneath — Tabs when Tabs
           does (M-2), the day otherwise. The old comment here forbade walking
           back to Tabs at all (D4); that prohibition existed because the walk
           was a guess, and it retired with the guessing. */
        dismissPanelScreen('settings'); }}
        initialView={settingsView} onView={visitSettingView}
        onLocation={applyLocation} onCalc={applyCalc} onHijriAdjust={applyHijriAdjust}
        theme={theme} onTheme={chooseTheme}
        deskId={boardId}
        onAuthChange={() => setLocStamp((n) => n + 1)} onSignedOut={onSignedOut} />, L.phone)}

      {/* ── Calendar Grid edit view — overlays the list; morphs in/out ── */}
      {edit &&
      <div ref={gridLayerRef} data-md-phone-viewport-layer="true"
        style={{ position: 'absolute', inset: 0, zIndex: 60 }}>
          {/* opaque backdrop that fades in to cover the list while editing */}
          <div ref={gridBgRef} style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
            <Background light={light} accent={accent} level={5} gradient={t.bgGradient} blackness={t.bgBlackness} />
            {!light && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 120,
            background: 'linear-gradient(180deg, rgba(6,6,8,0.45), rgba(6,6,8,0))' }} />}
          </div>
          <div style={{ position: 'absolute', inset: 0, zIndex: 1 }}>
            <GridView key={'grid-' + edit.dayOffset + '-' + (edit.key || 0)} accent={accent}
          eventsInit={activeGridCycle.events}
          prayers={activeGridCycle.prayers}
          gridStart={activeGridCycle.startMin} gridEnd={activeGridCycle.endMin}
          nowSec={cycle.nowMin * 60} isToday={edit.dayOffset === cycle.anchorOffset} dayOffset={edit.dayOffset}
          focalMin={edit.focalMin} phase={edit.phase}
          morph={{ origin: edit.origin, focal: edit.focal }}
          registerExit={(fn) => {exitFnRef.current = fn;}}
          registerApi={registerGridApi}
          onComposeRecord={composeForGrid}
          onScheduleRecord={scheduleFromTray}
          onTapBlock={editGridRecord}
          unscheduled={allDayRecords[mdDayKey(edit.dayOffset)] || []}
          onSwitchDay={switchEditDay}
          onClose={closeEdit} />
          </div>
        </div>}

      {/* ── Add / Edit Record — the compose sheet ───────────────────────────
             ONE mount, and it is not really here: the sheet portals itself onto
             <body>, above every window, in the rectangle its own ladder gives
             it (md-compose.jsx, md-sheet-size.ts). It used to be mounted twice
             — once in this panel and once in the board below — with `!L.canvas`
             choosing, which is why it had no size of its own and why a record
             opened over a window opened BEHIND it. Rendered from here so it
             lives beside the day's state, and drawn wherever it belongs. ── */}
      {compose && ComposeSheet && composeSheet}

      {/* ── Reschedule sheet (timeline swipe → date·time picker) ── */}
      {reschedule && window.WhenPicker &&
      <RescheduleSheet accent={accent} light={light} item={reschedule.item} dayOffset={reschedule.sourceOffset} nowMin={nowMin}
      showAnchors={true} weekStart="Sat"
      onCommit={(sched) => moveItem(reschedule.item, sched, reschedule.sourceOffset, reschedule.sourceDay)} onClose={() => setReschedule(null)} />}

      {/* ── Delete confirmation (timeline swipe → Delete) ── */}
      {confirm && ConfirmDialog &&
      <ConfirmDialog light={light} accent={accent} title={confirm.title} message={confirm.message}
      onCancel={() => setConfirm(null)} onConfirm={confirm.onConfirm} />}

      {/* transient toast (undo) */}
      {toast &&
      <div style={{ position: L.phone ? 'fixed' : 'absolute', left: 0, right: 0, bottom: 172, display: 'flex', justifyContent: 'center', zIndex: 85, pointerEvents: 'none' }}>
          <div style={{ pointerEvents: 'auto', display: 'inline-flex', alignItems: 'center', gap: 14, padding: '9px 12px 9px 16px', borderRadius: 999,
          background: light ? 'rgba(28,30,38,0.94)' : 'rgba(255,255,255,0.95)', color: light ? '#fff' : '#15171d',
          backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)', fontSize: 13.5, fontWeight: 600, animation: 'mdSlotIn .2s ease' }}>
          {toast.msg}
          {toast.undo && <button onClick={toast.undo} style={{ background: 'none', border: 'none', cursor: 'pointer', color: rgbaApp(accent, 1), fontSize: 13.5, fontWeight: 800, padding: 0 }}>Undo</button>}
          </div>
        </div>}

      </div>
      </div>{/* ════ end panel ════ */}

      {/* ════ THE BOARD SURFACE ════
             The space this module reserved from the start. It held one line of
             grey text — "it is reserved, not unfinished" — and it holds the
             board now: full-bleed beneath every window the app opens, the day
             included.

             THE GEOMETRY HAS NO CONDITION ON IT, and that is the point. It used
             to read `L.overlay ? absolute/inset:0 : relative/flex:1` — full-bleed
             for an upright tablet, and the leftover strip beside the day
             everywhere else. The leftover strip is what moved: `flex: 1` is a
             width computed from the panel's, so folding the day re-measured the
             board and the engine, holding its scrollX, redrew every shape 420px
             to the left. A board that is always the whole window is never
             re-measured, so there is no arithmetic left for a fold to change.

             A record still opens out here, and it opens ON TOP of the board
             rather than in place of it. That was a choice between three: split
             the canvas, hide the board, or float. Splitting gives both halves
             too little at the 560pt minimum this layout allows, and hiding
             would mean the board is not open by default after all. Floating
             also happens to be the shape the next iteration needs, when a record
             can be pinned to the board rather than merely shown above it. */}
      {(L.canvas || (L.phone && deskGround)) &&
      <div className={L.overlay
        ? (panelCollapsed ? 'md-board-stage-status' : 'md-board-stage-muted')
        : undefined}
        /* THE PHONE'S ANSWER, PUBLISHED — never `.excalidraw--mobile`, which is
           the ENGINE's answer to a different question ("is this container under
           730px") and is also true of a desktop window dragged narrow. A narrow
           desktop window has a pointer, a keyboard and no thumb. md-layout says
           what this is, once; the stylesheet reads it here for the desk's real
           viewport geometry and for the chrome it swaps end for end. */
        data-md-desk-phone={L.phone ? 'true' : undefined}
        aria-hidden={deskParked || undefined} inert={deskParked ? '' : undefined}
        style={{ position: 'absolute',
        inset: 0, width: '100%', minWidth: 0, height: '100%', overflow: 'hidden',
        /* Parked: mounted, still, and not painted. See `deskParked`. */
        opacity: deskParked ? 0 : 1,
        visibility: deskParked ? 'hidden' : 'visible',
        pointerEvents: deskParked ? 'none' : 'auto',
        /* IT HOLDS WHILE THE DAY ARRIVES. Parking on the first frame left the
           day sliding in over the empty void with the desk already gone, which
           is the blink this whole change is about. Uncovering is the other way
           round and takes no delay at all: the day is what covers the desk, so
           the desk has to be there before the day has moved. */
        transition: `opacity .3s ease ${deskParked ? '.06s' : '0s'}, visibility 0s linear ${deskParked ? '.36s' : '0s'}`,
        /* ── where the engine's own controls may stand ────────────────────
           The canvas runs under the day; the engine's CHROME must not. Its
           toolbar is centred in this container and its zoom/history row sits in
           the bottom-leading corner — which is precisely where the day now
           stands — so the engine's UI layer is inset by this while the canvas
           beneath it is left alone. One number, published once, read by the
           stylesheet for the engine's layer and by md-board.jsx for the two
           controls of ours that stand outside it.

           IT IS KEYED ON THE SETTING, NOT ON `folded`, which is the peek's
           promise restated for chrome: a glance pulls the day out over the
           board for as long as you are looking at it, and it must not shove the
           toolbar sideways on the way past. It clears the sheet's own inset from
           the screen edge as well as its width — the same `max()` the panel is
           placed with above — plus the gap the sheet's shadow needs. */
        '--md-board-chrome-start': panelCollapsed ? '0px'
          : `calc(max(var(--md-safe-inline), 14px) + ${L.panel}px + 14px)` }}>
        {BoardCanvas && <BoardCanvas accent={accent} light={light} openId={boardId}
          registerApi={(api) => { boardApiRef.current = api; }}
          ctx={boardCtx} onOpenRecord={openPinned} onManifestRead={manifestCall}
          onAddMedia={addMediaRecord} onAddFile={addFileRecord}
          onDeleteRecord={deletePinnedRecord} onContent={setBoardHasContent}
          muted={L.overlay && !panelCollapsed}
          active={!deskParked && !(L.overlay && !panelCollapsed)
            && !compose && !edit && !projectsOpen && !settingsOpen
            && !desksOpen && !reschedule && !confirm}
          /* Not the inset itself, just a number that moves when it does: the
             board re-measures where the engine's controls ended up, and the
             stylesheet owns the arithmetic. */
          chromeStart={panelCollapsed ? 0 : L.panel} />}
        {L.canvas && !(L.overlay && !panelCollapsed && compose) &&
        <BoardFab accent={accent} onNewPage={openCompose} />}
      </div>}

      {/* ── the invitation, and when it is worth making ──────────────────────
             On an upright tablet the panel covers the board, and this is the
             only sign that there is one. It shows ONLY WHILE THE BOARD IS
             EMPTY, which sounds backwards for one second and then does not: a
             board with anything on it is already visible around and behind the
             panel, so the drawing is its own invitation, and a label repeating
             it is one more thing to read over somebody's work. An empty board
             shows nothing at all, and a surface that looks like background is
             a surface nobody opens — so that is the one case that needs a word.

             Nothing is stranded either way: the sidebar toggle in the header
             collapses the panel from any state, board full or empty. */}
      {boardHasContent === false &&
      L.overlay && !panelCollapsed && !compose && !edit && !projectsOpen &&
      !settingsOpen && !reschedule && !confirm &&
      <OpenBoardButton onClick={() => setSidebarCollapsed(true)} />}

      {/* The sidebar's only survivors sit directly on the canvas. Their 36px row
          and 16px contents match the board toolbar controls beside them. */}
      {folded && PrayerCountdown &&
      <div data-testid="floating-prayer-countdown" ref={pillRef}
        /* Fixed on a phone, where the app root is a document that scrolls and
           an absolute corner would travel up the page with it. Everywhere else
           the root is the window and absolute already means the corner. */
        style={{ position: L.phone ? 'fixed' : 'absolute',
        top: 'calc(var(--md-top-inset) + 2px)', insetInlineStart: 22,
        zIndex: 94, height: 45, display: 'flex', alignItems: 'center',
        animation: 'mdSlotIn .2s ease' }}>
        <PrayerCountdown compact accent={accent} next={next} nowSec={nowSec}
          action={<SidebarToggle collapsed onToggle={unfoldToDay} />} />
      </div>}

      {/* Tweaks */}
      <TweaksPanel>
        <TweakSection label="Look & feel" />
        <TweakRadio label="Theme" value={t.theme}
        options={['light', 'dark']} onChange={(v) => setTweak('theme', v)} />
        <TweakSection label="Cards · dark theme" />
        <TweakSlider label="Glow" value={t.cardGlow} min={0} max={1} step={0.05}
        onChange={(v) => setTweak('cardGlow', v)} />
        <TweakSlider label="Halo" value={t.cardHalo} min={0} max={0.4} step={0.02}
        onChange={(v) => setTweak('cardHalo', v)} />
        <TweakSlider label="Shadow" value={t.cardShadow} min={0} max={2.5} step={0.1} unit="×"
        onChange={(v) => setTweak('cardShadow', v)} />
        <TweakSlider label="Blur" value={t.cardBlur} min={0.4} max={3} step={0.1} unit="×"
        onChange={(v) => setTweak('cardBlur', v)} />
        <TweakSection label="Background" />
        <TweakSelect label="Gradient" value={t.bgGradient}
        options={[{ value: 'cool', label: 'Cool blue' }, { value: 'warm', label: 'Warm sand' }, { value: 'violet', label: 'Violet' }, { value: 'neutral', label: 'Neutral' }]}
        onChange={(v) => setTweak('bgGradient', v)} />
        <TweakSlider label="Blackness · dark" value={t.bgBlackness} min={0} max={1} step={0.05}
        onChange={(v) => setTweak('bgBlackness', v)} />
        <TweakSection label="Testing" />
        {/* Seeded from the real clock when unpinned, so dragging it starts from now. */}
        <NowTimeSlider value={t.nowMin ?? Math.round(mdNowSec() / 60)} onChange={(v) => setTweak('nowMin', v)} />
      </TweaksPanel>
    </div>);

}

/* ── Mount: the app owns the viewport.
   This used to wrap <App> in a drawn 393×852 iPhone bezel centred on a lavender
   page — the prototype's harness, which shipped to production and made the live
   site read as a mockup on anything bigger than a phone. The app is responsive
   now: it fills the window at every size and paints its own background, so the
   page beneath it only ever shows through during the browser's first frame.
   That one frame is why the theme is still read synchronously here. ── */
function Mount() {
  /* ── the gate ────────────────────────────────────────────────────────────
     Which of the two surfaces this visit gets. Same discipline as the theme
     below and for the same reason: read SYNCHRONOUSLY in the initialiser, from
     the device alone, because an effect runs after the first paint and would
     show somebody one surface on their way to the other.

     Then settled against the server once, in the background. app/md-gate.ts
     owns both answers and, in particular, owns the rule that a server which
     could not be REACHED has not said anything — so an installed app opened
     offline by somebody signed in opens their day, not a landing film. */
  const [gate, setGate] = useState(() => window.mdLandingGate());
  useEffect(() => {
    let live = true;
    window.mdGateSettle().then((settled) => { if (live) setGate(settled); });
    return () => { live = false; };
  }, []);

  /* ── leaving an account is a reload, and entering one is not ──────────────
     A landing page has hydrated nothing, so a sign-in can choose its room and
     open the store in place — which is what md-landing's enter() does before it
     ever calls back here.

     Going the other way is not symmetrical and cannot be. The store, the search
     engine and a dozen module-level caches are all holding ONE account's
     documents by the time somebody signs out, and none of them can be talked
     out of it. Rebuilding React's tree would leave every one of them in place.
     So the way out is the way the browser gives us: reload, and boot cold into
     the film. See the invariant at the top of app/md-boot.js. */
  const leave = useCallback(() => {
    if (window.mdReloadApp) window.mdReloadApp();
    else setGate('landing');
  }, []);

  /* ── the wire under the sync ──────────────────────────────────────────────
     The room this account's devices share, joined when the door is open and let
     go of when it closes. Everything about whether a room can be reached at all
     is md-live's own business and it answers silently: no route, no binding, no
     session, no WebSocket all leave this app exactly as capable as it was, so
     there is nothing to branch on and nothing to show. Signing out runs the
     cleanup, which is what closes the socket rather than leaving one open on a
     device that is no longer anybody's. */
  useEffect(() => {
    if (gate !== 'app') return undefined;
    if (window.mdLiveStart) window.mdLiveStart();
    return () => { if (window.mdLiveStop) window.mdLiveStop(); };
  }, [gate]);

  /* Read in the initialiser, not an effect: an effect runs AFTER the first paint,
     so a dark-theme user watched the page flash white on every load. The user's
     own choice first; the Tweaks store is a design-mode fallback. */
  const [light, setLight] = useState(() => {
    const saved = window.mdLoadTheme && window.mdLoadTheme();
    if (saved) return window.mdThemeResolve(saved, window.mdPrefersDark()) === 'light';
    /* `tweaks:` is the design panel's own namespace, not ours — hence mdReadRaw
       and a local parse rather than mdRead. It still goes through md-storage so
       that "the only module that touches localStorage" has no exceptions to
       remember. In the shipped build the Tweaks panel is a stub and this key
       never exists, so this is a design-mode path only. */
    const raw = window.mdReadRaw('tweaks:' + location.pathname);
    if (raw) {
      try { const v = JSON.parse(raw); if (v && v.theme) return v.theme !== 'dark'; } catch (e) {}
    }
    return TWEAK_DEFAULTS.theme !== 'dark';
  });
  /* The front door. Returned BEFORE <App> is ever constructed, so a signed-out
     visitor does not pay for the app's boot to look at a film. */
  if (gate === 'landing') {
    return <MdLanding onSignedIn={() => setGate('app')} />;
  }

  /* The same door, with the three questions already open on it.

     This is a page that RELOADED into a half-finished account — signed in, with
     nothing answered — and it is drawn on the landing side deliberately: the
     front door must look identical to every visitor, always, or it reveals that
     this device is holding an account somebody abandoned halfway through.

     It falls AFTER the branch above and before <App>, which is the whole of the
     fix: onboarding used to fall through to the app, so closing the tab on the
     second question opened the product next time with no name, no date of birth
     and no gender — and the questions were never asked again. */
  if (gate === 'onboarding') {
    return <MdLanding onboarding onSignedIn={() => setGate('app')} />;
  }

  /* .md-root supplies the vh/dvh bounded fallback for wide app surfaces. The
     signed-in phone marker changes it to natural document flow, which is why
     that responsive geometry lives in CSS rather than this inline style. */
  return (
    <div className="md-root" style={{ background: light ? '#F6F7F8' : '#08090C' }}>
      <App onLight={setLight} onSignedOut={leave} />
    </div>);

}

/* ── the store opens before React does ──────────────────────────────────────
   Migrations must finish before the first useState initialiser reads anything,
   or the app renders the old shape and then writes it back over the new one.
   That was already true when this ran at module scope; the only change is that
   the store is now OPENED first, and opening is the part that cannot be
   synchronous.

   This is the one await in the app's boot, and it is why every initialiser below
   — :464, :477, :480, :482, :622 — gets to stay exactly as synchronous as it is.
   md-store.js:29 called that the blocker for moving off localStorage. It is paid
   here, once, rather than becoming a loading state in every surface.

   See app/md-boot.js for the ordering and the failure policy. Short version: if
   hydration fails the app still boots and reads straight from the browser, which
   is not an exotic path — it is how this app read storage until today.

   mdDayKey is handed in so a device still holding offset-keyed days from before
   R4 can have them resolved to real dates instead of quarantined. Nothing is
   ever dropped either way. */
window.mdBootGate({
  /* ── the gate goes first ──────────────────────────────────────────────────
     Everything below reads documents, and with storage compartmented by account
     a document cannot be read until it is known WHOSE it is. So the surface is
     decided from the device alone before any of it runs: a visitor at the front
     door renders immediately and touches no storage at all, and anybody signed
     in has their room selected and hydrated exactly as before.

     `mdLandingGate()` distinguishes three surfaces, but only one of them is
     cold. Both signed-in answers boot, so its answer HERE is only ever read as
     "landing or not" — which it can give honestly with nothing hydrated,
     because it comes from the session marker. Which of the two it really is gets
     settled again inside Mount, where the room is open and the profile can
     actually be read. */
  gate: () => window.mdLandingGate(),
  account: () => (window.mdAccountId ? window.mdAccountId() : null),

  /* Two things happen before anything persisted is read, and only the first is
     load-bearing today.

     mdStorageHydrate() fills the chokepoint's map from localStorage; every
     synchronous read in the app depends on it having finished, which is the
     whole reason this gate exists.

     mdEngineOpen() starts SQLite in its Worker on OPFS. Nothing reads or writes
     through it yet — B5 is the story that moves the data — so a failure here is
     genuinely nothing to the user, and it is deliberately not awaited into the
     result: it cannot throw (see the contract at the top of md-engine.js), and
     opening it now is what makes the first boot that DOES need it a boot where
     the wasm is already compiled and the file already exists. */
  hydrate: async () => {
    const storage = await window.mdStorageHydrate();
    /* Give what ships with the app a document to be found by — see
       md-builtins.ts. It runs HERE because it writes through the chokepoint and
       so needs the map filled, and because every surface that lists a project
       reads after this point. On a settled account it writes nothing and returns
       zero, so the cost of leaving it at every boot is one map lookup per
       built-in. Said out loud only when it actually did something. */
    if (window.mdBuiltinsMaterialise) {
      const named = window.mdBuiltinsMaterialise();
      if (named) console.info(`[ManDana] ${named} built-in records given a name to be found by`);
    }
    if (window.MD_ENGINE && window.mdEngineOpen) {
      const engine = await window.mdEngineOpen({
        ...window.MD_ENGINE,
        // Same compartment the three IndexedDB stores use, for the same
        // reason: two accounts on one device must not read each other's
        // documents. Defensive, like every other caller of this function.
        name: (typeof window.mdCompartmentDbName === 'function') ? window.mdCompartmentDbName('mandana') : 'mandana',
        withoutOpfs: window.mdEngineWithoutOpfs ? window.mdEngineWithoutOpfs() : false,
      });
      if (!engine.ok) console.warn(`[ManDana] durable engine unavailable: ${engine.error}`);
      else if (window.mdMigrate) {
        /* Which of the two engines opened is worth saying, and worth saying at
           every boot rather than only when it changes. `memory-idb` means the
           database lives in memory and the whole file is snapshotted into
           IndexedDB, which is correct but has a cost the OPFS path does not —
           and when a support question arrives about a slow save, this line is
           the first thing anybody will want to know. */
        console.info(`[ManDana] engine: ${engine.vfs}`
          + (engine.restored ? ' (opened an existing database)' : ' (nothing stored yet)'));
        /* The copy, once, verified by reading it back. Nothing reads from the
           engine yet, so a failure here changes nothing a user can see — it
           just means the next boot tries again against a localStorage that was
           never touched. Said out loud either way, because a migration that
           quietly never succeeds is a migration nobody notices is broken. */
        const moved = await window.mdMigrate();
        if (moved.migrated && moved.ok) {
          console.info(`[ManDana] account copied into the engine: ${moved.keys} documents, `
            + `${(moved.bytes / 1024 / 1024).toFixed(1)} MB, verified byte for byte`);
        } else if (!moved.ok) {
          console.warn('[ManDana] the account was NOT copied into the engine; localStorage is '
            + 'unchanged and this will be retried on the next boot:', moved.error || moved.audit);
        }
      }
    }
    return storage;
  },
  migrate: () => {
    const mdBoot = window.mdStoreInit({
      dayKeyForOffset: (n) => (window.mdDayKey ? window.mdDayKey(n) : null),
    });
    if (mdBoot.migrated) {
      console.info(`[ManDana] storage migrated v${mdBoot.from} → v${mdBoot.to}`);
    }
    if (mdBoot.quarantined) {
      console.warn(`[ManDana] ${mdBoot.quarantined} legacy entr${mdBoot.quarantined === 1 ? 'y' : 'ies'} `
        + `could not be interpreted and were parked under md:quarantine:* — not deleted, `
        + `and still included in a backup.`);
    }
    return mdBoot;
  },
  render: () => ReactDOM.createRoot(document.getElementById('root')).render(<Mount />),
});
